import type { SupportedLanguage } from '../types';

export type TranslationKey = string;
export type TranslationMap = Record<TranslationKey, string>;
export type Translations = Record<SupportedLanguage, TranslationMap>;

export const translations: Translations = {
  en: {
    // Header
    systemName: 'QUAZI',
    systemTagline: 'AI-Powered Financial Oversight',
    logout: 'Logout',
    
    // Confirmation Dialogs
    confirmLogout: 'Confirm Logout',
    confirmLogoutMessage: 'Are you sure you want to log out? Any unsaved work will be lost.',
    confirmDelete: 'Delete Analysis',
    confirmDeleteMessage: 'Are you sure you want to permanently delete',
    delete: 'Delete',
    deleting: 'Deleting...',
    cancel: 'Cancel',
    deleteAnalysis: 'Delete this analysis',
    
    // Duplicate Detection
    duplicateDetected: 'Duplicate Document',
    duplicateMessage: 'This document has already been analyzed',
    duplicateExplanation: 'You already have an analysis for:',
    analyzedOn: 'Analyzed on',
    duplicateChoiceHint: 'Would you like to view the existing analysis or create a new one?',
    analyzeAgain: 'Analyze Again',
    viewExisting: 'View Existing',
    
    // Search
    searchAnalyses: 'Search analyses...',
    noResults: 'No results found',
    tryDifferentSearch: 'Try a different search term',
    found: 'found',
    noAnalysesYet: 'No analyses yet',
    uploadToStart: 'Upload documents to start your first analysis',
    
    // Upload Section
    uploadTitle: 'Financial Forensic Analysis',
    uploadSubtitle: 'Drag and drop a financial report PDF here or click to select',
    uploadSubtitleMultiple: 'Drag and drop financial PDFs here or click to select multiple files',
    uploadButton: 'UPLOAD DOCUMENT',
    uploadLimit: 'Limit: 32MB per file | PDF only',
    uploadLimitMultiple: 'Limit: 32MB per file | Multiple PDFs will be analyzed as one combined report',
    uploadInstructions: 'Upload Instructions',
    uploadInstructionsText: 'Only PDF files are accepted. Supported documents include: P&L statements, balance sheets, income statements, financial reports, and management control documents.',
    uploadInstructionsTextMultiple: 'Only PDF files are accepted. You can upload multiple files (e.g., separate balance sheet and income statement) which will be analyzed together as one comprehensive report.',
    qualityDisclaimer: '💡 Quality Tip',
    qualityDisclaimerText: 'Better document quality = faster & more accurate results. High-resolution PDFs with clear text and numbers will produce the best analysis.',
    filesSelected: '{{count}} file(s) selected',
    removeFile: 'Remove file',
    addMoreFiles: 'Add More Files',
    analyzeFiles: 'Analyze Documents',
    errorNoFiles: 'Please select at least one file to analyze',
    
    // Analysis Guidance
    analysisGuidanceLabel: 'Analysis Guidance',
    optional: 'optional',
    analysisGuidancePlaceholder: 'e.g., "Focus on profitability trends for the security division" or "Compare Q3 vs Q4 performance" or "Pay special attention to operating expenses and explain any anomalies"...',
    analysisGuidanceHint: 'Add specific instructions to customize the analysis. If left empty, QUAZI will perform a standard comprehensive analysis.',
    
    // Errors
    errorPdfOnly: 'Only PDF files are accepted',
    errorFileSize: 'File exceeds 32MB limit',
    errorAnalysis: 'Analysis Error',
    errorMessage: 'Error analyzing document. Please verify the backend is running.',
    retryButton: 'Try Again',
    
    // Results
    documentAnalyzed: 'Document analyzed:',
    newAnalysis: 'New Analysis',
    
    // Sections
    executiveSummary: 'EXECUTIVE SUMMARY',
    positiveIndicators: 'POSITIVE INDICATORS',
    criticalAlerts: 'CRITICAL ALERTS',
    questionsForManagement: 'QUESTIONS FOR MANAGEMENT',
    requiredActions: 'REQUIRED ACTIONS',
    anomaliesDetected: 'ANOMALIES DETECTED',
    
    // Priority levels
    highPriority: '⚡ HIGH PRIORITY',
    mediumPriority: '◆ MEDIUM PRIORITY',
    lowPriority: '○ LOW PRIORITY',
    
    // Severity
    critical: 'CRITICAL',
    high: 'HIGH',
    medium: 'MEDIUM',
    low: 'LOW',
    
    // Loading
    analyzing: 'ANALYZING DOCUMENT',
    analyzingSubtitle: 'QUAZI AI is performing deep forensic analysis',
    loadingParsing: 'Parsing financial documents...',
    loadingExtracting: 'Extracting key metrics and patterns...',
    loadingAnalyzing: 'Running forensic analysis algorithms...',
    loadingValidating: 'Validating data integrity...',
    loadingGenerating: 'Generating executive insights...',
    loadingFinalizing: 'Finalizing comprehensive report...',
    
    // Footer
    developedFor: 'Developed for',
    poweredBy: 'Powered by QUAZI AI Engine',
    
    // Languages
    spanish: 'Spanish',
    english: 'English',
    portuguese: 'Portuguese',
    
    // Time
    days: 'days',
    
    // Export
    exportReport: 'Export Report',
    exportingReport: 'Generating Report...',
    exportPDF: 'Export PDF',
    exportingPDF: 'Generating PDF...',
    exportCSV: 'Export Data (CSV)',
    exporting: 'Exporting...',
    exportProfessionalPDF: 'Management Report',
    exportingProfessionalPDF: 'Generating Management Report...',
    exportError: 'Failed to export PDF. Please try again.',
    
    // Authentication
    tagline: 'AI-Powered Financial Oversight',
    emailPlaceholder: 'Email',
    passwordPlaceholder: 'Password',
    signIn: 'SIGN IN',
    authenticating: 'AUTHENTICATING...',
    forgotPassword: 'Forgot Password?',
    loginFailed: 'Login failed. Please try again.',
    
    // Password Reset
    resetPassword: 'Reset Password',
    enterEmailForReset: 'Enter your email to receive a password reset code',
    sendResetCode: 'SEND RESET CODE',
    sending: 'SENDING...',
    backToLogin: 'Back to Login',
    resetCodeSent: 'Password reset code sent to your email',
    failedToSendCode: 'Failed to send reset code',
    
    // Password Reset - Step 2
    enterCodeAndNewPassword: 'Enter the verification code from your email and your new password',
    verificationCode: 'Verification Code',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    passwordRequirements: 'Minimum 8 characters, include uppercase, lowercase, number, and special character',
    resetting: 'RESETTING...',
    backToEmail: 'Back to Email',
    passwordResetSuccess: 'Password reset successfully! Redirecting to login...',
    failedToResetPassword: 'Failed to reset password',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    rememberMe: 'Remember me',
    passwordStrength: 'Password Strength:',
    weak: 'Weak',
    mediumStrength: 'Medium',
    strong: 'Strong',
    
    // New Password (Temp Password Flow)
    setNewPassword: 'Set New Password',
    tempPasswordMessage: 'You are logging in with a temporary password. Please set a new password to continue.',
    setPassword: 'SET PASSWORD',
    settingPassword: 'SETTING PASSWORD...',
    failedToSetPassword: 'Failed to set new password',
    
    // Sidebar & History
    history: 'History',
    recentAnalyses: 'Recent Analyses',
    openHistory: 'Open history',
    collapseHistory: 'Collapse sidebar',
    analysisCount: 'analysis',
    analysesCount: 'analyses',
    completed: 'Completed',
    
    // Chat
    chatWithQuazi: 'Chat with QUAZI',
    askQuestion: 'Ask a question...',
    chatEmptyState: 'Ask me anything about this analysis',
    closeChat: 'Close chat',
    
    // Quick Actions
    lastAnalysis: 'Last Analysis',
    continueAction: 'Continue',
    noChange: 'No change',
    
    // KPI Tooltips
    tooltipTotalAnalyses: 'Total number of documents you have analyzed',
    tooltipThisWeek: 'Analyses completed in the last 7 days vs previous week',
    tooltipAvgDuration: 'Average time to complete an analysis',
    tooltipChatMessages: 'Total AI chat interactions across all analyses',
    
    // Time Ago
    justNow: 'Just now',
    minutesAgo: '{{count}}m ago',
    hoursAgo: '{{count}}h ago',
    daysAgo: '{{count}}d ago',
    
    // Toast Messages
    chatExported: 'Chat conversation exported',
    exportComplete: 'Export Complete',
    
    // Comparison Feature
    compareAnalyses: 'Compare',
    compareAnalysesTitle: 'Compare Analyses',
    compareDescription: 'Select two analyses to compare side-by-side',
    selectTwoAnalyses: 'Select two analyses to compare',
    selectOneMore: 'Select one more analysis',
    readyToCompare: 'Ready to compare!',
    comparisonTitle: 'Analysis Comparison',
    compGrossMargin: 'Gross Margin',
    compOperatingMargin: 'Operating Margin',
    compNetMargin: 'Net Margin',
    compEbitdaMargin: 'EBITDA Margin',
    compCurrentRatio: 'Current Ratio',
    compDsoDays: 'DSO (Days)',
    compPersonnelCosts: 'Personnel Costs',
    compTotalRevenue: 'Total Revenue',
    marginComparison: 'Margin Comparison',
    executiveSummaries: 'Executive Summaries',
    criticalAlertsComparison: 'Critical Alerts',
    positiveIndicatorsComparison: 'Positive Indicators',
    compFlags: 'flags',
    compDifference: 'difference',
    noAlerts: 'No alerts',
    noIndicators: 'No indicators',
    compMore: 'more',
    keyPerformanceIndicators: 'Key Performance Indicators',
    
    // Analysis Completeness Feature
    analysisCompleteness: 'Analysis Completeness',
    completenessExcellent: 'Excellent',
    completenessGood: 'Good',
    completenessModerate: 'Moderate',
    completenessNeedsWork: 'Needs Work',
    statementsFound: 'statements',
    metricsCalculated: 'metrics',
    suggestionsAvailable: 'suggestions to improve',
    showLess: 'Show less',
    showDetails: 'Show details',
    missingDocuments: 'Missing Documents',
    incompleteMetrics: 'Metrics That Couldn\'t Be Calculated',
    improvementSuggestions: 'Ways to Improve This Analysis',
    completeYourAnalysis: 'Complete Your Analysis - Upload More Documents',
    excellentAnalysis: 'Excellent! This analysis is comprehensive with all key metrics calculated.',
    extendAnalysis: 'Extend Analysis',
    addMoreDocuments: 'Add More Documents',
    extendingAnalysis: 'Extending analysis with new documents...',
    analysisExtended: 'Analysis extended successfully!',
    extendAnalysisSubtitle: 'Upload additional documents to enhance and complete this analysis',
    extendingHint: 'Adding documents to existing analysis for more complete results',
    
    // Improve Analysis Modal
    improveAnalysis: 'Improve Analysis',
    improveAnalysisSubtitle: 'Upload additional documents to get a more complete analysis',
    recommendedUploads: 'Recommended Documents to Upload',
    uploadNewDocuments: 'Upload New Documents',
    additionalGuidance: 'Additional Guidance',
    guidancePlaceholder: 'e.g., Focus on liquidity analysis, compare with last year\'s results...',
    extending: 'Extending Analysis...',
    complete: 'Complete',
    analyzingDocuments: 'Analyzing your documents...',
    claudeAssessing: 'Claude is assessing what\'s missing',
    tryAgain: 'Try Again',
    analysisComprehensive: 'Your analysis is quite comprehensive!',
    addMoreDocsForInsights: 'Add more documents to unlock deeper insights.',
    statements: 'Statements',
    metrics: 'Metrics',
    clickToUploadPdf: 'Click to upload PDF files',
    filesSelectedCount: '{{count}} file(s) selected',
    dropFinancialDocs: 'Drop your financial documents here',
    incomeStatement: 'Income Statement',
    balanceSheet: 'Balance Sheet',
    cashFlowStatement: 'Cash Flow Statement',
    notesDisclosures: 'Notes & Disclosures',
    
    // Comparison View
    analysisComparison: 'Analysis Comparison',
    company1: 'Company 1',
    company2: 'Company 2',
    noAlertsFound: 'No alerts',
    noIndicatorsFound: 'No indicators',
    moreItems: '+{{count}} more',
    loading: 'Loading...',
    searchCompanyYearDoc: 'Search by company, year, or document...',
    selected: 'Selected:',
    noAnalysesFound: 'No analyses found',
    uploadDocsToCreate: 'Upload documents to create analyses',
    unknownCompany: 'Unknown Company',
    
    // Dashboard Welcome
    welcomeToQuazi: 'Welcome to QUAZI',
    systemDescription: 'Financial Forensic Analysis System',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    youveAnalyzed: "You've analyzed",
    documents: 'documents',
    document: 'document',
    
    // Dashboard Stats
    totalAnalyses: 'Total Analyses',
    thisWeek: 'This Week',
    avgDuration: 'Avg. Duration',
    chatMessages: 'Chat Messages',
    allTime: 'All time',
    lastSevenDays: 'Last 7 days',
    perAnalysis: 'Per analysis',
    aiInsights: 'AI insights',
    
    // Dashboard Charts
    analysisActivity: 'Analysis Activity',
    lastFourteenDays: 'Last 14 days',
    companiesAnalyzed: 'Companies Analyzed',
    unique: 'unique',
    recentActivity: 'Recent Activity',
    latestAnalyses: 'Latest analyses',
    industriesAnalyzed: 'Industries Analyzed',
    activityChartEmpty: 'Activity chart will appear after your first analysis',
    companyBreakdownEmpty: 'Company breakdown will appear here',
    recentActivityEmpty: 'Your recent analyses will appear here',
    
    // Onboarding
    getStartedWithQuazi: 'Get Started with Financial Forensics',
    getStartedSubtitle: 'Follow these simple steps to unlock powerful AI-driven financial insights',
    stepOneTitle: 'Upload Financial Documents',
    stepOneDesc: 'Drag & drop PDF files like P&L statements, balance sheets, or annual reports',
    stepTwoTitle: 'AI Analysis',
    stepTwoDesc: "QUAZI's AI extracts key metrics, detects anomalies, and generates insights",
    stepThreeTitle: 'Chat & Export',
    stepThreeDesc: 'Ask questions about your data and export professional PDF reports',
    uploadFirstDocument: 'Upload Your First Document',
    
    // Financial Charts
    totalRevenue: 'Total Revenue',
    revenuePerEmployee: 'Revenue per Employee',
    dsodays: 'DSO',
    netMargin: 'Net Margin',
    revenueBreakdown: 'Revenue Breakdown',
    byServiceLine: 'By Service Line',
    byGeography: 'By Geography',
    costAllocation: 'Cost Allocation',
    costVsBenchmark: 'Cost vs Benchmark',
    profitabilityMetrics: 'Profitability Metrics',
    charts: 'Charts',
    viewCharts: 'View Financial Charts',
    financialVisualizations: 'Financial Visualizations',
    costStructure: 'Cost Structure',
    trendsOverTime: 'Trends Over Time',
    noTrendsAvailable: 'No time-series data available in the document',
    additionalKPIs: 'Additional KPIs',
    service: 'Service',
    revenue: 'Revenue',
    location: 'Location',
    trend: 'Trend',
    industryBenchmark: 'Industry Benchmark',
    benchmark: 'Benchmark',
    metric: 'Metric',
    aiEstimate: 'AI Estimate',
    documentData: 'Document Data',
    benchmarkSource: 'Benchmark Source',
    value: 'Value',
    unit: 'Unit',
    
    // Modules
    moduleFinancialForensics: 'Financial Forensics',
    enabledModules: 'Enabled Modules',
    noModulesEnabled: 'No modules enabled yet. Contact support.',

    // AML Sidebar
    amlSidebarTitle: 'AML Compliance',
    amlNavCustomers: 'Customers',
    amlNavCustomersDesc: 'KYC & customer records',
    amlNavTransactions: 'Transactions',
    amlNavTransactionsDesc: 'Transaction history',
    amlNavTransactionReport: 'Transaction Report',
    amlNavTransactionReportDesc: 'Reports & export',
    amlNavTransactionPivotReport: 'Transaction Pivot Report',
    amlNavTransactionPivotReportDesc: 'Aggregated analysis',
    amlNavSanctions: 'OFAC Screening',
    amlNavSanctionsDesc: 'Sanctions list matching',
    amlNavClients: 'Clients',
    amlNavClientsDesc: 'AML client management',

    // AML OFAC sanctions screening
    amlColScreening: 'Screening',
    amlSanctionsTitle: 'OFAC Sanctions Screening',
    amlSanctionsSubtitle: 'Match your customers against the OFAC sanctions lists',
    amlSanctionsListSdn: 'SDN List',
    amlSanctionsListNonSdn: 'Non-SDN (Consolidated) List',
    amlSanctionsListShort_SDN: 'SDN',
    amlSanctionsListShort_NON_SDN: 'Non-SDN',
    amlSanctionsListState_never_run: 'Never downloaded',
    amlSanctionsListState_running: 'Downloading…',
    amlSanctionsListState_success: 'Up to date',
    amlSanctionsListState_failed: 'Last download failed',
    amlSanctionsListPublished: 'Published',
    amlSanctionsListEntries: 'Entries',
    amlSanctionsListNames: 'Names',
    amlSanctionsListUpdated: 'Last updated',
    amlSanctionsBtnRefresh: 'Refresh',
    amlSanctionsBtnRefreshAll: 'Refresh Lists',
    amlSanctionsBtnRunScreening: 'Run Screening',
    amlSanctionsNoListsWarning: 'Download the OFAC lists before running a screening.',
    amlScreeningHistoryTitle: 'Screening History',
    amlScreeningNoRuns: 'No screening has been run yet',
    amlScreeningColDate: 'Date',
    amlScreeningColStatus: 'Status',
    amlScreeningColScreened: 'Screened',
    amlScreeningColCleared: 'Cleared',
    amlScreeningColPotentialMatches: 'Potential Matches',
    amlScreeningColThreshold: 'Threshold',
    amlScreeningColTriggeredBy: 'Triggered By',
    amlScreeningRunStatus_running: 'Running',
    amlScreeningRunStatus_success: 'Completed',
    amlScreeningRunStatus_failed: 'Failed',
    amlScreeningDetailTitle: 'Screening Results',
    amlScreeningBtnBack: 'Back to screening',
    amlScreeningThresholdLabel: 'Threshold',
    amlScreeningBtnDownloadPdf: 'PDF Report',
    amlScreeningBtnDownloadExcel: 'Excel Report',
    amlScreeningListVersions: 'List Versions Used',
    amlScreeningSearchPlaceholder: 'Search customers…',
    amlScreeningFilterAllResults: 'All results',
    amlScreeningFilterAllReviews: 'All review states',
    amlScreeningColCustomer: 'Customer',
    amlScreeningColAddress: 'Address',
    amlScreeningColResult: 'Result',
    amlScreeningColScore: 'Score',
    amlScreeningColMatchedName: 'Matched OFAC Name',
    amlScreeningColList: 'List',
    amlScreeningColReview: 'Review',
    amlScreeningResultCleared: 'Cleared',
    amlScreeningResultPotentialMatch: 'Potential Match',
    amlScreeningResultNotScreened: 'Not screened',
    amlScreeningReviewPending: 'Pending',
    amlScreeningReviewFalsePositive: 'False positive',
    amlScreeningReviewConfirmedMatch: 'Confirmed match',
    amlScreeningNoResults: 'No results match these filters',
    amlScreeningAka: 'AKA',
    amlScreeningAkaOf: 'Alias of',
    amlScreeningReviewPanelTitle: 'Review Potential Match',
    amlScreeningBestScore: 'Best score',
    amlScreeningCandidatesTitle: 'Candidate Matches',
    amlScreeningMatchEntityType: 'Entity Type',
    amlScreeningMatchPrograms: 'Sanctions Programs',
    amlScreeningMatchAddresses: 'OFAC Addresses',
    amlScreeningMatchOfacUid: 'OFAC UID',
    amlScreeningViewOnOfac: 'View on OFAC',
    amlScreeningReviewNotesLabel: 'Review notes',
    amlScreeningReviewNotesPlaceholder: 'Explain the decision for the audit trail…',
    amlScreeningBtnMarkFalsePositive: 'Mark as False Positive',
    amlScreeningBtnConfirmMatch: 'Confirm Match',
    amlScreeningToastReportDownloaded: 'Report downloaded',
    amlScreeningToastReportFailed: 'Failed to download the report',
    amlScreeningToastReviewSaved: 'Review saved',
    amlScreeningToastReviewFailed: 'Failed to save the review',

    // AML Columns
    amlColDate: 'Date',
    amlColCustomerLegalName: 'Customer Legal Name',
    amlColLocation: 'Location',
    amlColInbound: 'Inbound',
    amlColOutbound: 'Outbound',
    amlColAmount: 'Amount',
    amlColFeeInbound: 'Fee Inbound',
    amlColFeeOutbound: 'Fee Outbound',
    amlColAmountFee: 'Amount Fee',
    amlColInvoiceNumber: 'Invoice Number',
    amlColInvoiceDate: 'Invoice Date',
    amlColInvoiceAmount: 'Invoice Amount',
    amlColStops: 'Stops',
    amlColPcs: 'PCS',
    amlColNotes: 'Notes',
    amlColRegistrationNumber: 'Registration Number',
    amlColInitialDate: 'Initial Date',
    amlColCode: 'Code',
    amlColStatus: 'Status',
    amlColName: 'Name',
    amlColAddress: 'Address',
    amlColPhone: 'Phone',
    amlColLogo: 'Logo',
    amlColEmail: 'Email',
    amlColClient: 'Client',
    amlColType: 'Type',
    amlColActions: 'Actions',

    // AML Table
    amlTableTotal: 'Total',
    amlTableNoTransactionsFound: 'No transactions found',
    amlTableNoCustomersFound: 'No customers found',
    amlTableNoClientsFound: 'No clients found',
    amlTableNoUsersFound: 'No users found',

    // AML Filters
    amlFilterTitle: 'Filters',
    amlFilterApplyFiltersToLoadReport: 'Apply filters to load the report',
    amlFilterApplyFiltersToLoadReportDesc: 'Use the filters above to load the transaction report.',
    amlFilterNoTransactionsFound: 'No transactions found',
    amlFilterNoTransactionsFoundDesc: 'Try adjusting the date range or other filters.',
    amlFilterPanelTitle: 'Filter',
    amlFilterCustomerLegalName: 'Customer Legal Name',
    amlFilterLocation: 'Location',
    amlFilterDateStart: 'Date (Start)',
    amlFilterDateEnd: 'Date (End)',
    amlFilterInvoiceDateStart: 'Invoice Date (Start)',
    amlFilterInvoiceDateEnd: 'Invoice Date (End)',
    amlFilterSearchCustomers: 'Search customers...',
    amlFilterSearchLocations: 'Search locations...',
    amlFilterNoCustomersFound: 'No customers found',
    amlFilterNoLocationsFound: 'No locations found',
    amlFilterReset: 'Reset',
    amlFilterApply: 'Apply',
    amlFilterClear: 'Clear',

    // AML Placeholders
    amlPlaceholderSearchByCustomerLocation: 'Search by customer, location...',
    amlPlaceholderAllCustomers: 'All customers',
    amlPlaceholderAllLocations: 'All locations',
    amlPlaceholderDateFrom: 'Date from',
    amlPlaceholderDateTo: 'Date to',
    amlPlaceholderSelectDateStart: 'Select date (Start)',
    amlPlaceholderSelectDateEnd: 'Select date (End)',
    amlPlaceholderSelectInvoiceDateStart: 'Select invoice date (Start)',
    amlPlaceholderSelectInvoiceDateEnd: 'Select invoice date (End)',
    amlPlaceholderStartDate: 'Start date',
    amlPlaceholderEndDate: 'End date',

    // AML Buttons
    amlBtnPdf: 'PDF',
    amlBtnExcel: 'Excel',
    amlBtnImport: 'Import',
    amlBtnAddTransaction: 'Add Transaction',
    amlBtnAddCustomer: 'Add Customer',
    amlBtnAddClient: 'Add Client',
    amlBtnAddUser: 'Add User',
    amlBtnSaveChanges: 'Save Changes',
    amlBtnCreateCustomer: 'Create Customer',
    amlBtnCreateClient: 'Create Client',
    amlBtnCreateUser: 'Create User',
    amlBtnUpdateTransaction: 'Update Transaction',
    amlBtnCreateTransaction: 'Create Transaction',
    amlBtnCancel: 'Cancel',
    amlBtnApply: 'Apply',
    amlBtnReset: 'Reset',
    amlBtnDelete: 'Delete',
    amlBtnExportReport: 'Export Report',

    // AML Pivot
    amlPivotTableNoPivotData: 'No pivot data',
    amlPivotTableNoPivotDataDesc: 'Try adjusting the date range or grouping.',
    amlPivotColTransactionPeriod: 'Transaction Period',
    amlPivotColInboundSum: 'Inbound Sum',
    amlPivotColInboundCount: 'Inbound Count',
    amlPivotColOutboundSum: 'Outbound Sum',
    amlPivotColOutboundCount: 'Outbound Count',
    amlPivotColInvoiceSum: 'Invoice Sum',
    amlPivotColInvoiceCount: 'Invoice Count',
    amlPivotColStopsSum: 'Stops Sum',
    amlPivotColPcsCount: 'PCS Count',
    amlPivotRadioClient: 'Client',
    amlPivotRadioMonth: 'Month',

    // AML Import
    amlImportModalTitleUpload: 'Import Transactions',
    amlImportModalTitleReview: 'Review Customer Matching',
    amlImportModalTitleResult: 'Import Result',
    amlImportClickToSelectFile: 'Click to select .xlsx file',
    amlImportSupportedFormat: 'Supported format: .xlsx',
    amlImportDownloadSampleTemplate: 'Download sample template',
    amlImportParsingFile: 'Parsing file and matching customers...',
    amlImportAiMatchingEnabled: 'AI matching enabled for unrecognized names',
    amlImportColCustomer: 'Customer',
    amlImportColLocation: 'Location',
    amlImportColStatus: 'Status',
    amlImportColAmlCustomer: 'AML Customer',
    amlImportColAmlLocation: 'AML Location',
    amlImportStatTotalRows: 'Total Rows',
    amlImportStatClients: 'Entries',
    amlImportStatMatched: 'Matched',
    amlImportStatAiSuggested: 'AI Suggested',
    amlImportStatUnresolved: 'Unresolved',
    amlImportStatSkipped: 'Skipped',
    amlImportFilterEmpty: 'No entries match this filter.',
    amlImportParseWarnings: 'Parse Warnings',
    amlImportBadgeSkipped: 'Skipped',
    amlImportBadgeOfacBlocked: 'OFAC blocked',
    amlImportOfacSkippedTitle: 'OFAC potential matches skipped',
    amlImportOfacSkippedBody: '{{count}} customer(s) flagged as OFAC potential matches were skipped and will not be imported.',
    amlImportOfacUnscreenedTitle: 'Customers not screened',
    amlImportOfacUnscreenedBody: '{{count}} mapped customer(s) have not been checked against the OFAC lists yet. They will still be imported.',
    amlImportBadgeMatched: 'Matched',
    amlImportBadgeResolved: 'Resolved',
    amlImportBadgeUnmatched: 'Unmatched',
    amlImportBadgeAi: 'AI',
    amlImportBadgeConfidenceHigh: 'High',
    amlImportBadgeConfidenceMedium: 'Medium',
    amlImportBadgeConfidenceLow: 'Low',
    amlImportRowsNotImported: 'Rows will not be imported',
    amlImportResolveAllUnmatched: 'Resolve all customers and locations, or skip entries before importing.',
    amlImportErrors: 'Errors',
    amlImportResultSuccessTitle: 'Import finished',
    amlImportResultPartialTitle: 'Import finished — some rows were skipped',
    amlImportResultFailedTitle: 'No transactions were imported',
    amlImportResultSuccessSummary: '{{count}} new transaction(s) were added.',
    amlImportResultPartialSummary:
      '{{imported}} transaction(s) were added. {{issues}} row(s) from the file were not imported — see the list below.',
    amlImportResultFailedSummary:
      '{{issues}} row(s) could not be imported. Fix the spreadsheet and try again.',
    amlImportResultRowProblemsHeading: 'Rows not imported',
    amlImportResultRowProblemsHint: 'Each message includes the row number from your .xlsx file.',
    amlImportParseWarningsHint: 'Each message includes the row number from your .xlsx file.',
    amlImportToastImportComplete: '{{imported}} transaction(s) imported.',
    amlImportToastImportDuplicates: '{{count}} duplicate(s) imported with a note.',
    amlImportToastImportPartial:
      '{{imported}} imported; {{issues}} row issue(s). See the summary in this window.',
    amlImportResultAllDuplicatesTitle: 'Duplicates imported',
    amlImportResultAllDuplicatesSummary:
      '{{count}} transaction(s) were added. Each was marked as a duplicate in Notes because it already exists (same customer, location, date, and amounts).',
    amlImportToastAllDupes: '{{count}} duplicate(s) imported with a note.',
    amlImportSelectCustomer: 'Select customer...',
    amlImportSelectLocation: 'Select location...',
    amlImportTooltipRestore: 'Restore',
    amlImportTooltipRejectManual: 'Reject — choose manually',
    amlImportTooltipUndoAiMatch: 'Undo AI match',
    amlImportTooltipSkipClient: 'Skip client',
    amlImportTooltipAcceptAiSuggestion: 'Accept AI suggestion',
    amlImportTooltipCreateNewCustomer: 'Create new customer',
    amlImportBtnUploadParse: 'Upload & Parse',
    amlImportBtnBack: 'Back',
    amlImportBtnSaveForLater: 'Save for later',
    amlImportBtnSubmitImport: 'Submit Import',
    amlImportBtnClose: 'Close',
    amlImportToastDraftSaved: 'Import saved. Open Import again to continue.',
    amlImportErrorDraftSaveFailed: 'Could not save this import. Please try again.',
    amlImportSavedBadge: 'Saved',
    amlImportDraftBannerTitle: 'Saved import',
    amlImportDraftBannerBody: '{{fileName}} — saved {{date}}',
    amlImportDraftBannerBodyNoFile: 'Saved {{date}}',
    amlImportBtnResumeDraft: 'Resume',
    amlImportBtnDiscardDraft: 'Discard',
    amlImportToastDraftDiscarded: 'Saved import discarded',
    amlImportResultTransactionsImported: '{{count}} transaction(s) imported',
    amlImportResultDuplicatesSkipped: '{{count}} duplicate(s) imported with a note',
    amlImportResultRowsSkippedUnmatched: '{{count}} row(s) skipped (unmatched)',
    amlImportErrorPleaseSelectXlsx: 'Please select an .xlsx file',
    amlImportErrorFailedToParseFile: 'Failed to parse file. Please check the file format.',
    amlImportErrorImportFailed: 'Import failed. Please try again.',
    amlImportCreateCustomerExistingHint:
      'This legal name already exists. Code and initial date are not needed — only the location from the file will be added to that customer.',
    amlImportModalTitleAddLocation: 'Add location',
    amlImportBtnAddLocation: 'Add location',
    amlImportToastCustomerCreated: 'Customer "{{name}}" created',
    amlImportToastLocationAdded: 'Location added to "{{name}}"',
    amlImportErrorCreateCustomerFailed: 'Could not create customer',

    // AML Create Customer Modal
    amlCreateCustomerModalTitle: 'Create Customer',
    amlCreateCustomerLabelInitialDate: 'Initial Date',
    amlCreateCustomerLabelLocationFromFile: 'Location (from file)',
    amlCreateCustomerErrorNameRequired: 'Name is required',
    amlCreateCustomerErrorValidCodeRequired: 'Valid code is required',
    amlCreateCustomerErrorInitialDateRequired: 'Initial date is required',

    // AML Transaction Form
    amlTransactionDetailTitle: 'Transaction Detail',
    amlTransactionEditTitle: 'Edit Transaction',
    amlTransactionAddTitle: 'Add Transaction',
    amlTransactionLabelCustomer: 'Customer',
    amlTransactionLabelLocation: 'Location',
    amlTransactionLabelTransactionDate: 'Transaction Date',
    amlTransactionLabelAmounts: 'Amounts',
    amlTransactionLabelFees: 'Fees',
    amlTransactionLabelInvoice: 'Invoice',
    amlTransactionLabelAmountCollectedInOut: 'Amount Collected (IN/OUT)',
    amlTransactionLabelFeeCollectedInOut: 'Amount Fee Collected (IN/OUT)',
    amlTransactionLabelSelectCustomer: 'Select customer',
    amlTransactionLabelSelectLocation: 'Select location',
    amlTransactionLabelLoading: 'Loading...',
    amlTransactionLabelSelectDate: 'Select date',
    amlTransactionLabelCollected: 'Collected',
    amlTransactionLabelFeeCollected: 'Fee Collected',
    amlTransactionLabelInvoiceHash: 'Invoice #',
    amlTransactionToastSaved: 'Saved',
    amlTransactionToastUpdatedSuccessfully: 'Transaction updated successfully',
    amlTransactionToastCreatedSuccessfully: 'Transaction created successfully',
    amlTransactionToastDeleted: 'Transaction deleted',
    amlTransactionToastFailedToDelete: 'Failed to delete transaction',
    amlTransactionToastFailedToUpdate: 'Failed to update transaction',
    amlTransactionToastFailedToCreate: 'Failed to create transaction',
    amlTransactionOfacBlockedTitle: 'OFAC potential match',
    amlTransactionOfacBlockedBody: 'This customer is flagged on the OFAC screening list. Create a transaction only after a reviewer marks the hit as a false positive.',
    amlTransactionOfacUnscreenedTitle: 'Customer not screened',
    amlTransactionOfacUnscreenedBody: 'This customer has not been checked against the OFAC lists yet. You can still create the transaction, but run a screening as soon as possible.',
    amlFilterRemoveName: 'Remove {{name}}',
    amlTransactionTooltipFullEdit: 'Full edit',
    amlTransactionTooltipView: 'View',
    amlTransactionTooltipDelete: 'Delete',
    amlTransactionAriaLabelEdit: 'Edit transaction',
    amlTransactionAriaLabelView: 'View transaction',
    amlTransactionAriaLabelDelete: 'Delete transaction',
    amlTransactionDeleteTitle: 'Delete Transaction',
    amlTransactionDeleteMessage: 'Remove this transaction from reports and lists? It can be restored later if needed.',
    amlTransactionDeleteConfirm: 'Delete',
    amlTransactionBulkDelete: 'Delete selected',
    amlTransactionBulkSelected: '{{count}} selected',
    amlTransactionBulkDeleteTitle: 'Delete Transactions',
    amlTransactionBulkDeleteMessage: 'Remove {{count}} selected transaction(s) from reports and lists? They can be restored later if needed.',
    amlTransactionToastBulkDeleted: '{{count}} transaction(s) deleted',
    amlTransactionAriaLabelSelect: 'Select transaction',
    amlTransactionAriaLabelSelectAll: 'Select all on this page',
    amlTransactionAriaLabelClearSelection: 'Clear selection',

    // AML Customer Form
    amlCustomerFormTitleEdit: 'Edit Customer',
    amlCustomerFormTitleCreate: 'Add Customer',
    amlCustomerFieldCustomerLegalName: 'Customer Legal Name',
    amlCustomerFieldCode: 'Code',
    amlCustomerFieldRegistrationNumber: 'Registration Number',
    amlCustomerFieldEinFein: 'EIN/FEIN',
    amlCustomerFieldInitialDate: 'Initial Date',
    amlCustomerFieldDateCompanyEstablished: 'Date Company Established',
    amlCustomerFieldCompanyLegalRepresentative: 'Company Legal Representative',
    amlCustomerFieldMainAddress: 'Main Address',
    amlCustomerFieldServicesAndProduct: 'Services and/or Product',
    amlCustomerFieldTypeOfBusinessActivity: 'Type of Business Activity',
    amlCustomerFieldNotesAmlRev: 'Notes AML REV',
    amlCustomerFieldContact: 'Contact',
    amlCustomerFieldEmail: 'Email',
    amlCustomerFieldPhone: 'Phone',
    amlCustomerFieldLocationsDetail: 'Locations detail',
    amlCustomerFieldStatus: 'Status',
    amlCustomerStatusActive: 'Active',
    amlCustomerStatusInactive: 'Inactive',
    amlCustomerNotApplicable: 'N/A',
    amlCustomerAriaLabelActions: 'Actions',
    amlCustomerPlaceholderLegalName: 'Enter customer legal name',
    amlCustomerPlaceholderCode: 'Enter code',
    amlCustomerPlaceholderRegistrationNumber: 'Enter registration number',
    amlCustomerPlaceholderEinFein: 'Enter EIN/FEIN',
    amlCustomerPlaceholderSelectInitialDate: 'Select initial date',
    amlCustomerPlaceholderSelectDate: 'Select date...',
    amlCustomerPlaceholderCompanyLegalRep: 'Enter company legal representative',
    amlCustomerPlaceholderMainAddress: 'Enter main address',
    amlCustomerPlaceholderServicesProduct: 'Enter services and/or product',
    amlCustomerPlaceholderTypeOfActivity: 'Enter type of business activity',
    amlCustomerPlaceholderNotesAmlRev: 'Enter notes AML REV',
    amlCustomerPlaceholderContact: 'Enter contact',
    amlCustomerPlaceholderEmailAddress: 'Enter email address',
    amlCustomerPlaceholderEnterPhone: 'Enter phone',
    amlCustomerPlaceholderLocationName: 'Enter location name',
    amlCustomerValidationNameRequired: 'Customer Legal Name is required',
    amlCustomerValidationDuplicateLegalName:
      'A customer with this legal name already exists. Choose a different name.',
    amlCustomerValidationCodeRequired: 'Code is required',
    amlCustomerValidationCodeMustBeNumber: 'Code must be a valid number',
    amlCustomerValidationInitialDateRequired: 'Initial Date is required',
    amlCustomerValidationDateCannotBePast: 'Date cannot be in the past',
    amlCustomerValidationDateCannotBeFuture: 'Date cannot be in the future',
    amlCustomerValidationInvalidDate: 'Invalid date',
    amlCustomerValidationMustBeAtLeast3Chars: 'Must be at least 3 characters',
    amlCustomerValidationOnlyLettersAllowed: 'Only letters are allowed',
    amlCustomerValidationOnlyLettersNumbers: 'Only letters and numbers are allowed',
    amlCustomerValidationValidEmail: 'Please enter a valid email address',
    amlCustomerValidationAllLocationNamesRequired: 'All location names are required',
    amlCustomerToastUpdated: 'Customer updated successfully',
    amlCustomerToastCreated: 'Customer created — OFAC screening started',
    amlCustomerToastDeleted: '"{{name}}" deleted successfully',
    amlCustomerToastFailedToDelete: 'Failed to delete customer',
    amlCustomerPanelTitleDetail: 'Customer Detail',
    amlCustomerPanelTitleKyc: 'KYC Report',
    amlCustomerBtnAdd: 'Add',
    amlCustomerBtnSaveChanges: 'Save Changes',
    amlCustomerBtnCreateCustomer: 'Create Customer',
    amlCustomerSearchPlaceholder: 'Search by name, code, registration number...',
    amlCustomerDropdownDetail: 'Detail',
    amlCustomerDropdownKycReport: 'KYC Report',
    amlCustomerDropdownEdit: 'Edit',
    amlCustomerDropdownDelete: 'Delete',
    amlCustomerDeleteTitle: 'Delete customer?',
    amlCustomerDeleteMessage: 'Hide "{{name}}" and its transactions from lists and reports? History is kept and can be restored later if needed.',
    amlCustomerDeleteConfirm: 'Delete',
    amlCustomerDeleteCancel: 'Cancel',

    // AML KYC
    amlKycTableHeaderDataReviewedByCO: 'Data reviewed by C.O',
    amlKycTableHeaderData: 'Data',
    amlKycTableHeaderAdditionalNotes: 'Additional Notes',
    amlKycFieldInitialDate: 'Initial Date',
    amlKycFieldCode: 'Code',
    amlKycFieldCustomerLegalName: 'Customer Legal Name',
    amlKycFieldEinFein: 'EIN / FEIN',
    amlKycFieldRegistrationNumber: 'Registration Number',
    amlKycFieldDateCompanyEstablished: 'Date Company Established',
    amlKycFieldCompanyLegalRepresentative: 'Company Legal Representative',
    amlKycFieldMainAddress: 'Main Address',
    amlKycFieldLocationsDetail: 'Locations Detail',
    amlKycFieldServicesProduct: 'Services and/or Product',
    amlKycFieldTypeOfBusinessActivity: 'Type of Business Activity',
    amlKycFieldContact: 'Contact',
    amlKycFieldEmail: 'Email',
    amlKycFieldPhone: 'Phone',
    amlKycDateReviewed: 'Date reviewed:',
    amlKycBtnExportReport: 'Export Report',
    amlCustomerDetailLocationsDetail: 'Locations detail',

    // AML Client Form
    amlClientFormTitleEdit: 'Edit Client',
    amlClientFormTitleCreate: 'Add Client',
    amlClientFieldClientLogo: 'Client Logo',
    amlClientFieldClientName: 'Client Name',
    amlClientFieldAddress: 'Address',
    amlClientFieldPhone: 'Phone',
    amlClientPlaceholderCompanyName: 'Company name',
    amlClientPlaceholderBusinessAddress: 'Business address',
    amlClientPlaceholderPhoneNumber: 'Phone number',
    amlClientBtnChangeImage: 'Change Image',
    amlClientBtnUploadImage: 'Upload Image',
    amlClientBtnCancelUpload: 'Cancel Upload',
    amlClientBtnRemoveImage: 'Remove Image',
    amlClientImageFormats: 'JPG, PNG, GIF, WebP or SVG',
    amlClientBtnSaveChanges: 'Save Changes',
    amlClientBtnCreateClient: 'Create Client',
    amlClientToastUpdated: 'Client updated successfully',
    amlClientToastCreated: 'Client created successfully',
    amlClientToastFailedToUpdate: 'Failed to update client',
    amlClientToastFailedToCreate: 'Failed to create client',
    amlClientToastLogoUploadFailed: 'Client saved but logo upload failed',
    amlClientToastCreatedLogoFailed: 'Client created but logo upload failed',
    amlClientSearchPlaceholder: 'Search clients...',
    amlClientDeleteTitle: 'Delete Client',
    amlClientDeleteMessage: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
    amlClientDeleteConfirm: 'Delete',
    amlClientShowingResults: 'Showing {{from}}–{{to}} of {{total}}',

    // AML User Management
    amlUserListSearchPlaceholder: 'Search by name, email, client...',
    amlUserRoleSuperAdmin: 'Super Admin',
    amlUserRoleClientAdmin: 'Client Admin',
    amlUserRoleOperator: 'Operator',
    amlUserRoleReviewer: 'Reviewer',
    amlUserRoleUser: 'User',
    amlUserRolePlatformAdmin: 'Platform Admin',
    amlUserStatusActive: 'Active',
    amlUserStatusInvited: 'Invited',
    amlUserStatusInactive: 'Inactive',
    amlUserMenuLogin: 'Login',
    amlUserMenuDetail: 'Detail',
    amlUserMenuEdit: 'Edit',
    amlUserMenuResetPassword: 'Reset Password',
    amlUserMenuActivate: 'Activate',
    amlUserMenuDelete: 'Delete',
    amlUserLoginAsTitle: 'Login as User',
    amlUserLoginAsMessage: 'You are about to log in as {{name}}. Your current session will be saved and you can restore it by logging out.',
    amlUserBtnLogin: 'Login',
    amlUserDeleteTitle: 'Delete User',
    amlUserDeleteMessage: 'Are you sure you want to permanently delete "{{name}}"? This action cannot be undone.',
    amlUserActivateTitle: 'Activate User',
    amlUserActivateMessage: 'Are you sure you want to reactivate "{{name}}"? They will regain access to the system.',
    amlUserActivateConfirm: 'Activate',
    amlUserToastDeleted: 'User "{{name}}" has been deleted.',
    amlUserToastActivated: 'User "{{name}}" has been activated.',
    amlUserToastFailedToDelete: 'Failed to delete user.',
    amlUserToastFailedToActivate: 'Failed to activate user.',
    amlUserDetailTitle: 'User Detail',
    amlUserDetailFieldStatus: 'Status',
    amlUserDetailFieldRole: 'Role',
    amlUserDetailFieldClient: 'Client',
    amlUserDetailFieldCreated: 'Created',
    amlUserFormTitleEdit: 'Edit User',
    amlUserFormTitleCreate: 'Add User',
    amlUserFormFieldEmail: 'Email',
    amlUserFormFieldFirstName: 'First Name',
    amlUserFormFieldLastName: 'Last Name',
    amlUserFormFieldPassword: 'Password',
    amlUserFormFieldConfirmPassword: 'Confirm Password',
    amlUserFormFieldClient: 'Client',
    amlUserFormFieldType: 'Type',
    amlUserFormFieldActive: 'Active',
    amlUserFormPlaceholderEmail: 'user@example.com',
    amlUserFormPlaceholderFirstName: 'Enter first name',
    amlUserFormPlaceholderLastName: 'Enter last name',
    amlUserFormPlaceholderPassword: 'Enter password',
    amlUserFormPlaceholderConfirmPassword: 'Confirm password',
    amlUserFormPlaceholderSelectClient: 'Select client',
    amlUserFormValidationEmailRequired: 'Email is required',
    amlUserFormValidationValidEmail: 'Enter a valid email address',
    amlUserFormValidationFirstNameRequired: 'First name is required',
    amlUserFormValidationLastNameRequired: 'Last name is required',
    amlUserFormValidationClientRequired: 'Client is required',
    amlUserFormValidationPasswordRequired: 'Password is required',
    amlUserFormValidationPasswordMinLength: 'Password must be at least {{count}} characters',
    amlUserFormValidationPasswordUppercase: 'Password must include an uppercase letter',
    amlUserFormValidationPasswordLowercase: 'Password must include a lowercase letter',
    amlUserFormValidationPasswordNumber: 'Password must include a number',
    amlUserFormValidationPasswordSpecial: 'Password must include a special character',
    amlUserFormValidationPasswordConfirmRequired: 'Password confirmation is required',
    amlUserFormValidationPasswordsDoNotMatch: 'Passwords do not match',
    amlUserFormValidationEnterNewPasswordFirst: 'Enter new password before confirming',
    amlUserFormErrorEmailAlreadyExists: 'A user with this email already exists',
    amlUserFormErrorUserNotFound: 'User not found — they may have been deleted',
    amlUserFormErrorNoPermission: 'You do not have permission to perform this action',
    amlUserFormErrorInvalidData: 'Invalid data — please check your inputs',
    amlUserFormErrorNetworkError: 'Network error — check your connection and try again',
    amlUserFormBtnSaveChanges: 'Save Changes',
    amlUserFormBtnCreateUser: 'Create User',
    amlUserToastUserUpdated: 'User updated successfully',
    amlUserToastUserCreated: 'User created successfully',
    amlUserFormShowingOf: 'Showing {{from}}–{{to}} of {{total}}',

    // AML Reset Password
    amlResetPasswordTitle: 'Reset Password',
    amlResetPasswordFieldNewPassword: 'New Password',
    amlResetPasswordFieldConfirmPassword: 'Confirm Password',
    amlResetPasswordPlaceholderNew: 'Enter new password',
    amlResetPasswordPlaceholderConfirm: 'Confirm new password',
    amlResetPasswordRuleAtLeast8Chars: 'At least 8 characters',
    amlResetPasswordRuleUppercase: 'Uppercase letter',
    amlResetPasswordRuleLowercase: 'Lowercase letter',
    amlResetPasswordRuleNumber: 'Number',
    amlResetPasswordRuleSpecialChar: 'Special character',
    amlResetPasswordValidationRequired: 'Password is required',
    amlResetPasswordValidationMinLength: 'Password must be at least {{count}} characters',
    amlResetPasswordValidationDoesNotMeetRequirements: 'Password does not meet all requirements',
    amlResetPasswordValidationConfirmRequired: 'Please confirm the password',
    amlResetPasswordValidationDoNotMatch: 'Passwords do not match',
    amlResetPasswordBtnSetPassword: 'Set Password',
    amlResetPasswordToastSuccess: 'Password updated successfully',

    // AML Shared
    amlPageSizeShow: 'Show',
    amlPageSizePerPage: 'per page',
    amlEmptyStateApplyFiltersToLoadData: 'Apply filters to load data',
    amlEmptyStateApplyFiltersDesc: 'Use the filters above to load the report.',
    amlEmptyStateNoResultsFound: 'No results found',
    amlEmptyStateNoResultsDesc: 'Try adjusting your search or filters.',
    amlInlineCellPlaceholderEnter: 'Enter…',
    amlUserLoginFailedError: 'Failed to login as user.',

    // Performance Sidebar
    perfSidebarTitle: 'Performance',
    perfNavMyKpis: 'My KPIs',
    perfNavMyKpisDesc: 'Personal dashboard',
    perfNavTeam: 'Team',
    perfNavTeamDesc: "Direct reports' KPIs",
    perfNavOrganization: 'Organization',
    perfNavOrganizationDesc: 'Org-wide overview',
    perfNavKpiCatalog: 'KPI Catalog',
    perfNavKpiCatalogDesc: 'Templates & definitions',
    perfNavCycles: 'Cycles',
    perfNavCyclesDesc: 'Performance cycles',

    // Performance Header
    perfHeaderTitle: 'Performance',
    perfHeaderSubtitleDefault: 'KPI tracking and insights',
    perfBtnAssignKpi: 'Assign KPI',

    // Performance My KPIs
    perfMyKpisGetStartedTitle: 'Get Started with Performance Tracking',
    perfMyKpisGetStartedDesc: 'To track KPIs, you first need to create a performance cycle — a time period like a quarter or half-year during which KPIs are tracked. This lets you compare performance across periods and keep goals focused.',
    perfMyKpisCreateFirstCycle: 'Create Your First Cycle',
    perfMyKpisAskAdminToCreateCycle: 'Ask your admin to create a performance cycle in the Cycles tab.',
    perfMyKpisNoActiveCycleTitle: 'No Active Cycle Selected',
    perfMyKpisNoActiveCycleDesc: 'Select a cycle from the dropdown in the header, or activate one of your draft cycles in the Cycles tab.',
    perfMyKpisOverallScore: 'Overall Score',
    perfMyKpisSummaryOnTrack: 'On Track',
    perfMyKpisSummaryAtRisk: 'At Risk',
    perfMyKpisSummaryBehind: 'Behind',
    perfMyKpisSummaryNotStarted: 'Not Started',
    perfMyKpisSummaryCompleted: 'Completed',
    perfMyKpisNoKpisAssigned: 'No KPIs assigned for this cycle',
    perfMyKpisNoKpisDesc: 'Browse the KPI Catalog to assign KPIs, or your manager will set them for you.',

    // Performance Team
    perfTeamNoDirectReportsTitle: 'No Direct Reports',
    perfTeamNoDirectReportsDesc: 'This tab shows KPI progress for people who report to you. Currently, no one in the organization has their "Reports To" field set to you.',
    perfTeamSetupInstructions: 'To set up your team:',
    perfTeamSetupStep1: 'Go to the Organization module (gear icon, sidebar)',
    perfTeamSetupStep2: "Open a team member's profile in the People tab",
    perfTeamSetupStep3: 'Set their "Reports To" field to yourself',
    perfTeamMemberCount: '{{count}} team member(s)',
    perfTeamOnTrack: '{{count}} on track',
    perfTeamAtRisk: '{{count}} at risk',
    perfTeamBehind: '{{count}} behind',
    perfTeamBtnAiSummary: 'AI Summary',
    perfTeamAiSummaryGenerating: 'Generating...',
    perfTeamAiSummaryTitle: 'AI Team Summary',
    perfTeamNoKpisAssigned: 'No KPIs assigned',

    // Performance Organization
    perfOrgNoActiveCycleTitle: 'No Active Cycle',
    perfOrgNoActiveCycleDesc: 'There is no active performance cycle. Create one to see organization-wide KPI data.',
    perfOrgSummaryTotalKpis: 'Total KPIs',
    perfOrgSummaryOnTrack: 'On Track',
    perfOrgSummaryAtRisk: 'At Risk',
    perfOrgSummaryBehind: 'Behind',
    perfOrgSummaryNotStarted: 'Not Started',
    perfOrgSummaryOverallScore: 'Overall Score',
    perfOrgByDepartment: 'By Department',
    perfOrgAllMembers: 'All Members ({{count}})',
    perfOrgMemberKpiCount: '{{count}} KPIs',
    perfOrgNoMembersWithKpis: 'No members with KPIs in this department.',

    // Performance KPI Detail
    perfKpiDetailTabProgress: 'Progress',
    perfKpiDetailTabComments: 'Comments',
    perfKpiDetailTabReviews: 'Reviews',
    perfKpiDetailCheckIn: 'Check In',
    perfKpiDetailAssignedTo: 'Assigned to {{name}}',
    perfKpiDetailAiInsight: 'AI Insight',
    perfKpiDetailCheckInHistory: 'Check-in History',
    perfKpiDetailNoCheckIns: 'No check-ins yet',
    perfKpiDetailNoComments: 'No comments yet. Start the conversation.',
    perfKpiDetailNoReviews: 'No reviews yet. Reviews are submitted during the review phase.',
    perfKpiDetailAddComment: 'Add a comment...',
    perfKpiDetailOverdue: '({{count}} overdue)',
    perfKpiDetailFinancialImpact: 'Financial impact: ${{value}}',
    perfKpiDetailStrengths: 'Strengths: {{text}}',
    perfKpiDetailImprovements: 'Areas to improve: {{text}}',
    perfKpiDetailDeleteKpiQuestion: 'Delete this KPI and all its data?',
    perfKpiDetailArchiveKpiQuestion: 'Archive this KPI?',
    perfKpiDetailBtnDelete: 'Yes, delete',
    perfKpiDetailBtnArchive: 'Yes, archive',
    perfKpiDetailBtnCancel: 'Cancel',
    perfKpiDetailAriaLabelClose: 'Close panel',
    perfKpiDetailAriaLabelArchive: 'Archive this KPI',
    perfKpiDetailAriaLabelDelete: 'Delete this KPI',
    perfKpiDetailAriaLabelSendComment: 'Send comment',
    perfKpiDetailRevisionFeedbackPlaceholder: 'Revision feedback...',
    perfKpiDetailBtnVerify: 'Verify',
    perfKpiDetailBtnRequestRevision: 'Request Revision',
    perfKpiDetailBtnSend: 'Send',
    perfKpiDetailStatusSubmitted: 'Submitted',
    perfKpiDetailStatusVerified: 'Verified',
    perfKpiDetailStatusNeedsRevision: 'Needs Revision',
    perfKpiDetailReviewNote: 'Review note: {{text}}',

    // Performance KPI Card
    perfCardNoCheckInsYet: 'No check-ins yet',
    perfCardCheckIn: 'Check In',
    perfCardUnnamedKpi: 'Unnamed KPI',

    // Performance KPI Status
    perfKpiStatusOnTrack: 'On track',
    perfKpiStatusAtRisk: 'At risk',
    perfKpiStatusBehind: 'Behind',
    perfKpiStatusNotStarted: 'Not started',
    perfKpiStatusCompleted: 'Completed',
    perfKpiStatusPending: 'Pending',
    perfKpiStatusMissed: 'Missed',
    perfKpiStatusCancelled: 'Cancelled',

    // Performance Check-in Form
    perfCheckInFormTitle: 'Check In',
    perfCheckInBtnCancel: 'Cancel',
    perfCheckInFieldCurrentValue: 'Current Value',
    perfCheckInFieldFinancialImpact: 'Financial Impact (this period)',
    perfCheckInFieldNotes: 'Notes (optional)',
    perfCheckInFieldEvidence: 'Evidence (optional)',
    perfCheckInNotesPlaceholder: 'What drove this number? Any context for your manager...',
    perfCheckInAttachFiles: 'Attach files (PDF, images, spreadsheets)',
    perfCheckInUploading: 'Uploading...',
    perfCheckInFileTooLarge: 'File "{{name}}" exceeds 50MB limit',
    perfCheckInErrorFailedToSubmit: 'Failed to submit check-in',
    perfCheckInBtnSubmit: 'Submit Check-in',

    // Performance Assign KPI
    perfAssignKpiTitleTemplate: 'Assign: {{name}}',
    perfAssignKpiTitleCustom: 'Create & Assign KPI',
    perfAssignKpiStep1: '1. Define',
    perfAssignKpiStep2: '2. Assign',
    perfAssignKpiFieldKpiName: 'KPI Name',
    perfAssignKpiFieldDescription: 'Description',
    perfAssignKpiSectionMeasurement: 'Measurement',
    perfAssignKpiFieldTarget: 'Target',
    perfAssignKpiFieldBaseline: 'Baseline',
    perfAssignKpiFieldWeight: 'Weight',
    perfAssignKpiFieldUnitLabel: 'Unit Label',
    perfAssignKpiSectionCheckInFrequency: 'Check-in Frequency',
    perfAssignKpiCheckInFrequencyDesc: 'How often the assignee is expected to report progress. This is not the cycle — the cycle defines the overall period (e.g. Q1 2026), while cadence sets how many check-ins happen within it.',
    perfAssignKpiFieldFormula: 'Formula / Calculation Method',
    perfAssignKpiSectionVerifier: 'Check-in Verifier',
    perfAssignKpiVerifierOptional: 'Optional',
    perfAssignKpiVerifierPlaceholder: 'Choose who validates check-ins (e.g. Finance, a manager)...',
    perfAssignKpiSearchVerifier: 'Search verifier...',
    perfAssignKpiTrackFinancialImpact: 'Track Financial Impact',
    perfAssignKpiFinancialImpactDescOn: 'Set the expected monetary value this KPI will generate or save',
    perfAssignKpiFinancialImpactDescOff: 'Optional — link this KPI to a revenue or savings target',
    perfAssignKpiFieldExpectedFinancialImpact: 'Expected Financial Impact ($)',
    perfAssignKpiFinancialImpactNote: 'This value will track against actual impact reported in each check-in, giving you a running ROI view.',
    perfAssignKpiFromTemplate: 'From template:',
    perfAssignKpiSearchTeamMembers: 'Search team members...',
    perfAssignKpiSelectedCount: '{{count}} selected',
    perfAssignKpiAvailableCount: '{{count}} available',
    perfAssignKpiBtnCancel: 'Cancel',
    perfAssignKpiBtnNextPickPeople: 'Next: Pick People',
    perfAssignKpiBtnAssign: 'Assign KPI to {{count}} person/people',
    perfAssignKpiErrorFailed: 'Failed to assign KPI',
    perfAssignKpiKpiNamePlaceholder: 'e.g., Revenue Growth Rate, Net Value Creation...',
    perfAssignKpiDescriptionPlaceholder: 'What does this KPI measure and why does it matter?',
    perfAssignKpiFormulaPlaceholder: 'How is this KPI calculated? What\'s included/excluded?',
    perfAssignKpiUnitLabelPlaceholder: 'e.g., deliverables, departments, milestones...',
    perfAssignKpiFinancialImpactPlaceholder: 'e.g., 50 000',

    // Performance Units
    perfUnitCurrency: 'Currency ($)',
    perfUnitPercentage: 'Percentage (%)',
    perfUnitCount: 'Count',
    perfUnitScore: 'Score',
    perfUnitHours: 'Hours',
    perfUnitRatio: 'Ratio',
    perfUnitCustom: 'Custom',

    // Performance Categories
    perfCategoryFinancial: 'Financial',
    perfCategorySales: 'Sales',
    perfCategoryMarketing: 'Marketing',
    perfCategoryOperations: 'Operations',
    perfCategoryProduct: 'Product',
    perfCategoryHrPeople: 'HR & People',
    perfCategoryCustomerSuccess: 'Customer Success',
    perfCategoryCustom: 'Custom',

    // Performance Cadences
    perfCadenceWeekly: 'Weekly',
    perfCadenceBiweekly: 'Bi-weekly',
    perfCadenceMonthly: 'Monthly',
    perfCadenceQuarterly: 'Quarterly',

    // Performance Directionality
    perfDirectionalityHigherBetter: 'Higher is better',
    perfDirectionalityLowerBetter: 'Lower is better',
    perfDirectionalityHitExact: 'Hit exact target',

    // Performance Cycle Modal
    perfCycleModalTitle: 'New Performance Cycle',
    perfCycleFieldCycleType: 'Cycle Type',
    perfCycleFieldCycleName: 'Cycle Name',
    perfCycleFieldStartDate: 'Start Date',
    perfCycleFieldEndDate: 'End Date',
    perfCyclePlaceholderName: 'e.g., Marketing, Engineering, Company...',
    perfCyclePlaceholderStartDate: 'Pick start date',
    perfCyclePlaceholderEndDate: 'Pick end date',
    perfCycleWillDisplayAs: 'Will display as:',
    perfCycleAutoAdvance: 'Auto-advance to next cycle',
    perfCycleAutoAdvanceDesc: 'When this cycle closes, automatically create the next period and carry over active KPIs',
    perfCycleValidationFillAllFields: 'Please fill in all fields',
    perfCycleValidationEndDateAfterStart: 'End date must be after start date',
    perfCycleErrorFailedToCreate: 'Failed to create cycle',
    perfCycleBtnCancel: 'Cancel',
    perfCycleBtnCreate: 'Create Cycle',
    perfCycleTypeWeekly: 'Weekly',
    perfCycleTypeWeeklyDesc: 'Weeks in a year',
    perfCycleTypeMonthly: 'Monthly',
    perfCycleTypeMonthlyDesc: 'Calendar months',
    perfCycleTypeQuarterly: 'Quarterly',
    perfCycleTypeQuarterlyDesc: 'Q1, Q2, Q3, Q4',
    perfCycleTypeSemiAnnual: 'Semi-Annual',
    perfCycleTypeSemiAnnualDesc: 'H1 and H2',
    perfCycleTypeAnnual: 'Annual',
    perfCycleTypeAnnualDesc: 'Full year',
    perfCycleTypeCustom: 'Custom',
    perfCycleTypeCustomDesc: 'Set your own dates',

    // Performance Cycle Manager
    perfCycleManagerTitle: 'Performance Cycles',
    perfCycleManagerBtnNewCycle: 'New Cycle',
    perfCycleManagerWhatsACycleTitle: "What's a performance cycle?",
    perfCycleManagerWhatsACycleDesc: 'It\'s a recurring time period — a week, month, quarter, or year — during which KPIs are tracked. When a cycle closes, it auto-advances to the next period and carries over your active KPIs. Click "New Cycle" to get started.',
    perfCycleManagerStatusDraft: 'Draft',
    perfCycleManagerStatusActive: 'Active',
    perfCycleManagerStatusInReview: 'In Review',
    perfCycleManagerStatusClosed: 'Closed',
    perfCycleManagerBtnActivate: 'Activate',
    perfCycleManagerBtnStartReview: 'Start Review',
    perfCycleManagerBtnClose: 'Close',
    perfCycleManagerBtnDelete: 'Delete',
    perfCycleManagerBtnNo: 'No',
    perfCycleManagerRecurringCycleTooltip: 'Recurring cycle',
    perfCycleManagerAriaLabelDeleteCycle: 'Delete cycle',
    perfCycleManagerNoCyclesTitle: 'No performance cycles yet',
    perfCycleManagerNoCyclesDesc: 'Click "New Cycle" above to create your first cycle.',
    perfCycleManagerErrorCannotDelete: 'Cannot delete',

    // Performance KPI Catalog
    perfCatalogTitle: 'KPI Catalog',
    perfCatalogTemplatesCount: '{{count}} templates',
    perfCatalogBtnCustomKpi: 'Custom KPI',
    perfCatalogSearchPlaceholder: 'Search templates by name, description, or business question...',
    perfCatalogFilterAll: 'All',
    perfCatalogNoTemplatesMatch: 'No templates match your search',
    perfCatalogTemplateUnit: 'Unit:',
    perfCatalogTemplateTarget: 'Target:',
    perfCatalogTemplateCadence: 'Cadence:',

    // Settings Sidebar
    settingsSidebarTitle: 'Settings',
    settingsNavOrganization: 'Organization',
    settingsNavOrganizationDesc: 'People, departments & settings',
    settingsNavPlatform: 'Platform',
    settingsNavPlatformDesc: 'Global platform configuration',

    // Settings Platform
    settingsPlatformTitle: 'Platform Settings',
    settingsPlatformSubtitle: 'Tenant, organization & module management',
    settingsTabTenants: 'Tenants',
    settingsTabOrganizations: 'Organizations',
    settingsTabAi: 'AI',
    settingsTabDemo: 'Demo',

    // LLM / Anthropic API keys
    settingsLlmPlatformTitle: 'Anthropic API Key',
    settingsLlmPlatformHelp:
      'Platform default key used by all organizations that have not set their own key. Required for AI features.',
    settingsLlmOrgTitle: 'Anthropic API Key',
    settingsLlmOrgHelp:
      'Optional organization key. If not set, AI features use the platform default key.',
    settingsLlmFieldLabel: 'API key',
    settingsLlmFieldPlaceholder: 'sk-ant-...',
    settingsLlmConfigured: 'Configured',
    settingsLlmNotConfigured: 'Not configured',
    settingsLlmUsingPlatform: 'Using platform default',
    settingsLlmBtnSave: 'Save key',
    settingsLlmBtnUpdate: 'Update key',
    settingsLlmBtnClear: 'Clear key',
    settingsLlmBtnClearOrg: 'Use platform default',
    settingsLlmClearConfirmTitle: 'Clear API key?',
    settingsLlmClearConfirmMessage:
      'AI features will stop working until a new platform key is set (or orgs configure their own).',
    settingsLlmClearOrgConfirmMessage:
      'This organization will fall back to the platform Anthropic API key.',
    settingsLlmSaveSuccess: 'API key saved',
    settingsLlmSaveError: 'Failed to save API key',
    settingsLlmClearSuccess: 'API key cleared',
    settingsLlmClearError: 'Failed to clear API key',
    settingsLlmError: 'Failed to load API key status',
    settingsLlmModelTitle: 'Claude model',
    settingsLlmModelHelp:
      'Platform-wide Anthropic model ID used by all AI features (analysis, chat, KPI, discovery, sales, survey, AML).',
    settingsLlmModelFieldLabel: 'Model ID',
    settingsLlmModelFieldPlaceholder: 'claude-sonnet-5',
    settingsLlmModelBtnSave: 'Save model',
    settingsLlmModelSaveSuccess: 'Model saved',
    settingsLlmModelSaveError: 'Failed to save model',

    // Settings Tenants
    settingsTenantsBtnCreateTenant: 'Create Tenant',
    settingsTenantsFieldName: 'Name',
    settingsTenantsFieldSlug: 'Slug',
    settingsTenantsPlaceholderName: 'e.g. Acme Corp',
    settingsTenantsPlaceholderSlug: 'e.g. acme-corp',
    settingsTenantsNoTenants: 'No tenants yet',
    settingsTenantsBtnCreate: 'Create',
    settingsTenantsBtnCancel: 'Cancel',
    settingsTenantsBtnSuspend: 'Suspend',
    settingsTenantsBtnActivate: 'Activate',
    settingsTenantsCount: '{{count}} tenant',
    settingsTenantsCountPlural: '{{count}} tenants',
    settingsTenantsBadgeHq: 'HQ',
    settingsTenantsNoOrgsInTenant: 'No organizations in this tenant',
    settingsTenantsModuleAccess: 'Module Access',
    settingsTenantsAriaLabelInviteUser: 'Invite user to this org',
    settingsTenantsToastTenantCreated: 'Tenant created',
    settingsTenantsToastTenantSuspended: 'Tenant suspended',
    settingsTenantsToastTenantActivated: 'Tenant active',
    settingsTenantsToastModuleDisabled: 'Module disabled',
    settingsTenantsToastModuleEnabled: 'Module enabled',
    settingsTenantsErrorFailedToCreateTenant: 'Failed to create tenant',
    settingsTenantsErrorFailedToUpdateTenant: 'Failed to update tenant',
    settingsTenantsErrorFailedToToggleModule: 'Failed to toggle module',
    settingsTenantsErrorFailedToEnableModule: 'Failed to enable module',
    settingsProvisionModalTitle: 'Enable Module for Users',
    settingsProvisionModalBody: 'Enable this module for all existing users in the organization, or leave it disabled and let the org admin assign access manually?',
    settingsProvisionBtnCancel: 'Cancel',
    settingsProvisionBtnNoneManual: 'None (manual)',
    settingsProvisionBtnAllUsers: 'All Users',

    // Settings Organizations
    settingsOrgsBtnCreateOrg: 'Create Org',
    settingsOrgsFilterByTenant: 'Filter by tenant',
    settingsOrgsPlaceholderAllTenants: 'All tenants',
    settingsOrgsPlaceholderSelectTenant: 'Select tenant',
    settingsOrgsFieldOrganizationName: 'Organization Name',
    settingsOrgsPlaceholderOrgName: 'e.g. Operations LLC',
    settingsOrgsNoOrganizations: 'No organizations',
    settingsOrgsCount: '{{count}} organization',
    settingsOrgsCountPlural: '{{count}} organizations',
    settingsOrgsRoleCount: '{{count}} role',
    settingsOrgsRoleCountPlural: '{{count}} roles',
    settingsOrgsUnknownTenant: 'Unknown',
    settingsOrgsToastOrgCreated: 'Organization created',
    settingsOrgsErrorFailedToCreateOrg: 'Failed to create org',
    settingsOrgsModuleAccess: 'Module Access',
    settingsOrgsAriaLabelInviteUser: 'Invite user to this org',
    settingsOrgsLeadApiKeyTitle: 'Lead Form API Key',
    settingsOrgsLeadApiKeyHelp: 'Use this key as the X-API-Key header when submitting leads from your website.',
    settingsOrgsLeadApiKeyCopy: 'Copy',
    settingsOrgsLeadApiKeyCopied: 'Copied!',
    settingsOrgsLeadApiKeyError: 'Failed to load API key',
    settingsOrgsLeadApiKeyShow: 'Show key',
    settingsOrgsLeadApiKeyHide: 'Hide key',
    settingsOrgsLeadApiKeyRegenerate: 'Regenerate',
    settingsOrgsLeadApiKeyRegenerateConfirmTitle: 'Regenerate API key?',
    settingsOrgsLeadApiKeyRegenerateConfirmMessage:
      'The current key will stop working immediately. You must update any website or integration that uses the old key.',
    settingsOrgsLeadApiKeyRegenerateSuccess: 'API key regenerated',
    settingsOrgsLeadApiKeyRegenerateError: 'Failed to regenerate API key',

    // Settings Demo
    settingsDemoTitle: 'Demo Organizations',
    settingsDemoSubtitle: 'Create fully-populated demo organizations with departments, users, KPIs, and performance data.',
    settingsDemoBtnCreateDemoOrg: 'Create Demo Org',
    settingsDemoPlaceholderOrgName: 'Organization name (e.g. Acme Corp)',
    settingsDemoCreatedTitle: 'Demo Created',
    settingsDemoDismiss: 'Dismiss',
    settingsDemoFieldOrganization: 'Organization',
    settingsDemoFieldStats: 'Stats',
    settingsDemoFieldCredentials: 'Credentials',
    settingsDemoFieldEmail: 'Email',
    settingsDemoFieldPassword: 'Password',
    settingsDemoStatUsers: '{{count}} users',
    settingsDemoStatKpis: '{{count}} KPIs',
    settingsDemoNoDemoOrgs: 'No demo organizations yet',
    settingsDemoOrgCount: '{{count}} demo org',
    settingsDemoOrgCountPlural: '{{count}} demo orgs',
    settingsDemoToastDemoOrgCreated: 'Demo org created',
    settingsDemoToastDemoOrgDeleted: 'Demo org deleted',
    settingsDemoErrorFailedToCreate: 'Failed to create demo org',
    settingsDemoErrorFailedToDelete: 'Failed to delete',
    settingsDemoDeleteTitle: 'Delete Demo Organization',
    settingsDemoDeleteBody: 'This will permanently delete {{name}} and all its data.',
    settingsDemoBtnDeleteEverything: 'Delete Everything',
    settingsDemoBtnCancel: 'Cancel',

    // Discovery Portal
    discoveryPortalTitle: 'Discovery Agent',
    discoveryTabBoard: 'Board',
    discoveryTabChat: 'Chat',
    discoveryTabSpec: 'Spec',
    discoveryBoardFilterAll: 'All',
    discoveryBoardFilterCollecting: 'Collecting',
    discoveryBoardFilterSpecReady: 'Spec Ready',
    discoveryBoardFilterSubmitted: 'Submitted',
    discoveryBoardFilterInProgress: 'In Progress',
    discoveryBoardBtnNewRequest: 'New Request',
    discoveryBoardFailedToLoad: 'Failed to load requests.',
    discoveryBoardBtnRetry: 'Retry',
    discoveryBoardNoneYet: 'No discovery requests yet.',
    discoveryBoardBtnCreateFirst: 'Create your first request',
    discoveryBoardCardUntitledRequest: 'Untitled Request',
    discoveryBoardCardUnknownInitiator: 'Unknown',
    discoveryBoardNewRequestDialogTitle: 'New Discovery Request',
    discoveryBoardNewRequestLabel: 'Describe what you need built or changed',
    discoveryBoardNewRequestPlaceholder: 'e.g. We need a dashboard that shows real-time sales data for all regions...',
    discoveryBoardBtnCancelDialog: 'Cancel',
    discoveryBoardBtnCreateRequest: 'Create Request',
    discoveryStatusDraft: 'Draft',
    discoveryStatusCollecting: 'Collecting',
    discoveryStatusSpecReady: 'Spec Ready',
    discoveryStatusSubmitted: 'Submitted',
    discoveryStatusInProgress: 'In Progress',
    discoveryStatusImplemented: 'Implemented',
    discoveryStatusDeclined: 'Declined',
    discoveryChatSelectRequest: 'Select a request from the board to start chatting.',
    discoveryChatNotificationsOn: 'Notifications on',
    discoveryChatNotificationsOff: 'Notifications off',
    discoveryChatEmailNotificationsOn: 'Email notifications on',
    discoveryChatEmailNotificationsOff: 'Email notifications off',
    discoveryChatStakeholders: 'Stakeholders',
    discoveryChatUntitledRequest: 'Untitled Request',
    discoveryChatDiscoveryAgentLabel: 'Discovery Agent',
    discoveryChatInputPlaceholder: "Describe your needs or answer the agent's questions...",
    discoveryChatDropFilesHere: 'Drop files here',
    discoveryChatBtnAddMoreInfo: 'Add More Info',
    discoveryChatAriaLabelAttachFile: 'Attach file',
    discoveryActionSuggestedStakeholder: 'Suggested Stakeholder',
    discoveryActionSameOrDifferentPerson: '{{name}} is already a stakeholder. Is this the same person or someone different?',
    discoveryActionBtnSamePerson: 'Same person',
    discoveryActionBtnDifferentPerson: 'Different person',
    discoveryActionStakeholderLabelName: 'Name',
    discoveryActionStakeholderLabelContext: 'Context',
    discoveryActionStakeholderNamePlaceholder: 'Name',
    discoveryActionStakeholderContextPlaceholder: 'Why this person is relevant...',
    discoveryActionSearchByNameOrEmail: 'Search by name or enter email...',
    discoveryActionBtnAddStakeholder: 'Add Stakeholder',
    discoveryActionBtnAdding: 'Adding...',
    discoveryActionFollowUpRequestTitle: 'Follow-up Request',
    discoveryActionBtnSend: 'Send',
    discoveryActionBtnSkip: 'Skip',
    discoveryActionReadyToGenerateSpecTitle: 'Ready to Generate Spec',
    discoveryActionReadyToGenerateSpecDesc: 'The agent has gathered enough information to generate a specification document.',
    discoveryActionBtnGenerateSpec: 'Generate Spec',
    discoveryStakeholderPanelTitle: 'Stakeholders',
    discoveryStakeholderPanelNoStakeholders: 'No stakeholders yet.',
    discoveryStakeholderPanelNoStakeholdersDesc: 'Add people who can provide input on this request.',
    discoveryStakeholderAriaLabelAdd: 'Add stakeholder',
    discoveryStakeholderSearchPlaceholder: 'Search by email or name...',
    discoveryStakeholderContextPlaceholder: 'Relevant for... (e.g. knows the billing system)',
    discoveryStakeholderBtnAdd: 'Add Stakeholder',
    discoveryStakeholderBadgeExternal: 'External',
    discoveryStakeholderBadgeInternal: 'Internal',
    discoveryStakeholderBadgeAppUser: 'App User',
    discoveryStakeholderStatusInvited: 'Invited',
    discoveryStakeholderStatusActive: 'Active',
    discoveryStakeholderStatusCompleted: 'Completed',
    discoveryStakeholderErrorAlreadyExists: 'This person is already a stakeholder on this request.',
    discoveryStakeholderErrorFailedToAdd: 'Failed to add stakeholder',
    discoverySpecTitle: 'Specification',
    discoverySpecBtnBackToChat: 'Back to Chat',
    discoverySpecVersionLabel: 'Version {{count}}',
    discoverySpecBtnDownloadMd: 'Download MD',
    discoverySpecBtnExportPdf: 'Export PDF',
    discoverySpecBtnRequestRevision: 'Request Revision',
    discoverySpecNoSpecTitle: 'No specification generated yet.',
    discoverySpecNoSpecDesc: 'Continue chatting with the Discovery Agent to gather requirements, then generate a spec when ready.',
    discoverySpecBtnGenerateSpec: 'Generate Spec',
    discoverySpecErrorFailedToLoad: 'Failed to load spec versions',
    discoverySpecErrorFailedToGenerate: 'Failed to generate spec',
    discoveryExternalPortalBrandDiscovery: 'Discovery',
    discoveryExternalPortalClosedBanner: 'This request has been closed. You can review the conversation but cannot send new messages.',
    discoveryExternalPortalDefaultWelcome: "You've been invited to share your perspective on this feature request. The agent will ask you targeted questions.",
    discoveryExternalPortalEmptyPrompt: 'Share your thoughts, requirements, or questions about this feature request. The discovery agent will guide the conversation.',
    discoveryExternalPortalInputPlaceholder: 'Type your message…',
    discoveryExternalPortalBtnAddMoreInfo: 'Add More Info',
    discoveryExternalPortalLoadingPortal: 'Loading portal…',
    discoveryExternalPortalErrorTitle: 'Unable to Load',
    discoveryExternalPortalErrorDefault: 'This link is invalid or has expired. Please check with the person who shared it.',
    discoveryExternalPortalErrorInvalidLink: 'Invalid link — no access token provided.',
    discoveryExternalPortalErrorExpiredLink: 'This link is invalid or has expired.',
    discoveryExternalPortalErrorClosed: 'This discovery request has been closed.',
    discoveryExternalPortalErrorLoadFailed: 'Failed to load. Please try again later.',
    discoveryExternalPortalErrorUploadFailed: 'File upload failed. Please try again.',
    discoveryExternalPortalErrorSomethingWentWrong: 'Sorry, something went wrong. Please try again.',
    discoveryExternalPortalInterjectionAddressed: 'Thanks — your input on this has already been captured. You can continue below if you have more to add.',
    discoveryExternalPortalAriaLabelAttachFile: 'Attach file',
    discoveryExternalPortalAriaLabelStopGenerating: 'Stop generating',
    discoveryExternalPortalAriaLabelSendMessage: 'Send message',

    // Survey Portal
    surveyPortalTitle: 'Surveys',
    surveyTabBoard: 'Board',
    surveyTabEditor: 'Editor',
    surveyTabResults: 'Results',

    // Survey Board
    surveyBoardFilterAll: 'All',
    surveyBoardFilterDrafts: 'Drafts',
    surveyBoardFilterInProgress: 'In Progress',
    surveyBoardFilterCompleted: 'Completed',
    surveyBoardStatusDraft: 'Draft',
    surveyBoardStatusInProgress: 'In Progress',
    surveyBoardStatusCompleted: 'Completed',
    surveyBoardBtnNewSurvey: 'New Survey',
    surveyBoardFailedToLoad: 'Failed to load surveys.',
    surveyBoardBtnRetry: 'Retry',
    surveyBoardNoneYet: 'No surveys yet.',
    surveyBoardBtnCreateFirst: 'Create your first survey',
    surveyBoardCardUntitled: 'Untitled Survey',
    surveyBoardCardUnknownCreator: 'Unknown',
    surveyBoardBtnStop: 'Stop',
    surveyBoardDeleteTitle: 'Delete Survey',
    surveyBoardDeleteMessage: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
    surveyBoardDeleteConfirm: 'Delete',
    surveyBoardDeleteDeleting: 'Deleting…',
    surveyBoardDeleteTooltip: 'Delete survey',
    surveyBoardBtnCancel: 'Cancel',
    surveyBoardUploadTooltip: 'Upload response',

    // Survey Upload Modal
    surveyUploadTitle: 'Upload Survey Response',
    surveyUploadLabelName: 'Respondent Name',
    surveyUploadPlaceholderName: 'Full name…',
    surveyUploadLabelEmail: 'Respondent Email',
    surveyUploadPlaceholderEmail: 'email@example.com',
    surveyUploadLabelContent: 'Response Content',
    surveyUploadPlaceholderContent: 'Paste the email or text containing answers here…',
    surveyUploadModePaste: 'Paste Text',
    surveyUploadModeFile: 'Upload File',
    surveyUploadDropHint: 'Drop a file here (.txt, .csv, .xlsx, .doc, .docx, .pdf, …)',
    surveyUploadProcessing: 'Analyzing content with AI…',
    surveyUploadConfidenceLabel: 'AI confidence: {{value}}%',
    surveyUploadMappedCount: '{{count}} of {{total}} questions mapped',
    surveyUploadLowConfidence: 'The content does not appear to be a valid response to this survey. Please try a different text or select the correct survey.',
    surveyUploadProcessingFailed: 'Failed to process the content. Please try again.',
    surveyUploadStoreFailed: 'Failed to store the response.',
    surveyUploadSuccessToast: 'Response uploaded successfully!',
    surveyUploadTryDifferent: 'Try pasting a different text or uploading a different file.',
    surveyUploadBtnCancel: 'Cancel',
    surveyUploadBtnProcess: 'Process with AI',
    surveyUploadBtnBack: 'Back',
    surveyUploadBtnConfirm: 'Confirm & Save',
    surveyUploadBtnTryAgain: 'Try Again',
    surveyUploadBtnProceedAnyway: 'Proceed Anyway',
    surveyUploadLowConfirmTitle: 'Low Confidence — Are You Sure?',
    surveyUploadLowConfirmMessage: 'AI confidence is only {{value}}%. The mapped answers may be inaccurate. Do you still want to save this response?',
    surveyUploadLowConfirmYes: 'Yes, Save Anyway',
    surveyUploadEmailRequired: 'Email is required',
    surveyUploadEmailInvalid: 'Please enter a valid email',
    surveyUploadNameRequired: 'Name is required',

    // Survey Editor
    surveyEditorLabelTitle: 'Survey Title',
    surveyEditorPlaceholderTitle: 'Enter survey title...',
    surveyEditorLabelRecipients: 'Recipients',
    surveyEditorRecipientCount: '{{count}} recipient',
    surveyEditorRecipientCountPlural: '{{count}} recipients',
    surveyEditorPlaceholderEmail: 'Enter email and press Enter...',
    surveyEditorBtnAdd: 'Add',
    surveyEditorUploadExtracting: 'Extracting emails...',
    surveyEditorUploadLabel: 'Upload file to extract emails (CSV, Excel, PDF, TXT)',
    surveyEditorLabelPublicLink: 'Public Link',
    surveyEditorPublicLinkCaption: "Allow anyone with the link to respond (they'll enter their name and email).",
    surveyEditorPublicLinkPendingSave: ' The link will be generated when you save.',
    surveyEditorAriaLabelCopyLink: 'Copy link',
    surveyEditorLabelDeadline: 'Deadline (optional)',
    surveyEditorPlaceholderDeadline: 'Select deadline...',
    surveyEditorLabelQuestions: 'Questions',
    surveyEditorPlaceholderQuestion: 'Type your question...',
    surveyEditorBtnAddQuestion: 'Add Question',
    surveyEditorBtnSave: 'Save',
    surveyEditorBtnSaveAndSend: 'Save & Send',
    surveyEditorToastExtractedEmails: 'Extracted {{count}} new email(s) from {{filename}}',
    surveyEditorToastNoNewEmails: 'No new emails found in file',
    surveyEditorErrorExtractEmails: 'Failed to extract emails from file',
    surveyEditorErrorToggleLink: 'Failed to toggle public link',
    surveyEditorToastLinkCopied: 'Link copied to clipboard',
    surveyEditorErrorTitleRequired: 'Title and at least one question are required',
    surveyEditorErrorRecipientRequired: 'Add at least one recipient or enable public link to send',
    surveyEditorToastSaved: 'Survey saved',
    surveyEditorErrorSaveFailed: 'Failed to save survey',
    surveyEditorToastSent: 'Survey sent',
    surveyEditorErrorSendFailed: 'Failed to send survey',

    // Survey Results
    surveyResultsTabAiSummary: 'AI Summary',
    surveyResultsTabResponses: 'Responses',
    surveyResultsBtnDownloadPdf: 'Download PDF',
    surveyResultsBtnRegenerate: 'Regenerate Summary',
    surveyResultsAiLoading: 'Generating AI summary...',
    surveyResultsAiNotAvailable: 'AI summary not available yet.',
    surveyResultsOverview: 'Overview',
    surveyResultsKeyTakeaways: 'Key Takeaways',
    surveyResultsFallbackTitle: 'AI Summary',
    surveyResultsSentimentPositive: 'Positive',
    surveyResultsSentimentNegative: 'Negative',
    surveyResultsSentimentMixed: 'Mixed',
    surveyResultsSentimentNeutral: 'Neutral',
    surveyResultsResponseCount: '{{count}} response',
    surveyResultsResponseCountPlural: '{{count}} responses',
    surveyResultsSearchPlaceholder: 'Search by name or email...',
    surveyResultsAllRecipients: 'All Recipients ({{responded}} of {{total}} responded)',
    surveyResultsTagViaLink: 'Via link',
    surveyResultsBadgeCompleted: 'Completed',
    surveyResultsBadgePending: 'Pending',
    surveyResultsNotRespondedYet: "This recipient hasn't responded yet.",
    surveyResultsNoAnswers: 'No answers recorded.',
    surveyResultsFallbackQuestion: 'Question',
    surveyResultsSelectRecipient: 'Select a recipient to view their responses.',

    // Survey Responder
    surveyResponderErrorUnavailable: 'This survey is no longer available or the link is invalid.',
    surveyResponderErrorDuplicateEmail: 'This email has already been used to respond to this survey.',
    surveyResponderErrorSubmitFailed: 'Failed to submit your response. Please try again.',
    surveyResponderThankYou: 'Thank you!',
    surveyResponderResponseRecorded: 'Your response has been recorded. You can close this page.',
    surveyResponderLabelEmail: 'Your Email',
    surveyResponderPlaceholderEmail: 'your@email.com',
    surveyResponderLabelName: 'Your Name',
    surveyResponderPlaceholderName: 'Enter your name...',
    surveyResponderPlaceholderAnswer: 'Type your answer...',
    surveyResponderFallbackTitle: 'Survey',
    surveyResponderBtnSubmit: 'Submit Response',

    // Sales — Sidebar
    salesSidebarTitle: 'Sales',
    salesSidebarPipelineLabel: 'Pipeline',
    salesSidebarPipelineDesc: 'Kanban board',
    salesSidebarArchivedLabel: 'Archived',
    salesSidebarArchivedDesc: 'Archived leads',
    salesSidebarGeneratorLabel: 'AI Generator',
    salesSidebarGeneratorDesc: 'Discover new leads',
    salesSidebarSettingsLabel: 'Settings',
    salesSidebarSettingsDesc: 'Profile & examples',

    // Sales — Status labels
    salesStatusNew: 'New',
    salesStatusContacted: 'Contacted',
    salesStatusNegotiation: 'Negotiation',
    salesStatusWon: 'Won',
    salesStatusLost: 'Lost',
    salesStatusDisqualified: 'Disqualified',

    // Sales — Kanban board
    salesKanbanLeadsInPipeline: '{{count}} lead in pipeline',
    salesKanbanLeadsInPipelinePlural: '{{count}} leads in pipeline',
    salesKanbanErrorUpdateStatus: 'Failed to update status',
    salesKanbanMovedTo: 'Moved to {{status}}',
    salesKanbanNoLeads: 'No leads here',

    // Sales — Lead card
    salesLeadCardAiBadge: 'AI',
    salesLeadCardSalesLabel: 'Sales',
    salesLeadCardAiRatingTitle: 'AI Rating',
    salesLeadCardDragHandle: 'Drag to reorder',

    // Sales — Lead modal
    salesLeadModalTabOverview: 'Overview',
    salesLeadModalTabComments: 'Comments',
    salesLeadModalTabHistory: 'History',
    salesLeadModalBadgeAiDiscovered: 'AI Discovered',
    salesLeadModalBtnUnarchive: 'Unarchive',
    salesLeadModalBtnArchive: 'Archive',
    salesLeadModalLabelAiRating: 'AI Rating',
    salesLeadModalBtnRefreshAi: 'Refresh AI analysis',
    salesLeadModalLabelYourRating: 'Your Rating',
    salesLeadModalLabelBusinessInfo: 'Business Info',
    salesLeadModalLabelPhone: 'Phone',
    salesLeadModalLabelSuggestedPitch: 'Suggested Pitch',
    salesLeadModalLabelAiAnalysis: 'AI Analysis',
    salesLeadModalCreated: 'Created: ',
    salesLeadModalUpdated: 'Updated: ',
    salesLeadModalNoComments: 'No comments yet',
    salesLeadModalNoHistory: 'No history yet',
    salesLeadModalCommentPlaceholder: 'Type a comment... (@agent to ask AI)',
    salesLeadModalCommentHint: 'followed by a question to ask AI',
    salesLeadModalCommentHintPrefix: 'Type ',
    salesLeadModalAgentLabel: 'AI Agent',
    salesLeadModalFallbackUser: 'User',
    salesLeadModalFallbackSystem: 'System',
    salesLeadModalToastUnarchived: 'Lead unarchived',
    salesLeadModalToastArchived: 'Lead archived',
    salesLeadModalToastAiRefreshed: 'AI rating refreshed',
    salesLeadModalErrorRefreshAi: 'Failed to refresh AI rating',
    salesLeadModalErrorAgentFailed: 'Agent request failed',
    salesLeadModalErrorCommentFailed: 'Failed to send comment',
    salesLeadModalHistoryCreatedAs: 'Created as {{status}}',

    // Sales — Archived
    salesArchivedEmpty: 'No archived leads',
    salesArchivedEmptyDesc: 'Archived leads will appear here',
    salesArchivedCount: '{{count}} archived lead',
    salesArchivedCountPlural: '{{count}} archived leads',
    salesArchivedColCompany: 'Company',
    salesArchivedColStatus: 'Status',
    salesArchivedColAiRating: 'AI Rating',
    salesArchivedColSalesRating: 'Sales Rating',
    salesArchivedColCreated: 'Created',
    salesArchivedBtnUnarchive: 'Unarchive',

    // Sales — Company Profile
    salesProfileTitle: 'Company Profile',
    salesProfileDesc: 'Describe your company, ideal customer profile, and what makes a good lead. This context is used by the AI agent when qualifying leads.',
    salesProfilePlaceholder: 'e.g. We are a B2B SaaS company focused on enterprise compliance. Our ideal customer is...',
    salesProfileBtnSave: 'Save Profile',
    salesProfileToastSaved: 'Company profile updated',
    salesProfileErrorSave: 'Failed to update profile',

    // Sales — Lead Examples
    salesExamplesTitle: 'Lead Examples',
    salesExamplesDesc: 'Good and bad examples help the AI learn what makes a quality lead for your business.',
    salesExamplesBtnAdd: 'Add Example',
    salesExamplesTypeGood: 'Good',
    salesExamplesTypeBad: 'Bad',
    salesExamplesLabelUrl: 'Company URL',
    salesExamplesPlaceholderUrl: 'https://example.com',
    salesExamplesLabelExplanation: 'Explanation',
    salesExamplesPlaceholderExplanation: 'Why is this a good/bad lead example?',
    salesExamplesBtnCancel: 'Cancel',
    salesExamplesBtnSave: 'Save Example',
    salesExamplesEmpty: 'No examples yet',
    salesExamplesEmptyDesc: 'Add good and bad lead examples to improve AI qualification accuracy',
    salesExamplesAriaDelete: 'Delete example',
    salesExamplesToastAdded: 'Example added',
    salesExamplesErrorAdd: 'Failed to add example',
    salesExamplesToastDeleted: 'Example deleted',
    salesExamplesErrorDelete: 'Failed to delete example',

    // Sales — Lead Generator
    salesGeneratorTitle: 'AI Lead Generator',
    salesGeneratorSubtitle: 'Discover and qualify leads powered by AI analysis',
    salesGeneratorCardLocation: 'Target Location',
    salesGeneratorLabelCountry: 'Country',
    salesGeneratorPlaceholderCountry: 'Select country...',
    salesGeneratorLabelState: 'State',
    salesGeneratorPlaceholderState: 'All states',
    salesGeneratorLabelCity: 'City',
    salesGeneratorPlaceholderCity: 'e.g. Austin',
    salesGeneratorLabelDistance: 'Distance: {{value}} km',
    salesGeneratorCardRefinement: 'Search Refinement',
    salesGeneratorOptionalLabel: '(optional)',
    salesGeneratorLabelKeywords: 'Industry / Keywords',
    salesGeneratorPlaceholderKeywords: 'e.g. SaaS, manufacturing, healthcare',
    salesGeneratorHintKeywords: 'Leave empty to let AI decide based on your company profile',
    salesGeneratorLabelMaxResults: 'Max Results',
    salesGeneratorBtnGenerate: 'Generate Leads',
    salesGeneratorInfoText: 'AI will use your company profile and lead examples from Settings to find and rate potential customers.',
    salesGeneratorInitMessage: 'Initializing AI Lead Generator...',
    salesGeneratorRunningTitle: 'Discovering leads...',
    salesGeneratorFoundCount: '{{count}} found',
    salesGeneratorBtnNewSearch: 'New Search',
    salesGeneratorEmptyTitle: 'No leads discovered',
    salesGeneratorEmptyDesc: 'Try broadening your search criteria.',
    salesGeneratorBtnTryAgain: 'Try Again',
    salesGeneratorBtnDeselectAll: 'Deselect All',
    salesGeneratorBtnSelectAll: 'Select All',
    salesGeneratorSelectedCount: '{{selected}} of {{total}} selected',
    salesGeneratorBtnDiscard: 'Discard',
    salesGeneratorBtnAddToPipeline: 'Add {{count}} to Pipeline',
    salesGeneratorErrorNoLeads: 'No leads selected',
    salesGeneratorToastLeadsAdded: '{{count}} leads added to pipeline!',
    salesGeneratorErrorAddFailed: 'Failed to add leads',
    salesGeneratorErrorGenerateFailed: 'Failed to generate leads',
    salesGeneratorStreamEndedEarly: 'Found {{count}} leads (stream ended early).',
    salesGeneratorNoResults: 'Search ended without results.',
    salesGeneratorFoundSummary: 'Found {{count}} leads.'
  },
  
  es: {
    // Header
    systemName: 'QUAZI',
    systemTagline: 'Supervisión Financiera con IA',
    logout: 'Salir',
    
    // Confirmation Dialogs
    confirmLogout: 'Confirmar Cierre de Sesión',
    confirmLogoutMessage: '¿Estás seguro de que quieres cerrar sesión? Se perderá cualquier trabajo no guardado.',
    confirmDelete: 'Eliminar Análisis',
    confirmDeleteMessage: '¿Estás seguro de que quieres eliminar permanentemente',
    delete: 'Eliminar',
    deleting: 'Eliminando...',
    cancel: 'Cancelar',
    deleteAnalysis: 'Eliminar este análisis',
    
    // Duplicate Detection
    duplicateDetected: 'Documento Duplicado',
    duplicateMessage: 'Este documento ya ha sido analizado',
    duplicateExplanation: 'Ya tienes un análisis para:',
    analyzedOn: 'Analizado el',
    duplicateChoiceHint: '¿Deseas ver el análisis existente o crear uno nuevo?',
    analyzeAgain: 'Analizar de Nuevo',
    viewExisting: 'Ver Existente',
    
    // Search
    searchAnalyses: 'Buscar análisis...',
    noResults: 'No se encontraron resultados',
    tryDifferentSearch: 'Intenta con otro término de búsqueda',
    found: 'encontrados',
    noAnalysesYet: 'Aún no hay análisis',
    uploadToStart: 'Sube documentos para comenzar tu primer análisis',
    
    // Upload Section
    uploadTitle: 'Análisis Forense Financiero',
    uploadSubtitle: 'Arrastra un reporte financiero PDF aquí o haz clic para seleccionar',
    uploadSubtitleMultiple: 'Arrastra PDFs financieros aquí o haz clic para seleccionar múltiples archivos',
    uploadButton: 'CARGAR DOCUMENTO',
    uploadLimit: 'Límite: 32MB por archivo | Solo PDF',
    uploadLimitMultiple: 'Límite: 32MB por archivo | Múltiples PDFs serán analizados como un informe combinado',
    uploadInstructions: 'Instrucciones de Carga',
    uploadInstructionsText: 'Solo se aceptan archivos PDF. Documentos admitidos: estados de resultados, balances generales, estados de ingresos, informes financieros y documentos de control de gestión.',
    uploadInstructionsTextMultiple: 'Solo se aceptan archivos PDF. Puedes cargar múltiples archivos (por ejemplo, balance general e informe de resultados separados) que serán analizados juntos como un informe integral.',
    qualityDisclaimer: '💡 Consejo de Calidad',
    qualityDisclaimerText: 'Mejor calidad del documento = resultados más rápidos y precisos. Los PDFs de alta resolución con texto y números claros producirán el mejor análisis.',
    filesSelected: '{{count}} archivo(s) seleccionado(s)',
    removeFile: 'Eliminar archivo',
    addMoreFiles: 'Agregar Más Archivos',
    analyzeFiles: 'Analizar Documentos',
    errorNoFiles: 'Por favor selecciona al menos un archivo para analizar',
    
    // Analysis Guidance
    analysisGuidanceLabel: 'Guía de Análisis',
    optional: 'opcional',
    analysisGuidancePlaceholder: 'ej., "Enfocarse en tendencias de rentabilidad de la división de seguridad" o "Comparar rendimiento T3 vs T4" o "Prestar especial atención a gastos operativos y explicar anomalías"...',
    analysisGuidanceHint: 'Añade instrucciones específicas para personalizar el análisis. Si se deja vacío, QUAZI realizará un análisis estándar integral.',
    
    // Errors
    errorPdfOnly: 'Solo se aceptan archivos PDF',
    errorFileSize: 'El archivo excede el límite de 32MB',
    errorAnalysis: 'Error en el Análisis',
    errorMessage: 'Error al analizar el documento. Verifica que el backend esté corriendo.',
    retryButton: 'Intentar de Nuevo',
    
    // Results
    documentAnalyzed: 'Documento analizado:',
    newAnalysis: 'Nuevo Análisis',
    
    // Sections
    executiveSummary: 'RESUMEN EJECUTIVO',
    positiveIndicators: 'INDICADORES POSITIVOS',
    criticalAlerts: 'ALERTAS CRÍTICAS',
    questionsForManagement: 'PREGUNTAS PARA LA GERENCIA',
    requiredActions: 'ACCIONES REQUERIDAS',
    anomaliesDetected: 'ANOMALÍAS DETECTADAS',
    
    // Priority levels
    highPriority: '⚡ PRIORIDAD ALTA',
    mediumPriority: '◆ PRIORIDAD MEDIA',
    lowPriority: '○ PRIORIDAD BAJA',
    
    // Severity
    critical: 'CRÍTICO',
    high: 'ALTO',
    medium: 'MEDIO',
    low: 'BAJO',
    
    // Loading
    analyzing: 'ANALIZANDO DOCUMENTO',
    analyzingSubtitle: 'QUAZI AI está realizando análisis forense profundo',
    loadingParsing: 'Analizando documentos financieros...',
    loadingExtracting: 'Extrayendo métricas y patrones clave...',
    loadingAnalyzing: 'Ejecutando algoritmos de análisis forense...',
    loadingValidating: 'Validando integridad de datos...',
    loadingGenerating: 'Generando perspectivas ejecutivas...',
    loadingFinalizing: 'Finalizando informe completo...',
    
    // Footer
    developedFor: 'Desarrollado para',
    poweredBy: 'Powered by QUAZI AI Engine',
    
    // Languages
    spanish: 'Español',
    english: 'English',
    portuguese: 'Português',
    
    // Time
    days: 'días',
    
    // Export
    exportReport: 'Exportar Informe',
    exportingReport: 'Generando Informe...',
    exportPDF: 'Exportar PDF',
    exportingPDF: 'Generando PDF...',
    exportCSV: 'Exportar Datos (CSV)',
    exporting: 'Exportando...',
    exportProfessionalPDF: 'Informe Ejecutivo',
    exportingProfessionalPDF: 'Generando Informe Ejecutivo...',
    exportError: 'Error al exportar PDF. Por favor intenta de nuevo.',
    
    // Authentication
    tagline: 'Supervisión Financiera con IA',
    emailPlaceholder: 'Correo Electrónico',
    passwordPlaceholder: 'Contraseña',
    signIn: 'INICIAR SESIÓN',
    authenticating: 'AUTENTICANDO...',
    forgotPassword: '¿Olvidaste tu Contraseña?',
    loginFailed: 'Error de inicio de sesión. Inténtalo de nuevo.',
    
    // Password Reset
    resetPassword: 'Restablecer Contraseña',
    enterEmailForReset: 'Ingresa tu correo electrónico para recibir un código de restablecimiento',
    sendResetCode: 'ENVIAR CÓDIGO',
    sending: 'ENVIANDO...',
    backToLogin: 'Volver al Inicio de Sesión',
    resetCodeSent: 'Código de restablecimiento enviado a tu correo',
    failedToSendCode: 'Error al enviar el código',
    
    // Password Reset - Step 2
    enterCodeAndNewPassword: 'Ingresa el código de verificación de tu correo y tu nueva contraseña',
    verificationCode: 'Código de Verificación',
    newPassword: 'Nueva Contraseña',
    confirmNewPassword: 'Confirmar Nueva Contraseña',
    passwordRequirements: 'Mínimo 8 caracteres, incluye mayúscula, minúscula, número y carácter especial',
    resetting: 'RESTABLECIENDO...',
    backToEmail: 'Volver al Correo',
    passwordResetSuccess: '¡Contraseña restablecida exitosamente! Redirigiendo...',
    failedToResetPassword: 'Error al restablecer la contraseña',
    passwordsDoNotMatch: 'Las contraseñas no coinciden',
    passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
    rememberMe: 'Recuérdame',
    passwordStrength: 'Fortaleza de contraseña:',
    weak: 'Débil',
    mediumStrength: 'Media',
    strong: 'Fuerte',
    
    // New Password (Temp Password Flow)
    setNewPassword: 'Establecer Nueva Contraseña',
    tempPasswordMessage: 'Estás iniciando sesión con una contraseña temporal. Establece una nueva contraseña para continuar.',
    setPassword: 'ESTABLECER CONTRASEÑA',
    settingPassword: 'ESTABLECIENDO...',
    failedToSetPassword: 'Error al establecer la nueva contraseña',
    
    // Sidebar & History
    history: 'Historial',
    recentAnalyses: 'Análisis Recientes',
    openHistory: 'Abrir historial',
    collapseHistory: 'Contraer barra lateral',
    analysisCount: 'análisis',
    analysesCount: 'análisis',
    completed: 'Completado',
    
    // Chat
    chatWithQuazi: 'Chat con QUAZI',
    askQuestion: 'Haz una pregunta...',
    chatEmptyState: 'Pregúntame cualquier cosa sobre este análisis',
    closeChat: 'Cerrar chat',
    
    // Quick Actions
    lastAnalysis: 'Último Análisis',
    continueAction: 'Continuar',
    noChange: 'Sin cambios',
    
    // KPI Tooltips
    tooltipTotalAnalyses: 'Número total de documentos que has analizado',
    tooltipThisWeek: 'Análisis completados en los últimos 7 días vs semana anterior',
    tooltipAvgDuration: 'Tiempo promedio para completar un análisis',
    tooltipChatMessages: 'Total de interacciones de chat IA en todos los análisis',
    
    // Time Ago
    justNow: 'Ahora mismo',
    minutesAgo: 'hace {{count}}m',
    hoursAgo: 'hace {{count}}h',
    daysAgo: 'hace {{count}}d',
    
    // Toast Messages
    chatExported: 'Conversación de chat exportada',
    exportComplete: 'Exportación Completa',
    
    // Comparison Feature
    compareAnalyses: 'Comparar',
    compareAnalysesTitle: 'Comparar Análisis',
    compareDescription: 'Selecciona dos análisis para comparar lado a lado',
    selectTwoAnalyses: 'Selecciona dos análisis para comparar',
    selectOneMore: 'Selecciona un análisis más',
    readyToCompare: '¡Listo para comparar!',
    comparisonTitle: 'Comparación de Análisis',
    compGrossMargin: 'Margen Bruto',
    compOperatingMargin: 'Margen Operativo',
    compNetMargin: 'Margen Neto',
    compEbitdaMargin: 'Margen EBITDA',
    compCurrentRatio: 'Ratio Corriente',
    compDsoDays: 'DSO (Días)',
    compPersonnelCosts: 'Costos de Personal',
    compTotalRevenue: 'Ingresos Totales',
    marginComparison: 'Comparación de Márgenes',
    executiveSummaries: 'Resúmenes Ejecutivos',
    criticalAlertsComparison: 'Alertas Críticas',
    positiveIndicatorsComparison: 'Indicadores Positivos',
    compFlags: 'alertas',
    compDifference: 'diferencia',
    noAlerts: 'Sin alertas',
    noIndicators: 'Sin indicadores',
    compMore: 'más',
    keyPerformanceIndicators: 'Indicadores Clave de Rendimiento',
    
    // Analysis Completeness Feature
    analysisCompleteness: 'Completitud del Análisis',
    completenessExcellent: 'Excelente',
    completenessGood: 'Bueno',
    completenessModerate: 'Moderado',
    completenessNeedsWork: 'Necesita Mejoras',
    statementsFound: 'estados',
    metricsCalculated: 'métricas',
    suggestionsAvailable: 'sugerencias para mejorar',
    showLess: 'Mostrar menos',
    showDetails: 'Mostrar detalles',
    missingDocuments: 'Documentos Faltantes',
    incompleteMetrics: 'Métricas Que No Pudieron Calcularse',
    improvementSuggestions: 'Formas de Mejorar Este Análisis',
    completeYourAnalysis: 'Completa Tu Análisis - Sube Más Documentos',
    excellentAnalysis: '¡Excelente! Este análisis es completo con todas las métricas clave calculadas.',
    extendAnalysis: 'Extender Análisis',
    addMoreDocuments: 'Agregar Más Documentos',
    extendingAnalysis: 'Extendiendo análisis con nuevos documentos...',
    analysisExtended: '¡Análisis extendido exitosamente!',
    extendAnalysisSubtitle: 'Sube documentos adicionales para mejorar y completar este análisis',
    extendingHint: 'Agregando documentos al análisis existente para resultados más completos',
    
    // Improve Analysis Modal
    improveAnalysis: 'Mejorar Análisis',
    improveAnalysisSubtitle: 'Sube documentos adicionales para obtener un análisis más completo',
    recommendedUploads: 'Documentos Recomendados para Subir',
    uploadNewDocuments: 'Subir Nuevos Documentos',
    additionalGuidance: 'Orientación Adicional',
    guidancePlaceholder: 'ej., Enfócate en análisis de liquidez, compara con resultados del año pasado...',
    extending: 'Extendiendo Análisis...',
    complete: 'Completo',
    analyzingDocuments: 'Analizando tus documentos...',
    claudeAssessing: 'Claude está evaluando lo que falta',
    tryAgain: 'Intentar de Nuevo',
    analysisComprehensive: '¡Tu análisis es bastante completo!',
    addMoreDocsForInsights: 'Agrega más documentos para obtener insights más profundos.',
    statements: 'Estados',
    metrics: 'Métricas',
    clickToUploadPdf: 'Haz clic para subir archivos PDF',
    filesSelectedCount: '{{count}} archivo(s) seleccionado(s)',
    dropFinancialDocs: 'Arrastra tus documentos financieros aquí',
    incomeStatement: 'Estado de Resultados',
    balanceSheet: 'Balance General',
    cashFlowStatement: 'Estado de Flujo de Efectivo',
    notesDisclosures: 'Notas y Revelaciones',
    
    // Comparison View
    analysisComparison: 'Comparación de Análisis',
    company1: 'Empresa 1',
    company2: 'Empresa 2',
    noAlertsFound: 'Sin alertas',
    noIndicatorsFound: 'Sin indicadores',
    moreItems: '+{{count}} más',
    loading: 'Cargando...',
    searchCompanyYearDoc: 'Buscar por empresa, año o documento...',
    selected: 'Seleccionado:',
    noAnalysesFound: 'No se encontraron análisis',
    uploadDocsToCreate: 'Sube documentos para crear análisis',
    unknownCompany: 'Empresa Desconocida',

    // Dashboard Welcome
    welcomeToQuazi: 'Bienvenido a QUAZI',
    systemDescription: 'Sistema de Análisis Forense Financiero',
    goodMorning: 'Buenos días',
    goodAfternoon: 'Buenas tardes',
    goodEvening: 'Buenas noches',
    youveAnalyzed: 'Has analizado',
    documents: 'documentos',
    document: 'documento',
    
    // Dashboard Stats
    totalAnalyses: 'Análisis Totales',
    thisWeek: 'Esta Semana',
    avgDuration: 'Duración Media',
    chatMessages: 'Mensajes de Chat',
    allTime: 'Todo el tiempo',
    lastSevenDays: 'Últimos 7 días',
    perAnalysis: 'Por análisis',
    aiInsights: 'Insights IA',
    
    // Dashboard Charts
    analysisActivity: 'Actividad de Análisis',
    lastFourteenDays: 'Últimos 14 días',
    companiesAnalyzed: 'Empresas Analizadas',
    unique: 'únicas',
    recentActivity: 'Actividad Reciente',
    latestAnalyses: 'Últimos análisis',
    industriesAnalyzed: 'Industrias Analizadas',
    activityChartEmpty: 'El gráfico de actividad aparecerá después de tu primer análisis',
    companyBreakdownEmpty: 'El desglose de empresas aparecerá aquí',
    recentActivityEmpty: 'Tus análisis recientes aparecerán aquí',
    
    // Onboarding
    getStartedWithQuazi: 'Comienza con QUAZI',
    getStartedSubtitle: 'Sigue estos sencillos pasos para desbloquear poderosos insights financieros impulsados por IA',
    stepOneTitle: 'Carga Documentos Financieros',
    stepOneDesc: 'Arrastra archivos PDF como estados de resultados, balances o informes anuales',
    stepTwoTitle: 'Análisis IA',
    stepTwoDesc: 'La IA de QUAZI extrae métricas clave, detecta anomalías y genera insights',
    stepThreeTitle: 'Chat y Exportación',
    stepThreeDesc: 'Haz preguntas sobre tus datos y exporta informes PDF profesionales',
    uploadFirstDocument: 'Carga Tu Primer Documento',
    
    // Financial Charts
    totalRevenue: 'Ingresos Totales',
    revenuePerEmployee: 'Ingresos por Empleado',
    dsodays: 'DSO',
    netMargin: 'Margen Neto',
    revenueBreakdown: 'Desglose de Ingresos',
    byServiceLine: 'Por Línea de Servicio',
    byGeography: 'Por Geografía',
    costAllocation: 'Distribución de Costos',
    costVsBenchmark: 'Costos vs Benchmark',
    profitabilityMetrics: 'Métricas de Rentabilidad',
    charts: 'Gráficos',
    viewCharts: 'Ver Gráficos Financieros',
    financialVisualizations: 'Visualizaciones Financieras',
    costStructure: 'Estructura de Costos',
    trendsOverTime: 'Tendencias en el Tiempo',
    noTrendsAvailable: 'No hay datos de series temporales disponibles en el documento',
    additionalKPIs: 'KPIs Adicionales',
    service: 'Servicio',
    revenue: 'Ingresos',
    location: 'Ubicación',
    trend: 'Tendencia',
    industryBenchmark: 'Referencia de la Industria',
    benchmark: 'Referencia',
    metric: 'Métrica',
    aiEstimate: 'Estimación IA',
    documentData: 'Datos del Documento',
    benchmarkSource: 'Fuente de Referencia',
    value: 'Valor',
    unit: 'Unidad',
    
    // Modules
    moduleFinancialForensics: 'Análisis Forense Financiero',
    enabledModules: 'Módulos Habilitados',
    noModulesEnabled: 'Aún no hay módulos habilitados. Contacta a soporte.',

    // AML Sidebar
    amlSidebarTitle: 'Cumplimiento AML',
    amlNavCustomers: 'Clientes',
    amlNavCustomersDesc: 'KYC y registros de clientes',
    amlNavTransactions: 'Transacciones',
    amlNavTransactionsDesc: 'Historial de transacciones',
    amlNavTransactionReport: 'Informe de Transacciones',
    amlNavTransactionReportDesc: 'Informes y exportación',
    amlNavTransactionPivotReport: 'Informe Pivote de Transacciones',
    amlNavTransactionPivotReportDesc: 'Análisis agregado',
    amlNavSanctions: 'Cribado OFAC',
    amlNavSanctionsDesc: 'Cotejo con listas de sanciones',
    amlNavClients: 'Clientes AML',
    amlNavClientsDesc: 'Gestión de clientes AML',

    // AML cribado de sanciones OFAC
    amlColScreening: 'Cribado',
    amlSanctionsTitle: 'Cribado de Sanciones OFAC',
    amlSanctionsSubtitle: 'Coteje sus clientes con las listas de sanciones de OFAC',
    amlSanctionsListSdn: 'Lista SDN',
    amlSanctionsListNonSdn: 'Lista No-SDN (Consolidada)',
    amlSanctionsListShort_SDN: 'SDN',
    amlSanctionsListShort_NON_SDN: 'No-SDN',
    amlSanctionsListState_never_run: 'Nunca descargada',
    amlSanctionsListState_running: 'Descargando…',
    amlSanctionsListState_success: 'Actualizada',
    amlSanctionsListState_failed: 'Falló la última descarga',
    amlSanctionsListPublished: 'Publicada',
    amlSanctionsListEntries: 'Entradas',
    amlSanctionsListNames: 'Nombres',
    amlSanctionsListUpdated: 'Última actualización',
    amlSanctionsBtnRefresh: 'Actualizar',
    amlSanctionsBtnRefreshAll: 'Actualizar Listas',
    amlSanctionsBtnRunScreening: 'Ejecutar Cribado',
    amlSanctionsNoListsWarning: 'Descargue las listas de OFAC antes de ejecutar un cribado.',
    amlScreeningHistoryTitle: 'Historial de Cribados',
    amlScreeningNoRuns: 'Aún no se ha ejecutado ningún cribado',
    amlScreeningColDate: 'Fecha',
    amlScreeningColStatus: 'Estado',
    amlScreeningColScreened: 'Analizados',
    amlScreeningColCleared: 'Sin Coincidencia',
    amlScreeningColPotentialMatches: 'Coincidencias Potenciales',
    amlScreeningColThreshold: 'Umbral',
    amlScreeningColTriggeredBy: 'Iniciado Por',
    amlScreeningRunStatus_running: 'En curso',
    amlScreeningRunStatus_success: 'Completado',
    amlScreeningRunStatus_failed: 'Fallido',
    amlScreeningDetailTitle: 'Resultados del Cribado',
    amlScreeningBtnBack: 'Volver al cribado',
    amlScreeningThresholdLabel: 'Umbral',
    amlScreeningBtnDownloadPdf: 'Informe PDF',
    amlScreeningBtnDownloadExcel: 'Informe Excel',
    amlScreeningListVersions: 'Versiones de Lista Usadas',
    amlScreeningSearchPlaceholder: 'Buscar clientes…',
    amlScreeningFilterAllResults: 'Todos los resultados',
    amlScreeningFilterAllReviews: 'Todos los estados de revisión',
    amlScreeningColCustomer: 'Cliente',
    amlScreeningColAddress: 'Dirección',
    amlScreeningColResult: 'Resultado',
    amlScreeningColScore: 'Puntaje',
    amlScreeningColMatchedName: 'Nombre OFAC Coincidente',
    amlScreeningColList: 'Lista',
    amlScreeningColReview: 'Revisión',
    amlScreeningResultCleared: 'Sin Coincidencia',
    amlScreeningResultPotentialMatch: 'Coincidencia Potencial',
    amlScreeningResultNotScreened: 'Sin analizar',
    amlScreeningReviewPending: 'Pendiente',
    amlScreeningReviewFalsePositive: 'Falso positivo',
    amlScreeningReviewConfirmedMatch: 'Coincidencia confirmada',
    amlScreeningNoResults: 'Ningún resultado coincide con estos filtros',
    amlScreeningAka: 'Alias',
    amlScreeningAkaOf: 'Alias de',
    amlScreeningReviewPanelTitle: 'Revisar Coincidencia Potencial',
    amlScreeningBestScore: 'Mejor puntaje',
    amlScreeningCandidatesTitle: 'Coincidencias Candidatas',
    amlScreeningMatchEntityType: 'Tipo de Entidad',
    amlScreeningMatchPrograms: 'Programas de Sanciones',
    amlScreeningMatchAddresses: 'Direcciones OFAC',
    amlScreeningMatchOfacUid: 'UID de OFAC',
    amlScreeningViewOnOfac: 'Ver en OFAC',
    amlScreeningReviewNotesLabel: 'Notas de revisión',
    amlScreeningReviewNotesPlaceholder: 'Explique la decisión para la pista de auditoría…',
    amlScreeningBtnMarkFalsePositive: 'Marcar como Falso Positivo',
    amlScreeningBtnConfirmMatch: 'Confirmar Coincidencia',
    amlScreeningToastReportDownloaded: 'Informe descargado',
    amlScreeningToastReportFailed: 'No se pudo descargar el informe',
    amlScreeningToastReviewSaved: 'Revisión guardada',
    amlScreeningToastReviewFailed: 'No se pudo guardar la revisión',

    // AML Columns
    amlColDate: 'Fecha',
    amlColCustomerLegalName: 'Nombre Legal del Cliente',
    amlColLocation: 'Ubicación',
    amlColInbound: 'Entrante',
    amlColOutbound: 'Saliente',
    amlColAmount: 'Monto',
    amlColFeeInbound: 'Tarifa Entrante',
    amlColFeeOutbound: 'Tarifa Saliente',
    amlColAmountFee: 'Monto de Tarifa',
    amlColInvoiceNumber: 'Número de Factura',
    amlColInvoiceDate: 'Fecha de Factura',
    amlColInvoiceAmount: 'Monto de Factura',
    amlColStops: 'Paradas',
    amlColPcs: 'PCS',
    amlColNotes: 'Notas',
    amlColRegistrationNumber: 'Número de Registro',
    amlColInitialDate: 'Fecha Inicial',
    amlColCode: 'Código',
    amlColStatus: 'Estado',
    amlColName: 'Nombre',
    amlColAddress: 'Dirección',
    amlColPhone: 'Teléfono',
    amlColLogo: 'Logo',
    amlColEmail: 'Correo',
    amlColClient: 'Cliente',
    amlColType: 'Tipo',
    amlColActions: 'Acciones',

    // AML Table
    amlTableTotal: 'Total',
    amlTableNoTransactionsFound: 'No se encontraron transacciones',
    amlTableNoCustomersFound: 'No se encontraron clientes',
    amlTableNoClientsFound: 'No se encontraron clientes AML',
    amlTableNoUsersFound: 'No se encontraron usuarios',

    // AML Filters
    amlFilterTitle: 'Filtros',
    amlFilterApplyFiltersToLoadReport: 'Aplica filtros para cargar el informe',
    amlFilterApplyFiltersToLoadReportDesc: 'Usa los filtros de arriba para cargar el informe de transacciones.',
    amlFilterNoTransactionsFound: 'No se encontraron transacciones',
    amlFilterNoTransactionsFoundDesc: 'Intenta ajustar el rango de fechas u otros filtros.',
    amlFilterPanelTitle: 'Filtro',
    amlFilterCustomerLegalName: 'Nombre Legal del Cliente',
    amlFilterLocation: 'Ubicación',
    amlFilterDateStart: 'Fecha (Inicio)',
    amlFilterDateEnd: 'Fecha (Fin)',
    amlFilterInvoiceDateStart: 'Fecha de Factura (Inicio)',
    amlFilterInvoiceDateEnd: 'Fecha de Factura (Fin)',
    amlFilterSearchCustomers: 'Buscar clientes...',
    amlFilterSearchLocations: 'Buscar ubicaciones...',
    amlFilterNoCustomersFound: 'No se encontraron clientes',
    amlFilterNoLocationsFound: 'No se encontraron ubicaciones',
    amlFilterReset: 'Restablecer',
    amlFilterApply: 'Aplicar',
    amlFilterClear: 'Limpiar',

    // AML Placeholders
    amlPlaceholderSearchByCustomerLocation: 'Buscar por cliente, ubicación...',
    amlPlaceholderAllCustomers: 'Todos los clientes',
    amlPlaceholderAllLocations: 'Todas las ubicaciones',
    amlPlaceholderDateFrom: 'Fecha desde',
    amlPlaceholderDateTo: 'Fecha hasta',
    amlPlaceholderSelectDateStart: 'Seleccionar fecha (Inicio)',
    amlPlaceholderSelectDateEnd: 'Seleccionar fecha (Fin)',
    amlPlaceholderSelectInvoiceDateStart: 'Seleccionar fecha de factura (Inicio)',
    amlPlaceholderSelectInvoiceDateEnd: 'Seleccionar fecha de factura (Fin)',
    amlPlaceholderStartDate: 'Fecha de inicio',
    amlPlaceholderEndDate: 'Fecha de fin',

    // AML Buttons
    amlBtnPdf: 'PDF',
    amlBtnExcel: 'Excel',
    amlBtnImport: 'Importar',
    amlBtnAddTransaction: 'Agregar Transacción',
    amlBtnAddCustomer: 'Agregar Cliente',
    amlBtnAddClient: 'Agregar Cliente AML',
    amlBtnAddUser: 'Agregar Usuario',
    amlBtnSaveChanges: 'Guardar Cambios',
    amlBtnCreateCustomer: 'Crear Cliente',
    amlBtnCreateClient: 'Crear Cliente AML',
    amlBtnCreateUser: 'Crear Usuario',
    amlBtnUpdateTransaction: 'Actualizar Transacción',
    amlBtnCreateTransaction: 'Crear Transacción',
    amlBtnCancel: 'Cancelar',
    amlBtnApply: 'Aplicar',
    amlBtnReset: 'Restablecer',
    amlBtnDelete: 'Eliminar',
    amlBtnExportReport: 'Exportar Informe',

    // AML Pivot
    amlPivotTableNoPivotData: 'Sin datos de pivote',
    amlPivotTableNoPivotDataDesc: 'Intenta ajustar el rango de fechas o la agrupación.',
    amlPivotColTransactionPeriod: 'Período de Transacción',
    amlPivotColInboundSum: 'Suma Entrante',
    amlPivotColInboundCount: 'Cantidad Entrante',
    amlPivotColOutboundSum: 'Suma Saliente',
    amlPivotColOutboundCount: 'Cantidad Saliente',
    amlPivotColInvoiceSum: 'Suma de Facturas',
    amlPivotColInvoiceCount: 'Cantidad de Facturas',
    amlPivotColStopsSum: 'Suma de Paradas',
    amlPivotColPcsCount: 'Cantidad PCS',
    amlPivotRadioClient: 'Cliente',
    amlPivotRadioMonth: 'Mes',

    // AML Import
    amlImportModalTitleUpload: 'Importar Transacciones',
    amlImportModalTitleReview: 'Revisar Coincidencia de Clientes',
    amlImportModalTitleResult: 'Resultado de Importación',
    amlImportClickToSelectFile: 'Haz clic para seleccionar archivo .xlsx',
    amlImportSupportedFormat: 'Formato compatible: .xlsx',
    amlImportDownloadSampleTemplate: 'Descargar plantilla de ejemplo',
    amlImportParsingFile: 'Analizando archivo y coincidiendo clientes...',
    amlImportAiMatchingEnabled: 'Coincidencia IA habilitada para nombres no reconocidos',
    amlImportColCustomer: 'Cliente',
    amlImportColLocation: 'Ubicación',
    amlImportColStatus: 'Estado',
    amlImportColAmlCustomer: 'Cliente AML',
    amlImportColAmlLocation: 'Ubicación AML',
    amlImportStatTotalRows: 'Total de Filas',
    amlImportStatClients: 'Entradas',
    amlImportStatMatched: 'Coincidentes',
    amlImportStatAiSuggested: 'Sugeridos por IA',
    amlImportStatUnresolved: 'Sin resolver',
    amlImportStatSkipped: 'Omitidos',
    amlImportFilterEmpty: 'Ninguna entrada coincide con este filtro.',
    amlImportParseWarnings: 'Advertencias de Análisis',
    amlImportBadgeSkipped: 'Omitido',
    amlImportBadgeOfacBlocked: 'Bloqueado por OFAC',
    amlImportOfacSkippedTitle: 'Coincidencias OFAC omitidas',
    amlImportOfacSkippedBody: 'Se omitieron {{count}} cliente(s) marcados como coincidencia potencial OFAC y no se importarán.',
    amlImportOfacUnscreenedTitle: 'Clientes sin cribado',
    amlImportOfacUnscreenedBody: '{{count}} cliente(s) asignados aún no se han comprobado contra las listas OFAC. Se importarán igualmente.',
    amlImportBadgeMatched: 'Coincidente',
    amlImportBadgeResolved: 'Resuelto',
    amlImportBadgeUnmatched: 'Sin coincidencia',
    amlImportBadgeAi: 'IA',
    amlImportBadgeConfidenceHigh: 'Alta',
    amlImportBadgeConfidenceMedium: 'Media',
    amlImportBadgeConfidenceLow: 'Baja',
    amlImportRowsNotImported: 'Las filas no serán importadas',
    amlImportResolveAllUnmatched: 'Resuelve todos los clientes y ubicaciones, u omite entradas antes de importar.',
    amlImportErrors: 'Errores',
    amlImportResultSuccessTitle: 'Importación finalizada',
    amlImportResultPartialTitle: 'Importación finalizada — algunas filas se omitieron',
    amlImportResultFailedTitle: 'No se importó ninguna transacción',
    amlImportResultSuccessSummary: 'Se añadieron {{count}} transacción(es) nueva(s).',
    amlImportResultPartialSummary:
      'Se añadieron {{imported}} transacción(es). {{issues}} fila(s) del archivo no se importaron — ver la lista abajo.',
    amlImportResultFailedSummary:
      'No se pudieron importar {{issues}} fila(s). Corrige la hoja de cálculo e inténtalo de nuevo.',
    amlImportResultRowProblemsHeading: 'Filas no importadas',
    amlImportResultRowProblemsHint: 'Cada mensaje incluye el número de fila de tu archivo .xlsx.',
    amlImportParseWarningsHint: 'Cada mensaje incluye el número de fila de tu archivo .xlsx.',
    amlImportToastImportComplete: 'Se importaron {{imported}} transacción(es).',
    amlImportToastImportDuplicates: 'Se importaron {{count}} duplicado(s) con una nota.',
    amlImportToastImportPartial:
      'Se importaron {{imported}}; {{issues}} fila(s) con problemas. Revisa el resumen en esta ventana.',
    amlImportResultAllDuplicatesTitle: 'Duplicados importados',
    amlImportResultAllDuplicatesSummary:
      'Se añadieron {{count}} transacción(es). Cada una se marcó como duplicado en Notas porque ya existe (mismo cliente, ubicación, fecha e importes).',
    amlImportToastAllDupes: 'Se importaron {{count}} duplicado(s) con una nota.',
    amlImportSelectCustomer: 'Seleccionar cliente...',
    amlImportSelectLocation: 'Seleccionar ubicación...',
    amlImportTooltipRestore: 'Restaurar',
    amlImportTooltipRejectManual: 'Rechazar — elegir manualmente',
    amlImportTooltipUndoAiMatch: 'Deshacer coincidencia IA',
    amlImportTooltipSkipClient: 'Omitir cliente',
    amlImportTooltipAcceptAiSuggestion: 'Aceptar sugerencia IA',
    amlImportTooltipCreateNewCustomer: 'Crear nuevo cliente',
    amlImportBtnUploadParse: 'Cargar y Analizar',
    amlImportBtnBack: 'Volver',
    amlImportBtnSaveForLater: 'Guardar para después',
    amlImportBtnSubmitImport: 'Enviar Importación',
    amlImportBtnClose: 'Cerrar',
    amlImportToastDraftSaved: 'Importación guardada. Abre Importar de nuevo para continuar.',
    amlImportErrorDraftSaveFailed: 'No se pudo guardar esta importación. Inténtalo de nuevo.',
    amlImportSavedBadge: 'Guardado',
    amlImportDraftBannerTitle: 'Importación guardada',
    amlImportDraftBannerBody: '{{fileName}} — guardada {{date}}',
    amlImportDraftBannerBodyNoFile: 'Guardada {{date}}',
    amlImportBtnResumeDraft: 'Reanudar',
    amlImportBtnDiscardDraft: 'Descartar',
    amlImportToastDraftDiscarded: 'Importación guardada descartada',
    amlImportResultTransactionsImported: '{{count}} transacción(es) importada(s)',
    amlImportResultDuplicatesSkipped: '{{count}} duplicado(s) importado(s) con una nota',
    amlImportResultRowsSkippedUnmatched: '{{count}} fila(s) omitida(s) (sin coincidencia)',
    amlImportErrorPleaseSelectXlsx: 'Por favor selecciona un archivo .xlsx',
    amlImportErrorFailedToParseFile: 'Error al analizar el archivo. Verifica el formato.',
    amlImportErrorImportFailed: 'Error en la importación. Inténtalo de nuevo.',
    amlImportCreateCustomerExistingHint:
      'Este nombre legal ya existe. No se requieren código ni fecha inicial — solo se añadirá la ubicación del archivo a ese cliente.',
    amlImportModalTitleAddLocation: 'Añadir ubicación',
    amlImportBtnAddLocation: 'Añadir ubicación',
    amlImportToastCustomerCreated: 'Cliente "{{name}}" creado',
    amlImportToastLocationAdded: 'Ubicación añadida a "{{name}}"',
    amlImportErrorCreateCustomerFailed: 'No se pudo crear el cliente',

    // AML Create Customer Modal
    amlCreateCustomerModalTitle: 'Crear Cliente',
    amlCreateCustomerLabelInitialDate: 'Fecha Inicial',
    amlCreateCustomerLabelLocationFromFile: 'Ubicación (del archivo)',
    amlCreateCustomerErrorNameRequired: 'El nombre es obligatorio',
    amlCreateCustomerErrorValidCodeRequired: 'Se requiere un código válido',
    amlCreateCustomerErrorInitialDateRequired: 'La fecha inicial es obligatoria',

    // AML Transaction Form
    amlTransactionDetailTitle: 'Detalle de Transacción',
    amlTransactionEditTitle: 'Editar Transacción',
    amlTransactionAddTitle: 'Agregar Transacción',
    amlTransactionLabelCustomer: 'Cliente',
    amlTransactionLabelLocation: 'Ubicación',
    amlTransactionLabelTransactionDate: 'Fecha de Transacción',
    amlTransactionLabelAmounts: 'Montos',
    amlTransactionLabelFees: 'Tarifas',
    amlTransactionLabelInvoice: 'Factura',
    amlTransactionLabelAmountCollectedInOut: 'Monto Cobrado (Entrada/Salida)',
    amlTransactionLabelFeeCollectedInOut: 'Tarifa Cobrada (Entrada/Salida)',
    amlTransactionLabelSelectCustomer: 'Seleccionar cliente',
    amlTransactionLabelSelectLocation: 'Seleccionar ubicación',
    amlTransactionLabelLoading: 'Cargando...',
    amlTransactionLabelSelectDate: 'Seleccionar fecha',
    amlTransactionLabelCollected: 'Cobrado',
    amlTransactionLabelFeeCollected: 'Tarifa Cobrada',
    amlTransactionLabelInvoiceHash: 'Factura #',
    amlTransactionToastSaved: 'Guardado',
    amlTransactionToastUpdatedSuccessfully: 'Transacción actualizada exitosamente',
    amlTransactionToastCreatedSuccessfully: 'Transacción creada exitosamente',
    amlTransactionToastDeleted: 'Transacción eliminada',
    amlTransactionToastFailedToDelete: 'Error al eliminar la transacción',
    amlTransactionToastFailedToUpdate: 'Error al actualizar la transacción',
    amlTransactionToastFailedToCreate: 'Error al crear la transacción',
    amlTransactionOfacBlockedTitle: 'Coincidencia potencial OFAC',
    amlTransactionOfacBlockedBody: 'Este cliente está marcado en la lista de cribado OFAC. Cree una transacción solo después de que un revisor lo marque como falso positivo.',
    amlTransactionOfacUnscreenedTitle: 'Cliente sin cribado',
    amlTransactionOfacUnscreenedBody: 'Este cliente aún no se ha comprobado contra las listas OFAC. Puede crear la transacción, pero ejecute un cribado lo antes posible.',
    amlFilterRemoveName: 'Eliminar {{name}}',
    amlTransactionTooltipFullEdit: 'Edición completa',
    amlTransactionTooltipView: 'Ver',
    amlTransactionTooltipDelete: 'Eliminar',
    amlTransactionAriaLabelEdit: 'Editar transacción',
    amlTransactionAriaLabelView: 'Ver transacción',
    amlTransactionAriaLabelDelete: 'Eliminar transacción',
    amlTransactionDeleteTitle: 'Eliminar Transacción',
    amlTransactionDeleteMessage: '¿Quitar esta transacción de informes y listas? Se puede restaurar más adelante si hace falta.',
    amlTransactionDeleteConfirm: 'Eliminar',
    amlTransactionBulkDelete: 'Eliminar seleccionadas',
    amlTransactionBulkSelected: '{{count}} seleccionadas',
    amlTransactionBulkDeleteTitle: 'Eliminar Transacciones',
    amlTransactionBulkDeleteMessage: '¿Quitar {{count}} transacción(es) de informes y listas? Se pueden restaurar más adelante si hace falta.',
    amlTransactionToastBulkDeleted: '{{count}} transacción(es) eliminada(s)',
    amlTransactionAriaLabelSelect: 'Seleccionar transacción',
    amlTransactionAriaLabelSelectAll: 'Seleccionar todas en esta página',
    amlTransactionAriaLabelClearSelection: 'Quitar selección',

    // AML Customer Form
    amlCustomerFormTitleEdit: 'Editar Cliente',
    amlCustomerFormTitleCreate: 'Agregar Cliente',
    amlCustomerFieldCustomerLegalName: 'Nombre Legal del Cliente',
    amlCustomerFieldCode: 'Código',
    amlCustomerFieldRegistrationNumber: 'Número de Registro',
    amlCustomerFieldEinFein: 'EIN/FEIN',
    amlCustomerFieldInitialDate: 'Fecha Inicial',
    amlCustomerFieldDateCompanyEstablished: 'Fecha de Constitución de la Empresa',
    amlCustomerFieldCompanyLegalRepresentative: 'Representante Legal de la Empresa',
    amlCustomerFieldMainAddress: 'Dirección Principal',
    amlCustomerFieldServicesAndProduct: 'Servicios y/o Producto',
    amlCustomerFieldTypeOfBusinessActivity: 'Tipo de Actividad Comercial',
    amlCustomerFieldNotesAmlRev: 'Notas AML REV',
    amlCustomerFieldContact: 'Contacto',
    amlCustomerFieldEmail: 'Correo',
    amlCustomerFieldPhone: 'Teléfono',
    amlCustomerFieldLocationsDetail: 'Detalle de ubicaciones',
    amlCustomerFieldStatus: 'Estado',
    amlCustomerStatusActive: 'Activo',
    amlCustomerStatusInactive: 'Inactivo',
    amlCustomerNotApplicable: 'N/D',
    amlCustomerAriaLabelActions: 'Acciones',
    amlCustomerPlaceholderLegalName: 'Ingresa el nombre legal del cliente',
    amlCustomerPlaceholderCode: 'Ingresa el código',
    amlCustomerPlaceholderRegistrationNumber: 'Ingresa el número de registro',
    amlCustomerPlaceholderEinFein: 'Ingresa el EIN/FEIN',
    amlCustomerPlaceholderSelectInitialDate: 'Seleccionar fecha inicial',
    amlCustomerPlaceholderSelectDate: 'Seleccionar fecha...',
    amlCustomerPlaceholderCompanyLegalRep: 'Ingresa el representante legal',
    amlCustomerPlaceholderMainAddress: 'Ingresa la dirección principal',
    amlCustomerPlaceholderServicesProduct: 'Ingresa servicios y/o producto',
    amlCustomerPlaceholderTypeOfActivity: 'Ingresa el tipo de actividad comercial',
    amlCustomerPlaceholderNotesAmlRev: 'Ingresa notas AML REV',
    amlCustomerPlaceholderContact: 'Ingresa el contacto',
    amlCustomerPlaceholderEmailAddress: 'Ingresa la dirección de correo',
    amlCustomerPlaceholderEnterPhone: 'Ingresa el teléfono',
    amlCustomerPlaceholderLocationName: 'Ingresa el nombre de la ubicación',
    amlCustomerValidationNameRequired: 'El nombre legal del cliente es obligatorio',
    amlCustomerValidationDuplicateLegalName:
      'Ya existe un cliente con este nombre legal. Elige otro nombre.',
    amlCustomerValidationCodeRequired: 'El código es obligatorio',
    amlCustomerValidationCodeMustBeNumber: 'El código debe ser un número válido',
    amlCustomerValidationInitialDateRequired: 'La fecha inicial es obligatoria',
    amlCustomerValidationDateCannotBePast: 'La fecha no puede ser en el pasado',
    amlCustomerValidationDateCannotBeFuture: 'La fecha no puede ser en el futuro',
    amlCustomerValidationInvalidDate: 'Fecha inválida',
    amlCustomerValidationMustBeAtLeast3Chars: 'Debe tener al menos 3 caracteres',
    amlCustomerValidationOnlyLettersAllowed: 'Solo se permiten letras',
    amlCustomerValidationOnlyLettersNumbers: 'Solo se permiten letras y números',
    amlCustomerValidationValidEmail: 'Por favor ingresa un correo válido',
    amlCustomerValidationAllLocationNamesRequired: 'Se requieren todos los nombres de ubicación',
    amlCustomerToastUpdated: 'Cliente actualizado exitosamente',
    amlCustomerToastCreated: 'Cliente creado — screening OFAC iniciado',
    amlCustomerToastDeleted: '"{{name}}" eliminado exitosamente',
    amlCustomerToastFailedToDelete: 'Error al eliminar el cliente',
    amlCustomerPanelTitleDetail: 'Detalle del Cliente',
    amlCustomerPanelTitleKyc: 'Informe KYC',
    amlCustomerBtnAdd: 'Agregar',
    amlCustomerBtnSaveChanges: 'Guardar Cambios',
    amlCustomerBtnCreateCustomer: 'Crear Cliente',
    amlCustomerSearchPlaceholder: 'Buscar por nombre, código, número de registro...',
    amlCustomerDropdownDetail: 'Detalle',
    amlCustomerDropdownKycReport: 'Informe KYC',
    amlCustomerDropdownEdit: 'Editar',
    amlCustomerDropdownDelete: 'Eliminar',
    amlCustomerDeleteTitle: '¿Eliminar cliente?',
    amlCustomerDeleteMessage: '¿Ocultar a "{{name}}" y sus transacciones de listas e informes? El historial se conserva y se puede restaurar más adelante.',
    amlCustomerDeleteConfirm: 'Eliminar',
    amlCustomerDeleteCancel: 'Cancelar',

    // AML KYC
    amlKycTableHeaderDataReviewedByCO: 'Datos revisados por C.O',
    amlKycTableHeaderData: 'Datos',
    amlKycTableHeaderAdditionalNotes: 'Notas Adicionales',
    amlKycFieldInitialDate: 'Fecha Inicial',
    amlKycFieldCode: 'Código',
    amlKycFieldCustomerLegalName: 'Nombre Legal del Cliente',
    amlKycFieldEinFein: 'EIN / FEIN',
    amlKycFieldRegistrationNumber: 'Número de Registro',
    amlKycFieldDateCompanyEstablished: 'Fecha de Constitución',
    amlKycFieldCompanyLegalRepresentative: 'Representante Legal',
    amlKycFieldMainAddress: 'Dirección Principal',
    amlKycFieldLocationsDetail: 'Detalle de Ubicaciones',
    amlKycFieldServicesProduct: 'Servicios y/o Producto',
    amlKycFieldTypeOfBusinessActivity: 'Tipo de Actividad Comercial',
    amlKycFieldContact: 'Contacto',
    amlKycFieldEmail: 'Correo',
    amlKycFieldPhone: 'Teléfono',
    amlKycDateReviewed: 'Fecha de revisión:',
    amlKycBtnExportReport: 'Exportar Informe',
    amlCustomerDetailLocationsDetail: 'Detalle de ubicaciones',

    // AML Client Form
    amlClientFormTitleEdit: 'Editar Cliente AML',
    amlClientFormTitleCreate: 'Agregar Cliente AML',
    amlClientFieldClientLogo: 'Logo del Cliente',
    amlClientFieldClientName: 'Nombre del Cliente',
    amlClientFieldAddress: 'Dirección',
    amlClientFieldPhone: 'Teléfono',
    amlClientPlaceholderCompanyName: 'Nombre de la empresa',
    amlClientPlaceholderBusinessAddress: 'Dirección comercial',
    amlClientPlaceholderPhoneNumber: 'Número de teléfono',
    amlClientBtnChangeImage: 'Cambiar Imagen',
    amlClientBtnUploadImage: 'Subir Imagen',
    amlClientBtnCancelUpload: 'Cancelar Carga',
    amlClientBtnRemoveImage: 'Eliminar Imagen',
    amlClientImageFormats: 'JPG, PNG, GIF, WebP o SVG',
    amlClientBtnSaveChanges: 'Guardar Cambios',
    amlClientBtnCreateClient: 'Crear Cliente AML',
    amlClientToastUpdated: 'Cliente actualizado exitosamente',
    amlClientToastCreated: 'Cliente creado exitosamente',
    amlClientToastFailedToUpdate: 'Error al actualizar el cliente',
    amlClientToastFailedToCreate: 'Error al crear el cliente',
    amlClientToastLogoUploadFailed: 'Cliente guardado pero falló la carga del logo',
    amlClientToastCreatedLogoFailed: 'Cliente creado pero falló la carga del logo',
    amlClientSearchPlaceholder: 'Buscar clientes...',
    amlClientDeleteTitle: 'Eliminar Cliente AML',
    amlClientDeleteMessage: '¿Estás seguro de que quieres eliminar a "{{name}}"? Esta acción no se puede deshacer.',
    amlClientDeleteConfirm: 'Eliminar',
    amlClientShowingResults: 'Mostrando {{from}}–{{to}} de {{total}}',

    // AML User Management
    amlUserListSearchPlaceholder: 'Buscar por nombre, correo, cliente...',
    amlUserRoleSuperAdmin: 'Super Administrador',
    amlUserRoleClientAdmin: 'Administrador de Cliente',
    amlUserRoleOperator: 'Operador',
    amlUserRoleReviewer: 'Revisor',
    amlUserRoleUser: 'Usuario',
    amlUserRolePlatformAdmin: 'Administrador de Plataforma',
    amlUserStatusActive: 'Activo',
    amlUserStatusInvited: 'Invitado',
    amlUserStatusInactive: 'Inactivo',
    amlUserMenuLogin: 'Iniciar sesión',
    amlUserMenuDetail: 'Detalle',
    amlUserMenuEdit: 'Editar',
    amlUserMenuResetPassword: 'Restablecer Contraseña',
    amlUserMenuActivate: 'Activar',
    amlUserMenuDelete: 'Eliminar',
    amlUserLoginAsTitle: 'Iniciar sesión como usuario',
    amlUserLoginAsMessage: 'Estás a punto de iniciar sesión como {{name}}. Tu sesión actual se guardará y podrás restaurarla cerrando sesión.',
    amlUserBtnLogin: 'Iniciar sesión',
    amlUserDeleteTitle: 'Eliminar Usuario',
    amlUserDeleteMessage: '¿Estás seguro de que quieres eliminar permanentemente a "{{name}}"? Esta acción no se puede deshacer.',
    amlUserActivateTitle: 'Activar Usuario',
    amlUserActivateMessage: '¿Estás seguro de que quieres reactivar a "{{name}}"? Volverá a tener acceso al sistema.',
    amlUserActivateConfirm: 'Activar',
    amlUserToastDeleted: 'Usuario "{{name}}" eliminado.',
    amlUserToastActivated: 'Usuario "{{name}}" activado.',
    amlUserToastFailedToDelete: 'Error al eliminar el usuario.',
    amlUserToastFailedToActivate: 'Error al activar el usuario.',
    amlUserDetailTitle: 'Detalle de Usuario',
    amlUserDetailFieldStatus: 'Estado',
    amlUserDetailFieldRole: 'Rol',
    amlUserDetailFieldClient: 'Cliente',
    amlUserDetailFieldCreated: 'Creado',
    amlUserFormTitleEdit: 'Editar Usuario',
    amlUserFormTitleCreate: 'Agregar Usuario',
    amlUserFormFieldEmail: 'Correo',
    amlUserFormFieldFirstName: 'Nombre',
    amlUserFormFieldLastName: 'Apellido',
    amlUserFormFieldPassword: 'Contraseña',
    amlUserFormFieldConfirmPassword: 'Confirmar Contraseña',
    amlUserFormFieldClient: 'Cliente',
    amlUserFormFieldType: 'Tipo',
    amlUserFormFieldActive: 'Activo',
    amlUserFormPlaceholderEmail: 'usuario@ejemplo.com',
    amlUserFormPlaceholderFirstName: 'Ingresa el nombre',
    amlUserFormPlaceholderLastName: 'Ingresa el apellido',
    amlUserFormPlaceholderPassword: 'Ingresa la contraseña',
    amlUserFormPlaceholderConfirmPassword: 'Confirma la contraseña',
    amlUserFormPlaceholderSelectClient: 'Seleccionar cliente',
    amlUserFormValidationEmailRequired: 'El correo es obligatorio',
    amlUserFormValidationValidEmail: 'Ingresa un correo válido',
    amlUserFormValidationFirstNameRequired: 'El nombre es obligatorio',
    amlUserFormValidationLastNameRequired: 'El apellido es obligatorio',
    amlUserFormValidationClientRequired: 'El cliente es obligatorio',
    amlUserFormValidationPasswordRequired: 'La contraseña es obligatoria',
    amlUserFormValidationPasswordMinLength: 'La contraseña debe tener al menos {{count}} caracteres',
    amlUserFormValidationPasswordUppercase: 'La contraseña debe incluir una letra mayúscula',
    amlUserFormValidationPasswordLowercase: 'La contraseña debe incluir una letra minúscula',
    amlUserFormValidationPasswordNumber: 'La contraseña debe incluir un número',
    amlUserFormValidationPasswordSpecial: 'La contraseña debe incluir un carácter especial',
    amlUserFormValidationPasswordConfirmRequired: 'Se requiere confirmar la contraseña',
    amlUserFormValidationPasswordsDoNotMatch: 'Las contraseñas no coinciden',
    amlUserFormValidationEnterNewPasswordFirst: 'Ingresa la nueva contraseña antes de confirmar',
    amlUserFormErrorEmailAlreadyExists: 'Ya existe un usuario con este correo',
    amlUserFormErrorUserNotFound: 'Usuario no encontrado — puede haber sido eliminado',
    amlUserFormErrorNoPermission: 'No tienes permiso para realizar esta acción',
    amlUserFormErrorInvalidData: 'Datos inválidos — revisa tus entradas',
    amlUserFormErrorNetworkError: 'Error de red — verifica tu conexión y vuelve a intentarlo',
    amlUserFormBtnSaveChanges: 'Guardar Cambios',
    amlUserFormBtnCreateUser: 'Crear Usuario',
    amlUserToastUserUpdated: 'Usuario actualizado exitosamente',
    amlUserToastUserCreated: 'Usuario creado exitosamente',
    amlUserFormShowingOf: 'Mostrando {{from}}–{{to}} de {{total}}',

    // AML Reset Password
    amlResetPasswordTitle: 'Restablecer Contraseña',
    amlResetPasswordFieldNewPassword: 'Nueva Contraseña',
    amlResetPasswordFieldConfirmPassword: 'Confirmar Contraseña',
    amlResetPasswordPlaceholderNew: 'Ingresa la nueva contraseña',
    amlResetPasswordPlaceholderConfirm: 'Confirma la nueva contraseña',
    amlResetPasswordRuleAtLeast8Chars: 'Al menos 8 caracteres',
    amlResetPasswordRuleUppercase: 'Letra mayúscula',
    amlResetPasswordRuleLowercase: 'Letra minúscula',
    amlResetPasswordRuleNumber: 'Número',
    amlResetPasswordRuleSpecialChar: 'Carácter especial',
    amlResetPasswordValidationRequired: 'La contraseña es obligatoria',
    amlResetPasswordValidationMinLength: 'La contraseña debe tener al menos {{count}} caracteres',
    amlResetPasswordValidationDoesNotMeetRequirements: 'La contraseña no cumple todos los requisitos',
    amlResetPasswordValidationConfirmRequired: 'Por favor confirma la contraseña',
    amlResetPasswordValidationDoNotMatch: 'Las contraseñas no coinciden',
    amlResetPasswordBtnSetPassword: 'Establecer Contraseña',
    amlResetPasswordToastSuccess: 'Contraseña actualizada exitosamente',

    // AML Shared
    amlPageSizeShow: 'Mostrar',
    amlPageSizePerPage: 'por página',
    amlEmptyStateApplyFiltersToLoadData: 'Aplica filtros para cargar datos',
    amlEmptyStateApplyFiltersDesc: 'Usa los filtros de arriba para cargar el informe.',
    amlEmptyStateNoResultsFound: 'No se encontraron resultados',
    amlEmptyStateNoResultsDesc: 'Intenta ajustar tu búsqueda o filtros.',
    amlInlineCellPlaceholderEnter: 'Ingresar…',
    amlUserLoginFailedError: 'Error al iniciar sesión como usuario.',

    // Performance Sidebar
    perfSidebarTitle: 'Rendimiento',
    perfNavMyKpis: 'Mis KPIs',
    perfNavMyKpisDesc: 'Panel personal',
    perfNavTeam: 'Equipo',
    perfNavTeamDesc: 'KPIs de reportes directos',
    perfNavOrganization: 'Organización',
    perfNavOrganizationDesc: 'Visión general de la organización',
    perfNavKpiCatalog: 'Catálogo de KPIs',
    perfNavKpiCatalogDesc: 'Plantillas y definiciones',
    perfNavCycles: 'Ciclos',
    perfNavCyclesDesc: 'Ciclos de rendimiento',

    // Performance Header
    perfHeaderTitle: 'Rendimiento',
    perfHeaderSubtitleDefault: 'Seguimiento de KPIs e insights',
    perfBtnAssignKpi: 'Asignar KPI',

    // Performance My KPIs
    perfMyKpisGetStartedTitle: 'Comienza con el Seguimiento de Rendimiento',
    perfMyKpisGetStartedDesc: 'Para rastrear KPIs, primero debes crear un ciclo de rendimiento — un período de tiempo como un trimestre o semestre durante el cual se rastrean los KPIs. Esto te permite comparar el rendimiento entre períodos y mantener los objetivos enfocados.',
    perfMyKpisCreateFirstCycle: 'Crea Tu Primer Ciclo',
    perfMyKpisAskAdminToCreateCycle: 'Pide a tu administrador que cree un ciclo de rendimiento en la pestaña Ciclos.',
    perfMyKpisNoActiveCycleTitle: 'Ningún Ciclo Activo Seleccionado',
    perfMyKpisNoActiveCycleDesc: 'Selecciona un ciclo del menú desplegable en el encabezado, o activa uno de tus ciclos borrador en la pestaña Ciclos.',
    perfMyKpisOverallScore: 'Puntuación General',
    perfMyKpisSummaryOnTrack: 'En curso',
    perfMyKpisSummaryAtRisk: 'En riesgo',
    perfMyKpisSummaryBehind: 'Retrasado',
    perfMyKpisSummaryNotStarted: 'Sin iniciar',
    perfMyKpisSummaryCompleted: 'Completado',
    perfMyKpisNoKpisAssigned: 'No hay KPIs asignados para este ciclo',
    perfMyKpisNoKpisDesc: 'Explora el Catálogo de KPIs para asignar KPIs, o tu manager los establecerá por ti.',

    // Performance Team
    perfTeamNoDirectReportsTitle: 'Sin Reportes Directos',
    perfTeamNoDirectReportsDesc: 'Esta pestaña muestra el progreso de KPIs de las personas que te reportan. Actualmente, nadie en la organización tiene su campo "Reporta a" configurado para ti.',
    perfTeamSetupInstructions: 'Para configurar tu equipo:',
    perfTeamSetupStep1: 'Ve al módulo de Organización (icono de engranaje, barra lateral)',
    perfTeamSetupStep2: 'Abre el perfil de un miembro del equipo en la pestaña Personas',
    perfTeamSetupStep3: 'Establece su campo "Reporta a" como tú mismo',
    perfTeamMemberCount: '{{count}} miembro(s) del equipo',
    perfTeamOnTrack: '{{count}} en curso',
    perfTeamAtRisk: '{{count}} en riesgo',
    perfTeamBehind: '{{count}} retrasado(s)',
    perfTeamBtnAiSummary: 'Resumen IA',
    perfTeamAiSummaryGenerating: 'Generando...',
    perfTeamAiSummaryTitle: 'Resumen IA del Equipo',
    perfTeamNoKpisAssigned: 'Sin KPIs asignados',

    // Performance Organization
    perfOrgNoActiveCycleTitle: 'Sin Ciclo Activo',
    perfOrgNoActiveCycleDesc: 'No hay un ciclo de rendimiento activo. Crea uno para ver los datos de KPI de toda la organización.',
    perfOrgSummaryTotalKpis: 'Total de KPIs',
    perfOrgSummaryOnTrack: 'En curso',
    perfOrgSummaryAtRisk: 'En riesgo',
    perfOrgSummaryBehind: 'Retrasado',
    perfOrgSummaryNotStarted: 'Sin iniciar',
    perfOrgSummaryOverallScore: 'Puntuación General',
    perfOrgByDepartment: 'Por Departamento',
    perfOrgAllMembers: 'Todos los Miembros ({{count}})',
    perfOrgMemberKpiCount: '{{count}} KPIs',
    perfOrgNoMembersWithKpis: 'No hay miembros con KPIs en este departamento.',

    // Performance KPI Detail
    perfKpiDetailTabProgress: 'Progreso',
    perfKpiDetailTabComments: 'Comentarios',
    perfKpiDetailTabReviews: 'Revisiones',
    perfKpiDetailCheckIn: 'Registrar',
    perfKpiDetailAssignedTo: 'Asignado a {{name}}',
    perfKpiDetailAiInsight: 'Insight IA',
    perfKpiDetailCheckInHistory: 'Historial de Registros',
    perfKpiDetailNoCheckIns: 'Aún sin registros',
    perfKpiDetailNoComments: 'Aún sin comentarios. Inicia la conversación.',
    perfKpiDetailNoReviews: 'Aún sin revisiones. Las revisiones se envían durante la fase de revisión.',
    perfKpiDetailAddComment: 'Agregar un comentario...',
    perfKpiDetailOverdue: '({{count}} atrasado(s))',
    perfKpiDetailFinancialImpact: 'Impacto financiero: ${{value}}',
    perfKpiDetailStrengths: 'Fortalezas: {{text}}',
    perfKpiDetailImprovements: 'Áreas a mejorar: {{text}}',
    perfKpiDetailDeleteKpiQuestion: '¿Eliminar este KPI y todos sus datos?',
    perfKpiDetailArchiveKpiQuestion: '¿Archivar este KPI?',
    perfKpiDetailBtnDelete: 'Sí, eliminar',
    perfKpiDetailBtnArchive: 'Sí, archivar',
    perfKpiDetailBtnCancel: 'Cancelar',
    perfKpiDetailAriaLabelClose: 'Cerrar panel',
    perfKpiDetailAriaLabelArchive: 'Archivar este KPI',
    perfKpiDetailAriaLabelDelete: 'Eliminar este KPI',
    perfKpiDetailAriaLabelSendComment: 'Enviar comentario',
    perfKpiDetailRevisionFeedbackPlaceholder: 'Comentarios de revisión...',
    perfKpiDetailBtnVerify: 'Verificar',
    perfKpiDetailBtnRequestRevision: 'Solicitar Revisión',
    perfKpiDetailBtnSend: 'Enviar',
    perfKpiDetailStatusSubmitted: 'Enviado',
    perfKpiDetailStatusVerified: 'Verificado',
    perfKpiDetailStatusNeedsRevision: 'Necesita Revisión',
    perfKpiDetailReviewNote: 'Nota de revisión: {{text}}',

    // Performance KPI Card
    perfCardNoCheckInsYet: 'Aún sin registros',
    perfCardCheckIn: 'Registrar',
    perfCardUnnamedKpi: 'KPI sin nombre',

    // Performance KPI Status
    perfKpiStatusOnTrack: 'En curso',
    perfKpiStatusAtRisk: 'En riesgo',
    perfKpiStatusBehind: 'Retrasado',
    perfKpiStatusNotStarted: 'Sin iniciar',
    perfKpiStatusCompleted: 'Completado',
    perfKpiStatusPending: 'Pendiente',
    perfKpiStatusMissed: 'Perdido',
    perfKpiStatusCancelled: 'Cancelado',

    // Performance Check-in Form
    perfCheckInFormTitle: 'Registrar',
    perfCheckInBtnCancel: 'Cancelar',
    perfCheckInFieldCurrentValue: 'Valor Actual',
    perfCheckInFieldFinancialImpact: 'Impacto Financiero (este período)',
    perfCheckInFieldNotes: 'Notas (opcional)',
    perfCheckInFieldEvidence: 'Evidencia (opcional)',
    perfCheckInNotesPlaceholder: '¿Qué impulsó este número? Contexto para tu manager...',
    perfCheckInAttachFiles: 'Adjuntar archivos (PDF, imágenes, hojas de cálculo)',
    perfCheckInUploading: 'Subiendo...',
    perfCheckInFileTooLarge: 'El archivo "{{name}}" supera el límite de 50MB',
    perfCheckInErrorFailedToSubmit: 'Error al enviar el registro',
    perfCheckInBtnSubmit: 'Enviar Registro',

    // Performance Assign KPI
    perfAssignKpiTitleTemplate: 'Asignar: {{name}}',
    perfAssignKpiTitleCustom: 'Crear y Asignar KPI',
    perfAssignKpiStep1: '1. Definir',
    perfAssignKpiStep2: '2. Asignar',
    perfAssignKpiFieldKpiName: 'Nombre del KPI',
    perfAssignKpiFieldDescription: 'Descripción',
    perfAssignKpiSectionMeasurement: 'Medición',
    perfAssignKpiFieldTarget: 'Objetivo',
    perfAssignKpiFieldBaseline: 'Línea Base',
    perfAssignKpiFieldWeight: 'Peso',
    perfAssignKpiFieldUnitLabel: 'Etiqueta de Unidad',
    perfAssignKpiSectionCheckInFrequency: 'Frecuencia de Registro',
    perfAssignKpiCheckInFrequencyDesc: 'Con qué frecuencia el asignado debe reportar el progreso. Este no es el ciclo — el ciclo define el período general (p.ej. T1 2026), mientras la cadencia establece cuántos registros ocurren dentro de él.',
    perfAssignKpiFieldFormula: 'Fórmula / Método de Cálculo',
    perfAssignKpiSectionVerifier: 'Verificador de Registros',
    perfAssignKpiVerifierOptional: 'Opcional',
    perfAssignKpiVerifierPlaceholder: 'Elige quién valida los registros (p.ej. Finanzas, un manager)...',
    perfAssignKpiSearchVerifier: 'Buscar verificador...',
    perfAssignKpiTrackFinancialImpact: 'Rastrear Impacto Financiero',
    perfAssignKpiFinancialImpactDescOn: 'Establece el valor monetario esperado que este KPI generará o ahorrará',
    perfAssignKpiFinancialImpactDescOff: 'Opcional — vincula este KPI a un objetivo de ingresos o ahorros',
    perfAssignKpiFieldExpectedFinancialImpact: 'Impacto Financiero Esperado ($)',
    perfAssignKpiFinancialImpactNote: 'Este valor se rastreará contra el impacto real reportado en cada registro, dándote una vista continua del ROI.',
    perfAssignKpiFromTemplate: 'Desde plantilla:',
    perfAssignKpiSearchTeamMembers: 'Buscar miembros del equipo...',
    perfAssignKpiSelectedCount: '{{count}} seleccionado(s)',
    perfAssignKpiAvailableCount: '{{count}} disponible(s)',
    perfAssignKpiBtnCancel: 'Cancelar',
    perfAssignKpiBtnNextPickPeople: 'Siguiente: Elegir Personas',
    perfAssignKpiBtnAssign: 'Asignar KPI a {{count}} persona(s)',
    perfAssignKpiErrorFailed: 'Error al asignar KPI',
    perfAssignKpiKpiNamePlaceholder: 'p.ej., Tasa de Crecimiento de Ingresos, Creación de Valor Neto...',
    perfAssignKpiDescriptionPlaceholder: '¿Qué mide este KPI y por qué es importante?',
    perfAssignKpiFormulaPlaceholder: '¿Cómo se calcula este KPI? ¿Qué incluye/excluye?',
    perfAssignKpiUnitLabelPlaceholder: 'p.ej., entregables, departamentos, hitos...',
    perfAssignKpiFinancialImpactPlaceholder: 'p.ej., 50 000',

    // Performance Units
    perfUnitCurrency: 'Moneda ($)',
    perfUnitPercentage: 'Porcentaje (%)',
    perfUnitCount: 'Cantidad',
    perfUnitScore: 'Puntuación',
    perfUnitHours: 'Horas',
    perfUnitRatio: 'Ratio',
    perfUnitCustom: 'Personalizado',

    // Performance Categories
    perfCategoryFinancial: 'Financiero',
    perfCategorySales: 'Ventas',
    perfCategoryMarketing: 'Marketing',
    perfCategoryOperations: 'Operaciones',
    perfCategoryProduct: 'Producto',
    perfCategoryHrPeople: 'RR.HH. y Personas',
    perfCategoryCustomerSuccess: 'Éxito del Cliente',
    perfCategoryCustom: 'Personalizado',

    // Performance Cadences
    perfCadenceWeekly: 'Semanal',
    perfCadenceBiweekly: 'Quincenal',
    perfCadenceMonthly: 'Mensual',
    perfCadenceQuarterly: 'Trimestral',

    // Performance Directionality
    perfDirectionalityHigherBetter: 'Más alto es mejor',
    perfDirectionalityLowerBetter: 'Más bajo es mejor',
    perfDirectionalityHitExact: 'Alcanzar objetivo exacto',

    // Performance Cycle Modal
    perfCycleModalTitle: 'Nuevo Ciclo de Rendimiento',
    perfCycleFieldCycleType: 'Tipo de Ciclo',
    perfCycleFieldCycleName: 'Nombre del Ciclo',
    perfCycleFieldStartDate: 'Fecha de Inicio',
    perfCycleFieldEndDate: 'Fecha de Fin',
    perfCyclePlaceholderName: 'p.ej., Marketing, Ingeniería, Empresa...',
    perfCyclePlaceholderStartDate: 'Elige fecha de inicio',
    perfCyclePlaceholderEndDate: 'Elige fecha de fin',
    perfCycleWillDisplayAs: 'Se mostrará como:',
    perfCycleAutoAdvance: 'Avance automático al siguiente ciclo',
    perfCycleAutoAdvanceDesc: 'Cuando este ciclo se cierre, crear automáticamente el siguiente período y llevar los KPIs activos',
    perfCycleValidationFillAllFields: 'Por favor completa todos los campos',
    perfCycleValidationEndDateAfterStart: 'La fecha de fin debe ser posterior a la de inicio',
    perfCycleErrorFailedToCreate: 'Error al crear el ciclo',
    perfCycleBtnCancel: 'Cancelar',
    perfCycleBtnCreate: 'Crear Ciclo',
    perfCycleTypeWeekly: 'Semanal',
    perfCycleTypeWeeklyDesc: 'Semanas del año',
    perfCycleTypeMonthly: 'Mensual',
    perfCycleTypeMonthlyDesc: 'Meses del calendario',
    perfCycleTypeQuarterly: 'Trimestral',
    perfCycleTypeQuarterlyDesc: 'T1, T2, T3, T4',
    perfCycleTypeSemiAnnual: 'Semestral',
    perfCycleTypeSemiAnnualDesc: 'S1 y S2',
    perfCycleTypeAnnual: 'Anual',
    perfCycleTypeAnnualDesc: 'Año completo',
    perfCycleTypeCustom: 'Personalizado',
    perfCycleTypeCustomDesc: 'Establece tus propias fechas',

    // Performance Cycle Manager
    perfCycleManagerTitle: 'Ciclos de Rendimiento',
    perfCycleManagerBtnNewCycle: 'Nuevo Ciclo',
    perfCycleManagerWhatsACycleTitle: '¿Qué es un ciclo de rendimiento?',
    perfCycleManagerWhatsACycleDesc: 'Es un período de tiempo recurrente — una semana, mes, trimestre o año — durante el cual se rastrean los KPIs. Cuando un ciclo se cierra, avanza automáticamente al siguiente período y lleva los KPIs activos. Haz clic en "Nuevo Ciclo" para comenzar.',
    perfCycleManagerStatusDraft: 'Borrador',
    perfCycleManagerStatusActive: 'Activo',
    perfCycleManagerStatusInReview: 'En Revisión',
    perfCycleManagerStatusClosed: 'Cerrado',
    perfCycleManagerBtnActivate: 'Activar',
    perfCycleManagerBtnStartReview: 'Iniciar Revisión',
    perfCycleManagerBtnClose: 'Cerrar',
    perfCycleManagerBtnDelete: 'Eliminar',
    perfCycleManagerBtnNo: 'No',
    perfCycleManagerRecurringCycleTooltip: 'Ciclo recurrente',
    perfCycleManagerAriaLabelDeleteCycle: 'Eliminar ciclo',
    perfCycleManagerNoCyclesTitle: 'Aún sin ciclos de rendimiento',
    perfCycleManagerNoCyclesDesc: 'Haz clic en "Nuevo Ciclo" arriba para crear tu primer ciclo.',
    perfCycleManagerErrorCannotDelete: 'No se puede eliminar',

    // Performance KPI Catalog
    perfCatalogTitle: 'Catálogo de KPIs',
    perfCatalogTemplatesCount: '{{count}} plantillas',
    perfCatalogBtnCustomKpi: 'KPI Personalizado',
    perfCatalogSearchPlaceholder: 'Buscar plantillas por nombre, descripción o pregunta de negocio...',
    perfCatalogFilterAll: 'Todos',
    perfCatalogNoTemplatesMatch: 'Ninguna plantilla coincide con tu búsqueda',
    perfCatalogTemplateUnit: 'Unidad:',
    perfCatalogTemplateTarget: 'Objetivo:',
    perfCatalogTemplateCadence: 'Cadencia:',

    // Settings Sidebar
    settingsSidebarTitle: 'Configuración',
    settingsNavOrganization: 'Organización',
    settingsNavOrganizationDesc: 'Personas, departamentos y configuración',
    settingsNavPlatform: 'Plataforma',
    settingsNavPlatformDesc: 'Configuración global de la plataforma',

    // Settings Platform
    settingsPlatformTitle: 'Configuración de la Plataforma',
    settingsPlatformSubtitle: 'Gestión de tenants, organizaciones y módulos',
    settingsTabTenants: 'Tenants',
    settingsTabOrganizations: 'Organizaciones',
    settingsTabAi: 'IA',
    settingsTabDemo: 'Demo',

    settingsLlmPlatformTitle: 'Clave API de Anthropic',
    settingsLlmPlatformHelp:
      'Clave predeterminada de la plataforma usada por las organizaciones que no tienen clave propia. Necesaria para las funciones de IA.',
    settingsLlmOrgTitle: 'Clave API de Anthropic',
    settingsLlmOrgHelp:
      'Clave opcional de la organización. Si no se configura, las funciones de IA usan la clave de la plataforma.',
    settingsLlmFieldLabel: 'Clave API',
    settingsLlmFieldPlaceholder: 'sk-ant-...',
    settingsLlmConfigured: 'Configurada',
    settingsLlmNotConfigured: 'No configurada',
    settingsLlmUsingPlatform: 'Usando predeterminada de plataforma',
    settingsLlmBtnSave: 'Guardar clave',
    settingsLlmBtnUpdate: 'Actualizar clave',
    settingsLlmBtnClear: 'Borrar clave',
    settingsLlmBtnClearOrg: 'Usar predeterminada de plataforma',
    settingsLlmClearConfirmTitle: '¿Borrar clave API?',
    settingsLlmClearConfirmMessage:
      'Las funciones de IA dejarán de funcionar hasta que se configure una nueva clave de plataforma (o las orgs configuren la suya).',
    settingsLlmClearOrgConfirmMessage:
      'Esta organización usará la clave API de Anthropic de la plataforma.',
    settingsLlmSaveSuccess: 'Clave API guardada',
    settingsLlmSaveError: 'Error al guardar la clave API',
    settingsLlmClearSuccess: 'Clave API borrada',
    settingsLlmClearError: 'Error al borrar la clave API',
    settingsLlmError: 'Error al cargar el estado de la clave API',
    settingsLlmModelTitle: 'Modelo Claude',
    settingsLlmModelHelp:
      'ID de modelo de Anthropic a nivel de plataforma usado por todas las funciones de IA.',
    settingsLlmModelFieldLabel: 'ID del modelo',
    settingsLlmModelFieldPlaceholder: 'claude-sonnet-5',
    settingsLlmModelBtnSave: 'Guardar modelo',
    settingsLlmModelSaveSuccess: 'Modelo guardado',
    settingsLlmModelSaveError: 'Error al guardar el modelo',

    // Settings Tenants
    settingsTenantsBtnCreateTenant: 'Crear Tenant',
    settingsTenantsFieldName: 'Nombre',
    settingsTenantsFieldSlug: 'Slug',
    settingsTenantsPlaceholderName: 'p.ej. Acme Corp',
    settingsTenantsPlaceholderSlug: 'p.ej. acme-corp',
    settingsTenantsNoTenants: 'Aún sin tenants',
    settingsTenantsBtnCreate: 'Crear',
    settingsTenantsBtnCancel: 'Cancelar',
    settingsTenantsBtnSuspend: 'Suspender',
    settingsTenantsBtnActivate: 'Activar',
    settingsTenantsCount: '{{count}} tenant',
    settingsTenantsCountPlural: '{{count}} tenants',
    settingsTenantsBadgeHq: 'HQ',
    settingsTenantsNoOrgsInTenant: 'No hay organizaciones en este tenant',
    settingsTenantsModuleAccess: 'Acceso a Módulos',
    settingsTenantsAriaLabelInviteUser: 'Invitar usuario a esta organización',
    settingsTenantsToastTenantCreated: 'Tenant creado',
    settingsTenantsToastTenantSuspended: 'Tenant suspendido',
    settingsTenantsToastTenantActivated: 'Tenant activo',
    settingsTenantsToastModuleDisabled: 'Módulo deshabilitado',
    settingsTenantsToastModuleEnabled: 'Módulo habilitado',
    settingsTenantsErrorFailedToCreateTenant: 'Error al crear el tenant',
    settingsTenantsErrorFailedToUpdateTenant: 'Error al actualizar el tenant',
    settingsTenantsErrorFailedToToggleModule: 'Error al cambiar el módulo',
    settingsTenantsErrorFailedToEnableModule: 'Error al habilitar el módulo',
    settingsProvisionModalTitle: 'Habilitar Módulo para Usuarios',
    settingsProvisionModalBody: '¿Habilitar este módulo para todos los usuarios existentes en la organización, o dejarlo deshabilitado y permitir que el administrador asigne el acceso manualmente?',
    settingsProvisionBtnCancel: 'Cancelar',
    settingsProvisionBtnNoneManual: 'Ninguno (manual)',
    settingsProvisionBtnAllUsers: 'Todos los Usuarios',

    // Settings Organizations
    settingsOrgsBtnCreateOrg: 'Crear Org',
    settingsOrgsFilterByTenant: 'Filtrar por tenant',
    settingsOrgsPlaceholderAllTenants: 'Todos los tenants',
    settingsOrgsPlaceholderSelectTenant: 'Seleccionar tenant',
    settingsOrgsFieldOrganizationName: 'Nombre de la Organización',
    settingsOrgsPlaceholderOrgName: 'p.ej. Operaciones S.A.',
    settingsOrgsNoOrganizations: 'Sin organizaciones',
    settingsOrgsCount: '{{count}} organización',
    settingsOrgsCountPlural: '{{count}} organizaciones',
    settingsOrgsRoleCount: '{{count}} rol',
    settingsOrgsRoleCountPlural: '{{count}} roles',
    settingsOrgsUnknownTenant: 'Desconocido',
    settingsOrgsToastOrgCreated: 'Organización creada',
    settingsOrgsErrorFailedToCreateOrg: 'Error al crear la organización',
    settingsOrgsModuleAccess: 'Acceso a Módulos',
    settingsOrgsAriaLabelInviteUser: 'Invitar usuario a esta organización',
    settingsOrgsLeadApiKeyTitle: 'Clave API de Formulario de Leads',
    settingsOrgsLeadApiKeyHelp: 'Usa esta clave como encabezado X-API-Key al enviar leads desde tu sitio web.',
    settingsOrgsLeadApiKeyCopy: 'Copiar',
    settingsOrgsLeadApiKeyCopied: '¡Copiado!',
    settingsOrgsLeadApiKeyError: 'Error al cargar la clave API',
    settingsOrgsLeadApiKeyShow: 'Mostrar clave',
    settingsOrgsLeadApiKeyHide: 'Ocultar clave',
    settingsOrgsLeadApiKeyRegenerate: 'Regenerar',
    settingsOrgsLeadApiKeyRegenerateConfirmTitle: '¿Regenerar clave API?',
    settingsOrgsLeadApiKeyRegenerateConfirmMessage:
      'La clave actual dejará de funcionar de inmediato. Debes actualizar cualquier sitio o integración que use la clave anterior.',
    settingsOrgsLeadApiKeyRegenerateSuccess: 'Clave API regenerada',
    settingsOrgsLeadApiKeyRegenerateError: 'Error al regenerar la clave API',

    // Settings Demo
    settingsDemoTitle: 'Organizaciones Demo',
    settingsDemoSubtitle: 'Crea organizaciones demo completamente pobladas con departamentos, usuarios, KPIs y datos de rendimiento.',
    settingsDemoBtnCreateDemoOrg: 'Crear Org Demo',
    settingsDemoPlaceholderOrgName: 'Nombre de la organización (p.ej. Acme Corp)',
    settingsDemoCreatedTitle: 'Demo Creado',
    settingsDemoDismiss: 'Descartar',
    settingsDemoFieldOrganization: 'Organización',
    settingsDemoFieldStats: 'Estadísticas',
    settingsDemoFieldCredentials: 'Credenciales',
    settingsDemoFieldEmail: 'Correo',
    settingsDemoFieldPassword: 'Contraseña',
    settingsDemoStatUsers: '{{count}} usuarios',
    settingsDemoStatKpis: '{{count}} KPIs',
    settingsDemoNoDemoOrgs: 'Aún sin organizaciones demo',
    settingsDemoOrgCount: '{{count}} org demo',
    settingsDemoOrgCountPlural: '{{count}} orgs demo',
    settingsDemoToastDemoOrgCreated: 'Org demo creada',
    settingsDemoToastDemoOrgDeleted: 'Org demo eliminada',
    settingsDemoErrorFailedToCreate: 'Error al crear la org demo',
    settingsDemoErrorFailedToDelete: 'Error al eliminar',
    settingsDemoDeleteTitle: 'Eliminar Organización Demo',
    settingsDemoDeleteBody: 'Esto eliminará permanentemente {{name}} y todos sus datos.',
    settingsDemoBtnDeleteEverything: 'Eliminar Todo',
    settingsDemoBtnCancel: 'Cancelar',

    // Discovery Portal
    discoveryPortalTitle: 'Agente de Descubrimiento',
    discoveryTabBoard: 'Tablero',
    discoveryTabChat: 'Chat',
    discoveryTabSpec: 'Especificación',
    discoveryBoardFilterAll: 'Todos',
    discoveryBoardFilterCollecting: 'Recopilando',
    discoveryBoardFilterSpecReady: 'Spec Lista',
    discoveryBoardFilterSubmitted: 'Enviado',
    discoveryBoardFilterInProgress: 'En Progreso',
    discoveryBoardBtnNewRequest: 'Nueva Solicitud',
    discoveryBoardFailedToLoad: 'Error al cargar las solicitudes.',
    discoveryBoardBtnRetry: 'Reintentar',
    discoveryBoardNoneYet: 'Aún sin solicitudes de descubrimiento.',
    discoveryBoardBtnCreateFirst: 'Crea tu primera solicitud',
    discoveryBoardCardUntitledRequest: 'Solicitud sin título',
    discoveryBoardCardUnknownInitiator: 'Desconocido',
    discoveryBoardNewRequestDialogTitle: 'Nueva Solicitud de Descubrimiento',
    discoveryBoardNewRequestLabel: 'Describe qué necesitas construir o cambiar',
    discoveryBoardNewRequestPlaceholder: 'p.ej. Necesitamos un panel que muestre datos de ventas en tiempo real para todas las regiones...',
    discoveryBoardBtnCancelDialog: 'Cancelar',
    discoveryBoardBtnCreateRequest: 'Crear Solicitud',
    discoveryStatusDraft: 'Borrador',
    discoveryStatusCollecting: 'Recopilando',
    discoveryStatusSpecReady: 'Spec Lista',
    discoveryStatusSubmitted: 'Enviado',
    discoveryStatusInProgress: 'En Progreso',
    discoveryStatusImplemented: 'Implementado',
    discoveryStatusDeclined: 'Rechazado',
    discoveryChatSelectRequest: 'Selecciona una solicitud del tablero para comenzar a chatear.',
    discoveryChatNotificationsOn: 'Notificaciones activadas',
    discoveryChatNotificationsOff: 'Notificaciones desactivadas',
    discoveryChatEmailNotificationsOn: 'Notificaciones por correo activadas',
    discoveryChatEmailNotificationsOff: 'Notificaciones por correo desactivadas',
    discoveryChatStakeholders: 'Partes interesadas',
    discoveryChatUntitledRequest: 'Solicitud sin título',
    discoveryChatDiscoveryAgentLabel: 'Agente de Descubrimiento',
    discoveryChatInputPlaceholder: 'Describe tus necesidades o responde las preguntas del agente...',
    discoveryChatDropFilesHere: 'Suelta archivos aquí',
    discoveryChatBtnAddMoreInfo: 'Agregar Más Información',
    discoveryChatAriaLabelAttachFile: 'Adjuntar archivo',
    discoveryActionSuggestedStakeholder: 'Parte Interesada Sugerida',
    discoveryActionSameOrDifferentPerson: '{{name}} ya es una parte interesada. ¿Es la misma persona o alguien diferente?',
    discoveryActionBtnSamePerson: 'La misma persona',
    discoveryActionBtnDifferentPerson: 'Una persona diferente',
    discoveryActionStakeholderLabelName: 'Nombre',
    discoveryActionStakeholderLabelContext: 'Contexto',
    discoveryActionStakeholderNamePlaceholder: 'Nombre',
    discoveryActionStakeholderContextPlaceholder: 'Por qué esta persona es relevante...',
    discoveryActionSearchByNameOrEmail: 'Buscar por nombre o ingresar correo...',
    discoveryActionBtnAddStakeholder: 'Agregar Parte Interesada',
    discoveryActionBtnAdding: 'Agregando...',
    discoveryActionFollowUpRequestTitle: 'Solicitud de Seguimiento',
    discoveryActionBtnSend: 'Enviar',
    discoveryActionBtnSkip: 'Omitir',
    discoveryActionReadyToGenerateSpecTitle: 'Listo para Generar Especificación',
    discoveryActionReadyToGenerateSpecDesc: 'El agente ha recopilado suficiente información para generar un documento de especificación.',
    discoveryActionBtnGenerateSpec: 'Generar Especificación',
    discoveryStakeholderPanelTitle: 'Partes Interesadas',
    discoveryStakeholderPanelNoStakeholders: 'Aún sin partes interesadas.',
    discoveryStakeholderPanelNoStakeholdersDesc: 'Agrega personas que puedan proporcionar información sobre esta solicitud.',
    discoveryStakeholderAriaLabelAdd: 'Agregar parte interesada',
    discoveryStakeholderSearchPlaceholder: 'Buscar por correo o nombre...',
    discoveryStakeholderContextPlaceholder: 'Relevante para... (p.ej. conoce el sistema de facturación)',
    discoveryStakeholderBtnAdd: 'Agregar Parte Interesada',
    discoveryStakeholderBadgeExternal: 'Externo',
    discoveryStakeholderBadgeInternal: 'Interno',
    discoveryStakeholderBadgeAppUser: 'Usuario de la App',
    discoveryStakeholderStatusInvited: 'Invitado',
    discoveryStakeholderStatusActive: 'Activo',
    discoveryStakeholderStatusCompleted: 'Completado',
    discoveryStakeholderErrorAlreadyExists: 'Esta persona ya es una parte interesada en esta solicitud.',
    discoveryStakeholderErrorFailedToAdd: 'Error al agregar la parte interesada',
    discoverySpecTitle: 'Especificación',
    discoverySpecBtnBackToChat: 'Volver al Chat',
    discoverySpecVersionLabel: 'Versión {{count}}',
    discoverySpecBtnDownloadMd: 'Descargar MD',
    discoverySpecBtnExportPdf: 'Exportar PDF',
    discoverySpecBtnRequestRevision: 'Solicitar Revisión',
    discoverySpecNoSpecTitle: 'Aún sin especificación generada.',
    discoverySpecNoSpecDesc: 'Continúa chateando con el Agente de Descubrimiento para recopilar requisitos, luego genera una especificación cuando estés listo.',
    discoverySpecBtnGenerateSpec: 'Generar Especificación',
    discoverySpecErrorFailedToLoad: 'Error al cargar las versiones de la especificación',
    discoverySpecErrorFailedToGenerate: 'Error al generar la especificación',
    discoveryExternalPortalBrandDiscovery: 'Descubrimiento',
    discoveryExternalPortalClosedBanner: 'Esta solicitud ha sido cerrada. Puedes revisar la conversación pero no puedes enviar nuevos mensajes.',
    discoveryExternalPortalDefaultWelcome: 'Has sido invitado a compartir tu perspectiva sobre esta solicitud de funcionalidad. El agente te hará preguntas específicas.',
    discoveryExternalPortalEmptyPrompt: 'Comparte tus pensamientos, requisitos o preguntas sobre esta solicitud de funcionalidad. El agente de descubrimiento guiará la conversación.',
    discoveryExternalPortalInputPlaceholder: 'Escribe tu mensaje…',
    discoveryExternalPortalBtnAddMoreInfo: 'Agregar Más Información',
    discoveryExternalPortalLoadingPortal: 'Cargando portal…',
    discoveryExternalPortalErrorTitle: 'No se puede cargar',
    discoveryExternalPortalErrorDefault: 'Este enlace es inválido o ha expirado. Por favor verifica con la persona que lo compartió.',
    discoveryExternalPortalErrorInvalidLink: 'Enlace inválido — no se proporcionó token de acceso.',
    discoveryExternalPortalErrorExpiredLink: 'Este enlace es inválido o ha expirado.',
    discoveryExternalPortalErrorClosed: 'Esta solicitud de descubrimiento ha sido cerrada.',
    discoveryExternalPortalErrorLoadFailed: 'Error al cargar. Por favor inténtalo más tarde.',
    discoveryExternalPortalErrorUploadFailed: 'Error al subir el archivo. Por favor inténtalo de nuevo.',
    discoveryExternalPortalErrorSomethingWentWrong: 'Lo sentimos, algo salió mal. Por favor inténtalo de nuevo.',
    discoveryExternalPortalInterjectionAddressed: 'Gracias — tu aporte sobre esto ya ha sido registrado. Puedes continuar abajo si tienes más que agregar.',
    discoveryExternalPortalAriaLabelAttachFile: 'Adjuntar archivo',
    discoveryExternalPortalAriaLabelStopGenerating: 'Detener generación',
    discoveryExternalPortalAriaLabelSendMessage: 'Enviar mensaje',

    // Survey Portal
    surveyPortalTitle: 'Encuestas',
    surveyTabBoard: 'Tablero',
    surveyTabEditor: 'Editor',
    surveyTabResults: 'Resultados',

    // Survey Board
    surveyBoardFilterAll: 'Todas',
    surveyBoardFilterDrafts: 'Borradores',
    surveyBoardFilterInProgress: 'En Progreso',
    surveyBoardFilterCompleted: 'Completadas',
    surveyBoardStatusDraft: 'Borrador',
    surveyBoardStatusInProgress: 'En Progreso',
    surveyBoardStatusCompleted: 'Completada',
    surveyBoardBtnNewSurvey: 'Nueva Encuesta',
    surveyBoardFailedToLoad: 'Error al cargar las encuestas.',
    surveyBoardBtnRetry: 'Reintentar',
    surveyBoardNoneYet: 'Aún no hay encuestas.',
    surveyBoardBtnCreateFirst: 'Crea tu primera encuesta',
    surveyBoardCardUntitled: 'Encuesta sin título',
    surveyBoardCardUnknownCreator: 'Desconocido',
    surveyBoardBtnStop: 'Detener',
    surveyBoardDeleteTitle: 'Eliminar encuesta',
    surveyBoardDeleteMessage: '¿Seguro que quieres eliminar "{{title}}"? Esta acción no se puede deshacer.',
    surveyBoardDeleteConfirm: 'Eliminar',
    surveyBoardDeleteDeleting: 'Eliminando…',
    surveyBoardDeleteTooltip: 'Eliminar encuesta',
    surveyBoardBtnCancel: 'Cancelar',
    surveyBoardUploadTooltip: 'Subir respuesta',

    // Survey Upload Modal
    surveyUploadTitle: 'Subir respuesta de encuesta',
    surveyUploadLabelName: 'Nombre del encuestado',
    surveyUploadPlaceholderName: 'Nombre completo…',
    surveyUploadLabelEmail: 'Correo del encuestado',
    surveyUploadPlaceholderEmail: 'correo@ejemplo.com',
    surveyUploadLabelContent: 'Contenido de la respuesta',
    surveyUploadPlaceholderContent: 'Pega el correo o texto con las respuestas aquí…',
    surveyUploadModePaste: 'Pegar texto',
    surveyUploadModeFile: 'Subir archivo',
    surveyUploadDropHint: 'Arrastra un archivo aquí (.txt, .csv, .xlsx, .doc, .docx, .pdf, …)',
    surveyUploadProcessing: 'Analizando contenido con IA…',
    surveyUploadConfidenceLabel: 'Confianza IA: {{value}}%',
    surveyUploadMappedCount: '{{count}} de {{total}} preguntas mapeadas',
    surveyUploadLowConfidence: 'El contenido no parece ser una respuesta válida a esta encuesta. Prueba con otro texto o selecciona la encuesta correcta.',
    surveyUploadProcessingFailed: 'Error al procesar el contenido. Inténtalo de nuevo.',
    surveyUploadStoreFailed: 'Error al guardar la respuesta.',
    surveyUploadSuccessToast: '¡Respuesta subida exitosamente!',
    surveyUploadTryDifferent: 'Intenta pegar otro texto o subir otro archivo.',
    surveyUploadBtnCancel: 'Cancelar',
    surveyUploadBtnProcess: 'Procesar con IA',
    surveyUploadBtnBack: 'Atrás',
    surveyUploadBtnConfirm: 'Confirmar y guardar',
    surveyUploadBtnTryAgain: 'Intentar de nuevo',
    surveyUploadBtnProceedAnyway: 'Continuar de todos modos',
    surveyUploadLowConfirmTitle: 'Confianza baja — ¿Estás seguro?',
    surveyUploadLowConfirmMessage: 'La confianza de IA es solo {{value}}%. Las respuestas mapeadas pueden ser imprecisas. ¿Deseas guardar esta respuesta de todos modos?',
    surveyUploadLowConfirmYes: 'Sí, guardar de todos modos',
    surveyUploadEmailRequired: 'El correo es obligatorio',
    surveyUploadEmailInvalid: 'Ingresa un correo válido',
    surveyUploadNameRequired: 'El nombre es obligatorio',

    // Survey Editor
    surveyEditorLabelTitle: 'Título de la encuesta',
    surveyEditorPlaceholderTitle: 'Ingresa el título de la encuesta...',
    surveyEditorLabelRecipients: 'Destinatarios',
    surveyEditorRecipientCount: '{{count}} destinatario',
    surveyEditorRecipientCountPlural: '{{count}} destinatarios',
    surveyEditorPlaceholderEmail: 'Ingresa el correo y presiona Enter...',
    surveyEditorBtnAdd: 'Agregar',
    surveyEditorUploadExtracting: 'Extrayendo correos...',
    surveyEditorUploadLabel: 'Sube un archivo para extraer correos (CSV, Excel, PDF, TXT)',
    surveyEditorLabelPublicLink: 'Enlace público',
    surveyEditorPublicLinkCaption: 'Permite que cualquiera con el enlace responda (ingresarán su nombre y correo).',
    surveyEditorPublicLinkPendingSave: ' El enlace se generará al guardar.',
    surveyEditorAriaLabelCopyLink: 'Copiar enlace',
    surveyEditorLabelDeadline: 'Fecha límite (opcional)',
    surveyEditorPlaceholderDeadline: 'Seleccionar fecha límite...',
    surveyEditorLabelQuestions: 'Preguntas',
    surveyEditorPlaceholderQuestion: 'Escribe tu pregunta...',
    surveyEditorBtnAddQuestion: 'Agregar pregunta',
    surveyEditorBtnSave: 'Guardar',
    surveyEditorBtnSaveAndSend: 'Guardar y enviar',
    surveyEditorToastExtractedEmails: 'Se extrajeron {{count}} correo(s) nuevo(s) de {{filename}}',
    surveyEditorToastNoNewEmails: 'No se encontraron correos nuevos en el archivo',
    surveyEditorErrorExtractEmails: 'Error al extraer correos del archivo',
    surveyEditorErrorToggleLink: 'Error al activar el enlace público',
    surveyEditorToastLinkCopied: 'Enlace copiado al portapapeles',
    surveyEditorErrorTitleRequired: 'El título y al menos una pregunta son requeridos',
    surveyEditorErrorRecipientRequired: 'Agrega al menos un destinatario o activa el enlace público para enviar',
    surveyEditorToastSaved: 'Encuesta guardada',
    surveyEditorErrorSaveFailed: 'Error al guardar la encuesta',
    surveyEditorToastSent: 'Encuesta enviada',
    surveyEditorErrorSendFailed: 'Error al enviar la encuesta',

    // Survey Results
    surveyResultsTabAiSummary: 'Resumen IA',
    surveyResultsTabResponses: 'Respuestas',
    surveyResultsBtnDownloadPdf: 'Descargar PDF',
    surveyResultsBtnRegenerate: 'Regenerar resumen',
    surveyResultsAiLoading: 'Generando resumen de IA...',
    surveyResultsAiNotAvailable: 'Resumen de IA aún no disponible.',
    surveyResultsOverview: 'Resumen general',
    surveyResultsKeyTakeaways: 'Conclusiones clave',
    surveyResultsFallbackTitle: 'Resumen IA',
    surveyResultsSentimentPositive: 'Positivo',
    surveyResultsSentimentNegative: 'Negativo',
    surveyResultsSentimentMixed: 'Mixto',
    surveyResultsSentimentNeutral: 'Neutral',
    surveyResultsResponseCount: '{{count}} respuesta',
    surveyResultsResponseCountPlural: '{{count}} respuestas',
    surveyResultsSearchPlaceholder: 'Buscar por nombre o correo...',
    surveyResultsAllRecipients: 'Todos los destinatarios ({{responded}} de {{total}} respondieron)',
    surveyResultsTagViaLink: 'Vía enlace',
    surveyResultsBadgeCompleted: 'Completado',
    surveyResultsBadgePending: 'Pendiente',
    surveyResultsNotRespondedYet: 'Este destinatario aún no ha respondido.',
    surveyResultsNoAnswers: 'No hay respuestas registradas.',
    surveyResultsFallbackQuestion: 'Pregunta',
    surveyResultsSelectRecipient: 'Selecciona un destinatario para ver sus respuestas.',

    // Survey Responder
    surveyResponderErrorUnavailable: 'Esta encuesta ya no está disponible o el enlace es inválido.',
    surveyResponderErrorDuplicateEmail: 'Este correo ya fue utilizado para responder esta encuesta.',
    surveyResponderErrorSubmitFailed: 'Error al enviar tu respuesta. Por favor inténtalo de nuevo.',
    surveyResponderThankYou: '¡Gracias!',
    surveyResponderResponseRecorded: 'Tu respuesta ha sido registrada. Puedes cerrar esta página.',
    surveyResponderLabelEmail: 'Tu correo',
    surveyResponderPlaceholderEmail: 'tu@correo.com',
    surveyResponderLabelName: 'Tu nombre',
    surveyResponderPlaceholderName: 'Ingresa tu nombre...',
    surveyResponderPlaceholderAnswer: 'Escribe tu respuesta...',
    surveyResponderFallbackTitle: 'Encuesta',
    surveyResponderBtnSubmit: 'Enviar respuesta',

    // Sales — Sidebar
    salesSidebarTitle: 'Ventas',
    salesSidebarPipelineLabel: 'Pipeline',
    salesSidebarPipelineDesc: 'Tablero Kanban',
    salesSidebarArchivedLabel: 'Archivados',
    salesSidebarArchivedDesc: 'Leads archivados',
    salesSidebarGeneratorLabel: 'Generador IA',
    salesSidebarGeneratorDesc: 'Descubrir nuevos leads',
    salesSidebarSettingsLabel: 'Configuración',
    salesSidebarSettingsDesc: 'Perfil y ejemplos',

    // Sales — Status labels
    salesStatusNew: 'Nuevo',
    salesStatusContacted: 'Contactado',
    salesStatusNegotiation: 'Negociación',
    salesStatusWon: 'Ganado',
    salesStatusLost: 'Perdido',
    salesStatusDisqualified: 'Descalificado',

    // Sales — Kanban board
    salesKanbanLeadsInPipeline: '{{count}} lead en pipeline',
    salesKanbanLeadsInPipelinePlural: '{{count}} leads en pipeline',
    salesKanbanErrorUpdateStatus: 'Error al actualizar estado',
    salesKanbanMovedTo: 'Movido a {{status}}',
    salesKanbanNoLeads: 'No hay leads aquí',

    // Sales — Lead card
    salesLeadCardAiBadge: 'IA',
    salesLeadCardSalesLabel: 'Ventas',
    salesLeadCardAiRatingTitle: 'Calificación IA',
    salesLeadCardDragHandle: 'Arrastrar para reordenar',

    // Sales — Lead modal
    salesLeadModalTabOverview: 'Resumen',
    salesLeadModalTabComments: 'Comentarios',
    salesLeadModalTabHistory: 'Historial',
    salesLeadModalBadgeAiDiscovered: 'Descubierto por IA',
    salesLeadModalBtnUnarchive: 'Desarchivar',
    salesLeadModalBtnArchive: 'Archivar',
    salesLeadModalLabelAiRating: 'Calificación IA',
    salesLeadModalBtnRefreshAi: 'Actualizar análisis IA',
    salesLeadModalLabelYourRating: 'Tu calificación',
    salesLeadModalLabelBusinessInfo: 'Info del negocio',
    salesLeadModalLabelPhone: 'Teléfono',
    salesLeadModalLabelSuggestedPitch: 'Pitch sugerido',
    salesLeadModalLabelAiAnalysis: 'Análisis IA',
    salesLeadModalCreated: 'Creado: ',
    salesLeadModalUpdated: 'Actualizado: ',
    salesLeadModalNoComments: 'Sin comentarios aún',
    salesLeadModalNoHistory: 'Sin historial aún',
    salesLeadModalCommentPlaceholder: 'Escribe un comentario... (@agent para preguntar a la IA)',
    salesLeadModalCommentHint: 'seguido de una pregunta para preguntar a la IA',
    salesLeadModalCommentHintPrefix: 'Escribe ',
    salesLeadModalAgentLabel: 'Agente IA',
    salesLeadModalFallbackUser: 'Usuario',
    salesLeadModalFallbackSystem: 'Sistema',
    salesLeadModalToastUnarchived: 'Lead desarchivado',
    salesLeadModalToastArchived: 'Lead archivado',
    salesLeadModalToastAiRefreshed: 'Calificación IA actualizada',
    salesLeadModalErrorRefreshAi: 'Error al actualizar calificación IA',
    salesLeadModalErrorAgentFailed: 'Solicitud al agente fallida',
    salesLeadModalErrorCommentFailed: 'Error al enviar comentario',
    salesLeadModalHistoryCreatedAs: 'Creado como {{status}}',

    // Sales — Archived
    salesArchivedEmpty: 'Sin leads archivados',
    salesArchivedEmptyDesc: 'Los leads archivados aparecerán aquí',
    salesArchivedCount: '{{count}} lead archivado',
    salesArchivedCountPlural: '{{count}} leads archivados',
    salesArchivedColCompany: 'Empresa',
    salesArchivedColStatus: 'Estado',
    salesArchivedColAiRating: 'Calificación IA',
    salesArchivedColSalesRating: 'Calificación Ventas',
    salesArchivedColCreated: 'Creado',
    salesArchivedBtnUnarchive: 'Desarchivar',

    // Sales — Company Profile
    salesProfileTitle: 'Perfil de empresa',
    salesProfileDesc: 'Describe tu empresa, el perfil de cliente ideal y qué hace un buen lead. Este contexto es usado por el agente de IA al calificar leads.',
    salesProfilePlaceholder: 'ej. Somos una empresa SaaS B2B enfocada en cumplimiento empresarial. Nuestro cliente ideal es...',
    salesProfileBtnSave: 'Guardar perfil',
    salesProfileToastSaved: 'Perfil de empresa actualizado',
    salesProfileErrorSave: 'Error al actualizar perfil',

    // Sales — Lead Examples
    salesExamplesTitle: 'Ejemplos de leads',
    salesExamplesDesc: 'Buenos y malos ejemplos ayudan a la IA a aprender qué hace un lead de calidad para tu negocio.',
    salesExamplesBtnAdd: 'Agregar ejemplo',
    salesExamplesTypeGood: 'Bueno',
    salesExamplesTypeBad: 'Malo',
    salesExamplesLabelUrl: 'URL de empresa',
    salesExamplesPlaceholderUrl: 'https://ejemplo.com',
    salesExamplesLabelExplanation: 'Explicación',
    salesExamplesPlaceholderExplanation: '¿Por qué este es un buen/mal ejemplo de lead?',
    salesExamplesBtnCancel: 'Cancelar',
    salesExamplesBtnSave: 'Guardar ejemplo',
    salesExamplesEmpty: 'Sin ejemplos aún',
    salesExamplesEmptyDesc: 'Agrega buenos y malos ejemplos de leads para mejorar la precisión de calificación IA',
    salesExamplesAriaDelete: 'Eliminar ejemplo',
    salesExamplesToastAdded: 'Ejemplo agregado',
    salesExamplesErrorAdd: 'Error al agregar ejemplo',
    salesExamplesToastDeleted: 'Ejemplo eliminado',
    salesExamplesErrorDelete: 'Error al eliminar ejemplo',

    // Sales — Lead Generator
    salesGeneratorTitle: 'Generador IA de leads',
    salesGeneratorSubtitle: 'Descubre y califica leads con análisis de IA',
    salesGeneratorCardLocation: 'Ubicación objetivo',
    salesGeneratorLabelCountry: 'País',
    salesGeneratorPlaceholderCountry: 'Seleccionar país...',
    salesGeneratorLabelState: 'Estado',
    salesGeneratorPlaceholderState: 'Todos los estados',
    salesGeneratorLabelCity: 'Ciudad',
    salesGeneratorPlaceholderCity: 'ej. Ciudad de México',
    salesGeneratorLabelDistance: 'Distancia: {{value}} km',
    salesGeneratorCardRefinement: 'Refinamiento de búsqueda',
    salesGeneratorOptionalLabel: '(opcional)',
    salesGeneratorLabelKeywords: 'Industria / Palabras clave',
    salesGeneratorPlaceholderKeywords: 'ej. SaaS, manufactura, salud',
    salesGeneratorHintKeywords: 'Déjalo vacío para que la IA decida según tu perfil de empresa',
    salesGeneratorLabelMaxResults: 'Máx. resultados',
    salesGeneratorBtnGenerate: 'Generar leads',
    salesGeneratorInfoText: 'La IA usará tu perfil de empresa y ejemplos de leads desde Configuración para encontrar y calificar clientes potenciales.',
    salesGeneratorInitMessage: 'Inicializando generador IA de leads...',
    salesGeneratorRunningTitle: 'Descubriendo leads...',
    salesGeneratorFoundCount: '{{count}} encontrados',
    salesGeneratorBtnNewSearch: 'Nueva búsqueda',
    salesGeneratorEmptyTitle: 'Sin leads descubiertos',
    salesGeneratorEmptyDesc: 'Intenta ampliar tus criterios de búsqueda.',
    salesGeneratorBtnTryAgain: 'Intentar de nuevo',
    salesGeneratorBtnDeselectAll: 'Deseleccionar todo',
    salesGeneratorBtnSelectAll: 'Seleccionar todo',
    salesGeneratorSelectedCount: '{{selected}} de {{total}} seleccionados',
    salesGeneratorBtnDiscard: 'Descartar',
    salesGeneratorBtnAddToPipeline: 'Agregar {{count}} al pipeline',
    salesGeneratorErrorNoLeads: 'Sin leads seleccionados',
    salesGeneratorToastLeadsAdded: '¡{{count}} leads añadidos al pipeline!',
    salesGeneratorErrorAddFailed: 'Error al agregar leads',
    salesGeneratorErrorGenerateFailed: 'Error al generar leads',
    salesGeneratorStreamEndedEarly: 'Se encontraron {{count}} leads (flujo terminado antes).',
    salesGeneratorNoResults: 'La búsqueda terminó sin resultados.',
    salesGeneratorFoundSummary: 'Se encontraron {{count}} leads.'
  },
  
  pt: {
    // Header
    systemName: 'QUAZI',
    systemTagline: 'Supervisão Financeira com IA',
    logout: 'Sair',
    
    // Upload Section
    uploadTitle: 'Análise Forense Financeira',
    uploadSubtitle: 'Arraste um relatório financeiro em PDF aqui ou clique para selecionar',
    uploadSubtitleMultiple: 'Arraste PDFs financeiros aqui ou clique para selecionar vários arquivos',
    uploadButton: 'CARREGAR DOCUMENTO',
    uploadLimit: 'Limite: 32MB por arquivo | Apenas PDF',
    uploadLimitMultiple: 'Limite: 32MB por arquivo | Múltiplos PDFs serão analisados como um relatório combinado',
    uploadInstructions: 'Instruções de Upload',
    uploadInstructionsText: 'Apenas arquivos PDF são aceitos. Documentos suportados: demonstrações de resultados, balanços patrimoniais, demonstrações de renda, relatórios financeiros e documentos de controle gerencial.',
    uploadInstructionsTextMultiple: 'Apenas arquivos PDF são aceitos. Você pode carregar vários arquivos (por exemplo, balanço patrimonial e demonstração de resultados separados) que serão analisados juntos como um relatório abrangente.',
    qualityDisclaimer: '💡 Dica de Qualidade',
    qualityDisclaimerText: 'Melhor qualidade do documento = resultados mais rápidos e precisos. PDFs de alta resolução com texto e números claros produzirão a melhor análise.',
    filesSelected: '{{count}} arquivo(s) selecionado(s)',
    removeFile: 'Remover arquivo',
    addMoreFiles: 'Adicionar Mais Arquivos',
    analyzeFiles: 'Analisar Documentos',
    errorNoFiles: 'Por favor selecione pelo menos um arquivo para analisar',
    
    // Analysis Guidance
    analysisGuidanceLabel: 'Orientação de Análise',
    optional: 'opcional',
    analysisGuidancePlaceholder: 'ex., "Focar em tendências de rentabilidade da divisão de segurança" ou "Comparar desempenho T3 vs T4" ou "Prestar atenção especial aos gastos operacionais e explicar anomalias"...',
    analysisGuidanceHint: 'Adicione instruções específicas para personalizar a análise. Se deixado vazio, QUAZI realizará uma análise padrão abrangente.',
    
    // Errors
    errorPdfOnly: 'Apenas arquivos PDF são aceitos',
    errorFileSize: 'O arquivo excede o limite de 32MB',
    errorAnalysis: 'Erro na Análise',
    errorMessage: 'Erro ao analisar o documento. Verifique se o backend está em execução.',
    retryButton: 'Tentar Novamente',
    
    // Results
    documentAnalyzed: 'Documento analisado:',
    newAnalysis: 'Nova Análise',
    
    // Sections
    executiveSummary: 'RESUMO EXECUTIVO',
    positiveIndicators: 'INDICADORES POSITIVOS',
    criticalAlerts: 'ALERTAS CRÍTICOS',
    questionsForManagement: 'PERGUNTAS PARA A GESTÃO',
    requiredActions: 'AÇÕES NECESSÁRIAS',
    anomaliesDetected: 'ANOMALIAS DETECTADAS',
    
    // Priority levels
    highPriority: '⚡ PRIORIDADE ALTA',
    mediumPriority: '◆ PRIORIDADE MÉDIA',
    lowPriority: '○ PRIORIDADE BAIXA',
    
    // Severity
    critical: 'CRÍTICO',
    high: 'ALTO',
    medium: 'MÉDIO',
    low: 'BAIXO',
    
    // Loading
    analyzing: 'ANALISANDO DOCUMENTO',
    analyzingSubtitle: 'QUAZI AI está realizando análise forense profunda',
    loadingParsing: 'Analisando documentos financeiros...',
    loadingExtracting: 'Extraindo métricas e padrões-chave...',
    loadingAnalyzing: 'Executando algoritmos de análise forense...',
    loadingValidating: 'Validando integridade dos dados...',
    loadingGenerating: 'Gerando insights executivos...',
    loadingFinalizing: 'Finalizando relatório abrangente...',
    
    // Footer
    developedFor: 'Desenvolvido para',
    poweredBy: 'Powered by QUAZI AI Engine',
    
    // Languages
    spanish: 'Español',
    english: 'English',
    portuguese: 'Português',
    
    // Time
    days: 'dias',
    
    // Export
    exportReport: 'Exportar Relatório',
    exportingReport: 'Gerando Relatório...',
    exportPDF: 'Exportar PDF',
    exportingPDF: 'Gerando PDF...',
    exportCSV: 'Exportar Dados (CSV)',
    exporting: 'Exportando...',
    exportProfessionalPDF: 'Relatório Executivo',
    exportingProfessionalPDF: 'Gerando Relatório Executivo...',
    exportError: 'Falha ao exportar PDF. Por favor tente novamente.',
    
    // Authentication
    tagline: 'Supervisão Financeira com IA',
    emailPlaceholder: 'E-mail',
    passwordPlaceholder: 'Senha',
    signIn: 'ENTRAR',
    authenticating: 'AUTENTICANDO...',
    forgotPassword: 'Esqueceu sua Senha?',
    loginFailed: 'Falha no login. Tente novamente.',
    
    // Password Reset
    resetPassword: 'Redefinir Senha',
    enterEmailForReset: 'Digite seu e-mail para receber um código de redefinição de senha',
    sendResetCode: 'ENVIAR CÓDIGO',
    sending: 'ENVIANDO...',
    backToLogin: 'Voltar ao Login',
    resetCodeSent: 'Código de redefinição enviado para seu e-mail',
    failedToSendCode: 'Falha ao enviar código',
    
    // Password Reset - Step 2
    enterCodeAndNewPassword: 'Digite o código de verificação do seu e-mail e sua nova senha',
    verificationCode: 'Código de Verificação',
    newPassword: 'Nova Senha',
    confirmNewPassword: 'Confirmar Nova Senha',
    passwordRequirements: 'Mínimo 8 caracteres, incluindo maiúscula, minúscula, número e caractere especial',
    resetting: 'REDEFININDO...',
    backToEmail: 'Voltar ao E-mail',
    passwordResetSuccess: 'Senha redefinida com sucesso! Redirecionando...',
    failedToResetPassword: 'Falha ao redefinir senha',
    passwordsDoNotMatch: 'As senhas não coincidem',
    passwordTooShort: 'A senha deve ter pelo menos 8 caracteres',
    rememberMe: 'Lembrar-me',
    passwordStrength: 'Força da senha:',
    weak: 'Fraca',
    mediumStrength: 'Média',
    strong: 'Forte',
    
    // New Password (Temp Password Flow)
    setNewPassword: 'Definir Nova Senha',
    tempPasswordMessage: 'Você está fazendo login com uma senha temporária. Defina uma nova senha para continuar.',
    setPassword: 'DEFINIR SENHA',
    settingPassword: 'DEFININDO...',
    failedToSetPassword: 'Falha ao definir nova senha',
    
    // Sidebar & History
    history: 'Histórico',
    recentAnalyses: 'Análises Recentes',
    openHistory: 'Abrir histórico',
    collapseHistory: 'Recolher barra lateral',
    analysisCount: 'análise',
    analysesCount: 'análises',
    completed: 'Concluído',
    
    // Chat
    chatWithQuazi: 'Chat com QUAZI',
    askQuestion: 'Faça uma pergunta...',
    chatEmptyState: 'Pergunte-me qualquer coisa sobre esta análise',
    closeChat: 'Fechar chat',
    
    // Quick Actions
    lastAnalysis: 'Última Análise',
    continueAction: 'Continuar',
    noChange: 'Sem alterações',
    
    // KPI Tooltips
    tooltipTotalAnalyses: 'Número total de documentos que você analisou',
    tooltipThisWeek: 'Análises concluídas nos últimos 7 dias vs semana anterior',
    tooltipAvgDuration: 'Tempo médio para concluir uma análise',
    tooltipChatMessages: 'Total de interações de chat IA em todas as análises',
    
    // Time Ago
    justNow: 'Agora mesmo',
    minutesAgo: 'há {{count}}m',
    hoursAgo: 'há {{count}}h',
    daysAgo: 'há {{count}}d',
    
    // Toast Messages
    chatExported: 'Conversa de chat exportada',
    exportComplete: 'Exportação Concluída',
    
    // Comparison Feature
    compareAnalyses: 'Comparar',
    compareAnalysesTitle: 'Comparar Análises',
    compareDescription: 'Selecione duas análises para comparar lado a lado',
    selectTwoAnalyses: 'Selecione duas análises para comparar',
    selectOneMore: 'Selecione mais uma análise',
    readyToCompare: 'Pronto para comparar!',
    comparisonTitle: 'Comparação de Análises',
    compGrossMargin: 'Margem Bruta',
    compOperatingMargin: 'Margem Operacional',
    compNetMargin: 'Margem Líquida',
    compEbitdaMargin: 'Margem EBITDA',
    compCurrentRatio: 'Índice Corrente',
    compDsoDays: 'DSO (Dias)',
    compPersonnelCosts: 'Custos de Pessoal',
    compTotalRevenue: 'Receita Total',
    marginComparison: 'Comparação de Margens',
    executiveSummaries: 'Resumos Executivos',
    criticalAlertsComparison: 'Alertas Críticos',
    positiveIndicatorsComparison: 'Indicadores Positivos',
    compFlags: 'alertas',
    compDifference: 'diferença',
    noAlerts: 'Sem alertas',
    noIndicators: 'Sem indicadores',
    compMore: 'mais',
    keyPerformanceIndicators: 'Indicadores Chave de Desempenho',
    
    // Analysis Completeness Feature
    analysisCompleteness: 'Completude da Análise',
    completenessExcellent: 'Excelente',
    completenessGood: 'Bom',
    completenessModerate: 'Moderado',
    completenessNeedsWork: 'Precisa Melhorar',
    statementsFound: 'demonstrações',
    metricsCalculated: 'métricas',
    suggestionsAvailable: 'sugestões para melhorar',
    showLess: 'Mostrar menos',
    showDetails: 'Mostrar detalhes',
    missingDocuments: 'Documentos Faltantes',
    incompleteMetrics: 'Métricas Que Não Puderam Ser Calculadas',
    improvementSuggestions: 'Formas de Melhorar Esta Análise',
    completeYourAnalysis: 'Complete Sua Análise - Carregue Mais Documentos',
    excellentAnalysis: 'Excelente! Esta análise é abrangente com todas as métricas principais calculadas.',
    extendAnalysis: 'Estender Análise',
    addMoreDocuments: 'Adicionar Mais Documentos',
    extendingAnalysis: 'Estendendo análise com novos documentos...',
    analysisExtended: 'Análise estendida com sucesso!',
    extendAnalysisSubtitle: 'Carregue documentos adicionais para aprimorar e completar esta análise',
    extendingHint: 'Adicionando documentos à análise existente para resultados mais completos',
    
    // Improve Analysis Modal
    improveAnalysis: 'Melhorar Análise',
    improveAnalysisSubtitle: 'Carregue documentos adicionais para obter uma análise mais completa',
    recommendedUploads: 'Documentos Recomendados para Carregar',
    uploadNewDocuments: 'Carregar Novos Documentos',
    additionalGuidance: 'Orientação Adicional',
    guidancePlaceholder: 'ex., Foco em análise de liquidez, compare com resultados do ano passado...',
    extending: 'Estendendo Análise...',
    complete: 'Completo',
    analyzingDocuments: 'Analisando seus documentos...',
    claudeAssessing: 'Claude está avaliando o que está faltando',
    tryAgain: 'Tentar Novamente',
    analysisComprehensive: 'Sua análise está bastante completa!',
    addMoreDocsForInsights: 'Adicione mais documentos para insights mais profundos.',
    statements: 'Demonstrações',
    metrics: 'Métricas',
    clickToUploadPdf: 'Clique para carregar arquivos PDF',
    filesSelectedCount: '{{count}} arquivo(s) selecionado(s)',
    dropFinancialDocs: 'Arraste seus documentos financeiros aqui',
    incomeStatement: 'Demonstração de Resultados',
    balanceSheet: 'Balanço Patrimonial',
    cashFlowStatement: 'Demonstração de Fluxo de Caixa',
    notesDisclosures: 'Notas e Divulgações',
    
    // Comparison View
    analysisComparison: 'Comparação de Análises',
    company1: 'Empresa 1',
    company2: 'Empresa 2',
    noAlertsFound: 'Sem alertas',
    noIndicatorsFound: 'Sem indicadores',
    moreItems: '+{{count}} mais',
    loading: 'Carregando...',
    searchCompanyYearDoc: 'Pesquisar por empresa, ano ou documento...',
    selected: 'Selecionado:',
    noAnalysesFound: 'Nenhuma análise encontrada',
    uploadDocsToCreate: 'Carregue documentos para criar análises',
    unknownCompany: 'Empresa Desconhecida',

    // Dashboard Welcome
    welcomeToQuazi: 'Bem-vindo ao QUAZI',
    systemDescription: 'Sistema de Análise Forense Financeira',
    goodMorning: 'Bom dia',
    goodAfternoon: 'Boa tarde',
    goodEvening: 'Boa noite',
    youveAnalyzed: 'Você analisou',
    documents: 'documentos',
    document: 'documento',
    
    // Dashboard Stats
    totalAnalyses: 'Análises Totais',
    thisWeek: 'Esta Semana',
    avgDuration: 'Duração Média',
    chatMessages: 'Mensagens de Chat',
    allTime: 'Todo o tempo',
    lastSevenDays: 'Últimos 7 dias',
    perAnalysis: 'Por análise',
    aiInsights: 'Insights IA',
    
    // Dashboard Charts
    analysisActivity: 'Atividade de Análise',
    lastFourteenDays: 'Últimos 14 dias',
    companiesAnalyzed: 'Empresas Analisadas',
    unique: 'únicas',
    recentActivity: 'Atividade Recente',
    latestAnalyses: 'Últimas análises',
    industriesAnalyzed: 'Indústrias Analisadas',
    activityChartEmpty: 'O gráfico de atividade aparecerá após sua primeira análise',
    companyBreakdownEmpty: 'A divisão de empresas aparecerá aqui',
    recentActivityEmpty: 'Suas análises recentes aparecerão aqui',
    
    // Onboarding
    getStartedWithQuazi: 'Comece com QUAZI',
    getStartedSubtitle: 'Siga estes passos simples para desbloquear insights financeiros poderosos impulsionados por IA',
    stepOneTitle: 'Carregar Documentos Financeiros',
    stepOneDesc: 'Arraste arquivos PDF como demonstrações de resultados, balanços ou relatórios anuais',
    stepTwoTitle: 'Análise IA',
    stepTwoDesc: 'A IA do QUAZI extrai métricas-chave, detecta anomalias e gera insights',
    stepThreeTitle: 'Chat e Exportação',
    stepThreeDesc: 'Faça perguntas sobre seus dados e exporte relatórios PDF profissionais',
    uploadFirstDocument: 'Carregue Seu Primeiro Documento',
    
    // Financial Charts
    totalRevenue: 'Receita Total',
    revenuePerEmployee: 'Receita por Funcionário',
    dsodays: 'DSO',
    netMargin: 'Margem Líquida',
    revenueBreakdown: 'Divisão de Receita',
    byServiceLine: 'Por Linha de Serviço',
    byGeography: 'Por Geografia',
    costAllocation: 'Alocação de Custos',
    costVsBenchmark: 'Custos vs Benchmark',
    profitabilityMetrics: 'Métricas de Rentabilidade',
    charts: 'Gráficos',
    viewCharts: 'Ver Gráficos Financeiros',
    financialVisualizations: 'Visualizações Financeiras',
    costStructure: 'Estrutura de Custos',
    trendsOverTime: 'Tendências ao Longo do Tempo',
    noTrendsAvailable: 'Não há dados de séries temporais disponíveis no documento',
    additionalKPIs: 'KPIs Adicionais',
    service: 'Serviço',
    revenue: 'Receita',
    location: 'Localização',
    trend: 'Tendência',
    industryBenchmark: 'Referência da Indústria',
    benchmark: 'Referência',
    metric: 'Métrica',
    aiEstimate: 'Estimativa de IA',
    documentData: 'Dados do Documento',
    benchmarkSource: 'Fonte de Referência',
    value: 'Valor',
    unit: 'Unidade',
    
    // Modules
    moduleFinancialForensics: 'Análise Forense Financeira',
    enabledModules: 'Módulos Habilitados',
    noModulesEnabled: 'Nenhum módulo habilitado ainda. Entre em contato com o suporte.',

    // AML Sidebar
    amlSidebarTitle: 'Conformidade AML',
    amlNavCustomers: 'Clientes',
    amlNavCustomersDesc: 'KYC e registros de clientes',
    amlNavTransactions: 'Transações',
    amlNavTransactionsDesc: 'Histórico de transações',
    amlNavTransactionReport: 'Relatório de Transações',
    amlNavTransactionReportDesc: 'Relatórios e exportação',
    amlNavTransactionPivotReport: 'Relatório Pivô de Transações',
    amlNavTransactionPivotReportDesc: 'Análise agregada',
    amlNavSanctions: 'Triagem OFAC',
    amlNavSanctionsDesc: 'Cotejo com listas de sanções',
    amlNavClients: 'Clientes AML',
    amlNavClientsDesc: 'Gestão de clientes AML',

    // AML triagem de sanções OFAC
    amlColScreening: 'Triagem',
    amlSanctionsTitle: 'Triagem de Sanções OFAC',
    amlSanctionsSubtitle: 'Compare seus clientes com as listas de sanções da OFAC',
    amlSanctionsListSdn: 'Lista SDN',
    amlSanctionsListNonSdn: 'Lista Não-SDN (Consolidada)',
    amlSanctionsListShort_SDN: 'SDN',
    amlSanctionsListShort_NON_SDN: 'Não-SDN',
    amlSanctionsListState_never_run: 'Nunca baixada',
    amlSanctionsListState_running: 'Baixando…',
    amlSanctionsListState_success: 'Atualizada',
    amlSanctionsListState_failed: 'Falha no último download',
    amlSanctionsListPublished: 'Publicada',
    amlSanctionsListEntries: 'Registros',
    amlSanctionsListNames: 'Nomes',
    amlSanctionsListUpdated: 'Última atualização',
    amlSanctionsBtnRefresh: 'Atualizar',
    amlSanctionsBtnRefreshAll: 'Atualizar Listas',
    amlSanctionsBtnRunScreening: 'Executar Triagem',
    amlSanctionsNoListsWarning: 'Baixe as listas da OFAC antes de executar uma triagem.',
    amlScreeningHistoryTitle: 'Histórico de Triagens',
    amlScreeningNoRuns: 'Nenhuma triagem foi executada ainda',
    amlScreeningColDate: 'Data',
    amlScreeningColStatus: 'Status',
    amlScreeningColScreened: 'Analisados',
    amlScreeningColCleared: 'Sem Correspondência',
    amlScreeningColPotentialMatches: 'Correspondências Potenciais',
    amlScreeningColThreshold: 'Limiar',
    amlScreeningColTriggeredBy: 'Iniciado Por',
    amlScreeningRunStatus_running: 'Em andamento',
    amlScreeningRunStatus_success: 'Concluída',
    amlScreeningRunStatus_failed: 'Falhou',
    amlScreeningDetailTitle: 'Resultados da Triagem',
    amlScreeningBtnBack: 'Voltar à triagem',
    amlScreeningThresholdLabel: 'Limiar',
    amlScreeningBtnDownloadPdf: 'Relatório PDF',
    amlScreeningBtnDownloadExcel: 'Relatório Excel',
    amlScreeningListVersions: 'Versões de Lista Usadas',
    amlScreeningSearchPlaceholder: 'Pesquisar clientes…',
    amlScreeningFilterAllResults: 'Todos os resultados',
    amlScreeningFilterAllReviews: 'Todos os estados de revisão',
    amlScreeningColCustomer: 'Cliente',
    amlScreeningColAddress: 'Endereço',
    amlScreeningColResult: 'Resultado',
    amlScreeningColScore: 'Pontuação',
    amlScreeningColMatchedName: 'Nome OFAC Correspondente',
    amlScreeningColList: 'Lista',
    amlScreeningColReview: 'Revisão',
    amlScreeningResultCleared: 'Sem Correspondência',
    amlScreeningResultPotentialMatch: 'Correspondência Potencial',
    amlScreeningResultNotScreened: 'Não analisado',
    amlScreeningReviewPending: 'Pendente',
    amlScreeningReviewFalsePositive: 'Falso positivo',
    amlScreeningReviewConfirmedMatch: 'Correspondência confirmada',
    amlScreeningNoResults: 'Nenhum resultado corresponde a estes filtros',
    amlScreeningAka: 'Vulgo',
    amlScreeningAkaOf: 'Vulgo de',
    amlScreeningReviewPanelTitle: 'Revisar Correspondência Potencial',
    amlScreeningBestScore: 'Melhor pontuação',
    amlScreeningCandidatesTitle: 'Correspondências Candidatas',
    amlScreeningMatchEntityType: 'Tipo de Entidade',
    amlScreeningMatchPrograms: 'Programas de Sanções',
    amlScreeningMatchAddresses: 'Endereços OFAC',
    amlScreeningMatchOfacUid: 'UID da OFAC',
    amlScreeningViewOnOfac: 'Ver na OFAC',
    amlScreeningReviewNotesLabel: 'Notas de revisão',
    amlScreeningReviewNotesPlaceholder: 'Explique a decisão para a trilha de auditoria…',
    amlScreeningBtnMarkFalsePositive: 'Marcar como Falso Positivo',
    amlScreeningBtnConfirmMatch: 'Confirmar Correspondência',
    amlScreeningToastReportDownloaded: 'Relatório baixado',
    amlScreeningToastReportFailed: 'Falha ao baixar o relatório',
    amlScreeningToastReviewSaved: 'Revisão salva',
    amlScreeningToastReviewFailed: 'Falha ao salvar a revisão',

    // AML Columns
    amlColDate: 'Data',
    amlColCustomerLegalName: 'Nome Legal do Cliente',
    amlColLocation: 'Localização',
    amlColInbound: 'Entrada',
    amlColOutbound: 'Saída',
    amlColAmount: 'Valor',
    amlColFeeInbound: 'Taxa de Entrada',
    amlColFeeOutbound: 'Taxa de Saída',
    amlColAmountFee: 'Valor da Taxa',
    amlColInvoiceNumber: 'Número da Fatura',
    amlColInvoiceDate: 'Data da Fatura',
    amlColInvoiceAmount: 'Valor da Fatura',
    amlColStops: 'Paradas',
    amlColPcs: 'PCS',
    amlColNotes: 'Notas',
    amlColRegistrationNumber: 'Número de Registro',
    amlColInitialDate: 'Data Inicial',
    amlColCode: 'Código',
    amlColStatus: 'Status',
    amlColName: 'Nome',
    amlColAddress: 'Endereço',
    amlColPhone: 'Telefone',
    amlColLogo: 'Logo',
    amlColEmail: 'E-mail',
    amlColClient: 'Cliente',
    amlColType: 'Tipo',
    amlColActions: 'Ações',

    // AML Table
    amlTableTotal: 'Total',
    amlTableNoTransactionsFound: 'Nenhuma transação encontrada',
    amlTableNoCustomersFound: 'Nenhum cliente encontrado',
    amlTableNoClientsFound: 'Nenhum cliente AML encontrado',
    amlTableNoUsersFound: 'Nenhum usuário encontrado',

    // AML Filters
    amlFilterTitle: 'Filtros',
    amlFilterApplyFiltersToLoadReport: 'Aplique filtros para carregar o relatório',
    amlFilterApplyFiltersToLoadReportDesc: 'Use os filtros acima para carregar o relatório de transações.',
    amlFilterNoTransactionsFound: 'Nenhuma transação encontrada',
    amlFilterNoTransactionsFoundDesc: 'Tente ajustar o intervalo de datas ou outros filtros.',
    amlFilterPanelTitle: 'Filtro',
    amlFilterCustomerLegalName: 'Nome Legal do Cliente',
    amlFilterLocation: 'Localização',
    amlFilterDateStart: 'Data (Início)',
    amlFilterDateEnd: 'Data (Fim)',
    amlFilterInvoiceDateStart: 'Data da Fatura (Início)',
    amlFilterInvoiceDateEnd: 'Data da Fatura (Fim)',
    amlFilterSearchCustomers: 'Pesquisar clientes...',
    amlFilterSearchLocations: 'Pesquisar localizações...',
    amlFilterNoCustomersFound: 'Nenhum cliente encontrado',
    amlFilterNoLocationsFound: 'Nenhuma localização encontrada',
    amlFilterReset: 'Redefinir',
    amlFilterApply: 'Aplicar',
    amlFilterClear: 'Limpar',

    // AML Placeholders
    amlPlaceholderSearchByCustomerLocation: 'Pesquisar por cliente, localização...',
    amlPlaceholderAllCustomers: 'Todos os clientes',
    amlPlaceholderAllLocations: 'Todas as localizações',
    amlPlaceholderDateFrom: 'Data de',
    amlPlaceholderDateTo: 'Data até',
    amlPlaceholderSelectDateStart: 'Selecionar data (Início)',
    amlPlaceholderSelectDateEnd: 'Selecionar data (Fim)',
    amlPlaceholderSelectInvoiceDateStart: 'Selecionar data da fatura (Início)',
    amlPlaceholderSelectInvoiceDateEnd: 'Selecionar data da fatura (Fim)',
    amlPlaceholderStartDate: 'Data de início',
    amlPlaceholderEndDate: 'Data de fim',

    // AML Buttons
    amlBtnPdf: 'PDF',
    amlBtnExcel: 'Excel',
    amlBtnImport: 'Importar',
    amlBtnAddTransaction: 'Adicionar Transação',
    amlBtnAddCustomer: 'Adicionar Cliente',
    amlBtnAddClient: 'Adicionar Cliente AML',
    amlBtnAddUser: 'Adicionar Usuário',
    amlBtnSaveChanges: 'Salvar Alterações',
    amlBtnCreateCustomer: 'Criar Cliente',
    amlBtnCreateClient: 'Criar Cliente AML',
    amlBtnCreateUser: 'Criar Usuário',
    amlBtnUpdateTransaction: 'Atualizar Transação',
    amlBtnCreateTransaction: 'Criar Transação',
    amlBtnCancel: 'Cancelar',
    amlBtnApply: 'Aplicar',
    amlBtnReset: 'Redefinir',
    amlBtnDelete: 'Excluir',
    amlBtnExportReport: 'Exportar Relatório',

    // AML Pivot
    amlPivotTableNoPivotData: 'Sem dados pivô',
    amlPivotTableNoPivotDataDesc: 'Tente ajustar o intervalo de datas ou o agrupamento.',
    amlPivotColTransactionPeriod: 'Período de Transação',
    amlPivotColInboundSum: 'Soma de Entrada',
    amlPivotColInboundCount: 'Contagem de Entrada',
    amlPivotColOutboundSum: 'Soma de Saída',
    amlPivotColOutboundCount: 'Contagem de Saída',
    amlPivotColInvoiceSum: 'Soma de Faturas',
    amlPivotColInvoiceCount: 'Contagem de Faturas',
    amlPivotColStopsSum: 'Soma de Paradas',
    amlPivotColPcsCount: 'Contagem PCS',
    amlPivotRadioClient: 'Cliente',
    amlPivotRadioMonth: 'Mês',

    // AML Import
    amlImportModalTitleUpload: 'Importar Transações',
    amlImportModalTitleReview: 'Revisar Correspondência de Clientes',
    amlImportModalTitleResult: 'Resultado da Importação',
    amlImportClickToSelectFile: 'Clique para selecionar arquivo .xlsx',
    amlImportSupportedFormat: 'Formato suportado: .xlsx',
    amlImportDownloadSampleTemplate: 'Baixar modelo de exemplo',
    amlImportParsingFile: 'Analisando arquivo e correspondendo clientes...',
    amlImportAiMatchingEnabled: 'Correspondência IA habilitada para nomes não reconhecidos',
    amlImportColCustomer: 'Cliente',
    amlImportColLocation: 'Localização',
    amlImportColStatus: 'Status',
    amlImportColAmlCustomer: 'Cliente AML',
    amlImportColAmlLocation: 'Localização AML',
    amlImportStatTotalRows: 'Total de Linhas',
    amlImportStatClients: 'Entradas',
    amlImportStatMatched: 'Correspondidos',
    amlImportStatAiSuggested: 'Sugeridos por IA',
    amlImportStatUnresolved: 'Não resolvidos',
    amlImportStatSkipped: 'Ignorados',
    amlImportFilterEmpty: 'Nenhuma entrada corresponde a este filtro.',
    amlImportParseWarnings: 'Avisos de Análise',
    amlImportBadgeSkipped: 'Ignorado',
    amlImportBadgeOfacBlocked: 'Bloqueado pela OFAC',
    amlImportOfacSkippedTitle: 'Correspondências OFAC ignoradas',
    amlImportOfacSkippedBody: '{{count}} cliente(s) sinalizados como correspondência potencial OFAC foram ignorados e não serão importados.',
    amlImportOfacUnscreenedTitle: 'Clientes não triados',
    amlImportOfacUnscreenedBody: '{{count}} cliente(s) mapeados ainda não foram verificados nas listas OFAC. Eles ainda serão importados.',
    amlImportBadgeMatched: 'Correspondido',
    amlImportBadgeResolved: 'Resolvido',
    amlImportBadgeUnmatched: 'Sem correspondência',
    amlImportBadgeAi: 'IA',
    amlImportBadgeConfidenceHigh: 'Alta',
    amlImportBadgeConfidenceMedium: 'Média',
    amlImportBadgeConfidenceLow: 'Baixa',
    amlImportRowsNotImported: 'As linhas não serão importadas',
    amlImportResolveAllUnmatched: 'Resolva todos os clientes e localizações, ou ignore entradas antes de importar.',
    amlImportErrors: 'Erros',
    amlImportResultSuccessTitle: 'Importação concluída',
    amlImportResultPartialTitle: 'Importação concluída — algumas linhas foram ignoradas',
    amlImportResultFailedTitle: 'Nenhuma transação foi importada',
    amlImportResultSuccessSummary: '{{count}} nova(s) transação(ões) adicionada(s).',
    amlImportResultPartialSummary:
      '{{imported}} transação(ões) adicionada(s). {{issues}} linha(s) do arquivo não foram importadas — veja a lista abaixo.',
    amlImportResultFailedSummary:
      '{{issues}} linha(s) não puderam ser importadas. Corrija a planilha e tente novamente.',
    amlImportResultRowProblemsHeading: 'Linhas não importadas',
    amlImportResultRowProblemsHint: 'Cada mensagem inclui o número da linha do seu arquivo .xlsx.',
    amlImportParseWarningsHint: 'Cada mensagem inclui o número da linha do seu arquivo .xlsx.',
    amlImportToastImportComplete: '{{imported}} transação(ões) importada(s).',
    amlImportToastImportDuplicates: '{{count}} duplicata(s) importada(s) com uma nota.',
    amlImportToastImportPartial:
      '{{imported}} importada(s); {{issues}} linha(s) com problemas. Veja o resumo nesta janela.',
    amlImportResultAllDuplicatesTitle: 'Duplicatas importadas',
    amlImportResultAllDuplicatesSummary:
      '{{count}} transação(ões) foram adicionadas. Cada uma foi marcada como duplicata em Notas porque já existe (mesmo cliente, localização, data e valores).',
    amlImportToastAllDupes: '{{count}} duplicata(s) importada(s) com uma nota.',
    amlImportSelectCustomer: 'Selecionar cliente...',
    amlImportSelectLocation: 'Selecionar localização...',
    amlImportTooltipRestore: 'Restaurar',
    amlImportTooltipRejectManual: 'Rejeitar — escolher manualmente',
    amlImportTooltipUndoAiMatch: 'Desfazer correspondência IA',
    amlImportTooltipSkipClient: 'Ignorar cliente',
    amlImportTooltipAcceptAiSuggestion: 'Aceitar sugestão IA',
    amlImportTooltipCreateNewCustomer: 'Criar novo cliente',
    amlImportBtnUploadParse: 'Carregar e Analisar',
    amlImportBtnBack: 'Voltar',
    amlImportBtnSaveForLater: 'Salvar para depois',
    amlImportBtnSubmitImport: 'Enviar Importação',
    amlImportBtnClose: 'Fechar',
    amlImportToastDraftSaved: 'Importação salva. Abra Importar novamente para continuar.',
    amlImportErrorDraftSaveFailed: 'Não foi possível salvar esta importação. Tente novamente.',
    amlImportSavedBadge: 'Salvo',
    amlImportDraftBannerTitle: 'Importação salva',
    amlImportDraftBannerBody: '{{fileName}} — salva {{date}}',
    amlImportDraftBannerBodyNoFile: 'Salva {{date}}',
    amlImportBtnResumeDraft: 'Retomar',
    amlImportBtnDiscardDraft: 'Descartar',
    amlImportToastDraftDiscarded: 'Importação salva descartada',
    amlImportResultTransactionsImported: '{{count}} transação(ões) importada(s)',
    amlImportResultDuplicatesSkipped: '{{count}} duplicata(s) importada(s) com uma nota',
    amlImportResultRowsSkippedUnmatched: '{{count}} linha(s) ignorada(s) (sem correspondência)',
    amlImportErrorPleaseSelectXlsx: 'Por favor selecione um arquivo .xlsx',
    amlImportErrorFailedToParseFile: 'Falha ao analisar o arquivo. Verifique o formato.',
    amlImportErrorImportFailed: 'Falha na importação. Tente novamente.',
    amlImportCreateCustomerExistingHint:
      'Este nome legal já existe. Código e data inicial não são necessários — apenas a localização do arquivo será adicionada a esse cliente.',
    amlImportModalTitleAddLocation: 'Adicionar localização',
    amlImportBtnAddLocation: 'Adicionar localização',
    amlImportToastCustomerCreated: 'Cliente "{{name}}" criado',
    amlImportToastLocationAdded: 'Localização adicionada a "{{name}}"',
    amlImportErrorCreateCustomerFailed: 'Não foi possível criar o cliente',

    // AML Create Customer Modal
    amlCreateCustomerModalTitle: 'Criar Cliente',
    amlCreateCustomerLabelInitialDate: 'Data Inicial',
    amlCreateCustomerLabelLocationFromFile: 'Localização (do arquivo)',
    amlCreateCustomerErrorNameRequired: 'O nome é obrigatório',
    amlCreateCustomerErrorValidCodeRequired: 'Um código válido é obrigatório',
    amlCreateCustomerErrorInitialDateRequired: 'A data inicial é obrigatória',

    // AML Transaction Form
    amlTransactionDetailTitle: 'Detalhe da Transação',
    amlTransactionEditTitle: 'Editar Transação',
    amlTransactionAddTitle: 'Adicionar Transação',
    amlTransactionLabelCustomer: 'Cliente',
    amlTransactionLabelLocation: 'Localização',
    amlTransactionLabelTransactionDate: 'Data da Transação',
    amlTransactionLabelAmounts: 'Valores',
    amlTransactionLabelFees: 'Taxas',
    amlTransactionLabelInvoice: 'Fatura',
    amlTransactionLabelAmountCollectedInOut: 'Valor Coletado (Entrada/Saída)',
    amlTransactionLabelFeeCollectedInOut: 'Taxa Coletada (Entrada/Saída)',
    amlTransactionLabelSelectCustomer: 'Selecionar cliente',
    amlTransactionLabelSelectLocation: 'Selecionar localização',
    amlTransactionLabelLoading: 'Carregando...',
    amlTransactionLabelSelectDate: 'Selecionar data',
    amlTransactionLabelCollected: 'Coletado',
    amlTransactionLabelFeeCollected: 'Taxa Coletada',
    amlTransactionLabelInvoiceHash: 'Fatura #',
    amlTransactionToastSaved: 'Salvo',
    amlTransactionToastUpdatedSuccessfully: 'Transação atualizada com sucesso',
    amlTransactionToastCreatedSuccessfully: 'Transação criada com sucesso',
    amlTransactionToastDeleted: 'Transação excluída',
    amlTransactionToastFailedToDelete: 'Falha ao excluir a transação',
    amlTransactionToastFailedToUpdate: 'Falha ao atualizar a transação',
    amlTransactionToastFailedToCreate: 'Falha ao criar a transação',
    amlTransactionOfacBlockedTitle: 'Correspondência potencial OFAC',
    amlTransactionOfacBlockedBody: 'Este cliente está sinalizado na lista de triagem OFAC. Crie uma transação somente depois que um revisor marcar o hit como falso positivo.',
    amlTransactionOfacUnscreenedTitle: 'Cliente não triado',
    amlTransactionOfacUnscreenedBody: 'Este cliente ainda não foi verificado nas listas OFAC. Você ainda pode criar a transação, mas execute uma triagem o quanto antes.',
    amlFilterRemoveName: 'Remover {{name}}',
    amlTransactionTooltipFullEdit: 'Edição completa',
    amlTransactionTooltipView: 'Ver',
    amlTransactionTooltipDelete: 'Excluir',
    amlTransactionAriaLabelEdit: 'Editar transação',
    amlTransactionAriaLabelView: 'Ver transação',
    amlTransactionAriaLabelDelete: 'Excluir transação',
    amlTransactionDeleteTitle: 'Excluir Transação',
    amlTransactionDeleteMessage: 'Remover esta transação de relatórios e listas? Ela pode ser restaurada depois se necessário.',
    amlTransactionDeleteConfirm: 'Excluir',
    amlTransactionBulkDelete: 'Excluir selecionadas',
    amlTransactionBulkSelected: '{{count}} selecionadas',
    amlTransactionBulkDeleteTitle: 'Excluir Transações',
    amlTransactionBulkDeleteMessage: 'Remover {{count}} transação(ões) de relatórios e listas? Elas podem ser restauradas depois se necessário.',
    amlTransactionToastBulkDeleted: '{{count}} transação(ões) excluída(s)',
    amlTransactionAriaLabelSelect: 'Selecionar transação',
    amlTransactionAriaLabelSelectAll: 'Selecionar todas nesta página',
    amlTransactionAriaLabelClearSelection: 'Limpar seleção',

    // AML Customer Form
    amlCustomerFormTitleEdit: 'Editar Cliente',
    amlCustomerFormTitleCreate: 'Adicionar Cliente',
    amlCustomerFieldCustomerLegalName: 'Nome Legal do Cliente',
    amlCustomerFieldCode: 'Código',
    amlCustomerFieldRegistrationNumber: 'Número de Registro',
    amlCustomerFieldEinFein: 'EIN/FEIN',
    amlCustomerFieldInitialDate: 'Data Inicial',
    amlCustomerFieldDateCompanyEstablished: 'Data de Constituição da Empresa',
    amlCustomerFieldCompanyLegalRepresentative: 'Representante Legal da Empresa',
    amlCustomerFieldMainAddress: 'Endereço Principal',
    amlCustomerFieldServicesAndProduct: 'Serviços e/ou Produto',
    amlCustomerFieldTypeOfBusinessActivity: 'Tipo de Atividade Comercial',
    amlCustomerFieldNotesAmlRev: 'Notas AML REV',
    amlCustomerFieldContact: 'Contato',
    amlCustomerFieldEmail: 'E-mail',
    amlCustomerFieldPhone: 'Telefone',
    amlCustomerFieldLocationsDetail: 'Detalhe de localizações',
    amlCustomerFieldStatus: 'Status',
    amlCustomerStatusActive: 'Ativo',
    amlCustomerStatusInactive: 'Inativo',
    amlCustomerNotApplicable: 'N/D',
    amlCustomerAriaLabelActions: 'Ações',
    amlCustomerPlaceholderLegalName: 'Insira o nome legal do cliente',
    amlCustomerPlaceholderCode: 'Insira o código',
    amlCustomerPlaceholderRegistrationNumber: 'Insira o número de registro',
    amlCustomerPlaceholderEinFein: 'Insira o EIN/FEIN',
    amlCustomerPlaceholderSelectInitialDate: 'Selecionar data inicial',
    amlCustomerPlaceholderSelectDate: 'Selecionar data...',
    amlCustomerPlaceholderCompanyLegalRep: 'Insira o representante legal',
    amlCustomerPlaceholderMainAddress: 'Insira o endereço principal',
    amlCustomerPlaceholderServicesProduct: 'Insira serviços e/ou produto',
    amlCustomerPlaceholderTypeOfActivity: 'Insira o tipo de atividade comercial',
    amlCustomerPlaceholderNotesAmlRev: 'Insira notas AML REV',
    amlCustomerPlaceholderContact: 'Insira o contato',
    amlCustomerPlaceholderEmailAddress: 'Insira o endereço de e-mail',
    amlCustomerPlaceholderEnterPhone: 'Insira o telefone',
    amlCustomerPlaceholderLocationName: 'Insira o nome da localização',
    amlCustomerValidationNameRequired: 'O nome legal do cliente é obrigatório',
    amlCustomerValidationDuplicateLegalName:
      'Já existe um cliente com este nome legal. Escolha outro nome.',
    amlCustomerValidationCodeRequired: 'O código é obrigatório',
    amlCustomerValidationCodeMustBeNumber: 'O código deve ser um número válido',
    amlCustomerValidationInitialDateRequired: 'A data inicial é obrigatória',
    amlCustomerValidationDateCannotBePast: 'A data não pode ser no passado',
    amlCustomerValidationDateCannotBeFuture: 'A data não pode ser no futuro',
    amlCustomerValidationInvalidDate: 'Data inválida',
    amlCustomerValidationMustBeAtLeast3Chars: 'Deve ter pelo menos 3 caracteres',
    amlCustomerValidationOnlyLettersAllowed: 'Apenas letras são permitidas',
    amlCustomerValidationOnlyLettersNumbers: 'Apenas letras e números são permitidos',
    amlCustomerValidationValidEmail: 'Por favor insira um e-mail válido',
    amlCustomerValidationAllLocationNamesRequired: 'Todos os nomes de localização são obrigatórios',
    amlCustomerToastUpdated: 'Cliente atualizado com sucesso',
    amlCustomerToastCreated: 'Cliente criado — screening OFAC iniciado',
    amlCustomerToastDeleted: '"{{name}}" excluído com sucesso',
    amlCustomerToastFailedToDelete: 'Falha ao excluir o cliente',
    amlCustomerPanelTitleDetail: 'Detalhe do Cliente',
    amlCustomerPanelTitleKyc: 'Relatório KYC',
    amlCustomerBtnAdd: 'Adicionar',
    amlCustomerBtnSaveChanges: 'Salvar Alterações',
    amlCustomerBtnCreateCustomer: 'Criar Cliente',
    amlCustomerSearchPlaceholder: 'Pesquisar por nome, código, número de registro...',
    amlCustomerDropdownDetail: 'Detalhe',
    amlCustomerDropdownKycReport: 'Relatório KYC',
    amlCustomerDropdownEdit: 'Editar',
    amlCustomerDropdownDelete: 'Excluir',
    amlCustomerDeleteTitle: 'Excluir cliente?',
    amlCustomerDeleteMessage: 'Ocultar "{{name}}" e suas transações de listas e relatórios? O histórico é mantido e pode ser restaurado depois se necessário.',
    amlCustomerDeleteConfirm: 'Excluir',
    amlCustomerDeleteCancel: 'Cancelar',

    // AML KYC
    amlKycTableHeaderDataReviewedByCO: 'Dados revisados pelo C.O',
    amlKycTableHeaderData: 'Dados',
    amlKycTableHeaderAdditionalNotes: 'Notas Adicionais',
    amlKycFieldInitialDate: 'Data Inicial',
    amlKycFieldCode: 'Código',
    amlKycFieldCustomerLegalName: 'Nome Legal do Cliente',
    amlKycFieldEinFein: 'EIN / FEIN',
    amlKycFieldRegistrationNumber: 'Número de Registro',
    amlKycFieldDateCompanyEstablished: 'Data de Constituição',
    amlKycFieldCompanyLegalRepresentative: 'Representante Legal',
    amlKycFieldMainAddress: 'Endereço Principal',
    amlKycFieldLocationsDetail: 'Detalhe de Localizações',
    amlKycFieldServicesProduct: 'Serviços e/ou Produto',
    amlKycFieldTypeOfBusinessActivity: 'Tipo de Atividade Comercial',
    amlKycFieldContact: 'Contato',
    amlKycFieldEmail: 'E-mail',
    amlKycFieldPhone: 'Telefone',
    amlKycDateReviewed: 'Data de revisão:',
    amlKycBtnExportReport: 'Exportar Relatório',
    amlCustomerDetailLocationsDetail: 'Detalhe de localizações',

    // AML Client Form
    amlClientFormTitleEdit: 'Editar Cliente AML',
    amlClientFormTitleCreate: 'Adicionar Cliente AML',
    amlClientFieldClientLogo: 'Logo do Cliente',
    amlClientFieldClientName: 'Nome do Cliente',
    amlClientFieldAddress: 'Endereço',
    amlClientFieldPhone: 'Telefone',
    amlClientPlaceholderCompanyName: 'Nome da empresa',
    amlClientPlaceholderBusinessAddress: 'Endereço comercial',
    amlClientPlaceholderPhoneNumber: 'Número de telefone',
    amlClientBtnChangeImage: 'Alterar Imagem',
    amlClientBtnUploadImage: 'Carregar Imagem',
    amlClientBtnCancelUpload: 'Cancelar Upload',
    amlClientBtnRemoveImage: 'Remover Imagem',
    amlClientImageFormats: 'JPG, PNG, GIF, WebP ou SVG',
    amlClientBtnSaveChanges: 'Salvar Alterações',
    amlClientBtnCreateClient: 'Criar Cliente AML',
    amlClientToastUpdated: 'Cliente atualizado com sucesso',
    amlClientToastCreated: 'Cliente criado com sucesso',
    amlClientToastFailedToUpdate: 'Falha ao atualizar o cliente',
    amlClientToastFailedToCreate: 'Falha ao criar o cliente',
    amlClientToastLogoUploadFailed: 'Cliente salvo mas falhou o upload do logo',
    amlClientToastCreatedLogoFailed: 'Cliente criado mas falhou o upload do logo',
    amlClientSearchPlaceholder: 'Pesquisar clientes...',
    amlClientDeleteTitle: 'Excluir Cliente AML',
    amlClientDeleteMessage: 'Tem certeza que deseja excluir "{{name}}"? Esta ação não pode ser desfeita.',
    amlClientDeleteConfirm: 'Excluir',
    amlClientShowingResults: 'Mostrando {{from}}–{{to}} de {{total}}',

    // AML User Management
    amlUserListSearchPlaceholder: 'Pesquisar por nome, e-mail, cliente...',
    amlUserRoleSuperAdmin: 'Super Administrador',
    amlUserRoleClientAdmin: 'Administrador do Cliente',
    amlUserRoleOperator: 'Operador',
    amlUserRoleReviewer: 'Revisor',
    amlUserRoleUser: 'Usuário',
    amlUserRolePlatformAdmin: 'Administrador da Plataforma',
    amlUserStatusActive: 'Ativo',
    amlUserStatusInvited: 'Convidado',
    amlUserStatusInactive: 'Inativo',
    amlUserMenuLogin: 'Entrar',
    amlUserMenuDetail: 'Detalhe',
    amlUserMenuEdit: 'Editar',
    amlUserMenuResetPassword: 'Redefinir Senha',
    amlUserMenuActivate: 'Ativar',
    amlUserMenuDelete: 'Excluir',
    amlUserLoginAsTitle: 'Entrar como Usuário',
    amlUserLoginAsMessage: 'Você está prestes a entrar como {{name}}. Sua sessão atual será salva e você pode restaurá-la fazendo logout.',
    amlUserBtnLogin: 'Entrar',
    amlUserDeleteTitle: 'Excluir Usuário',
    amlUserDeleteMessage: 'Tem certeza que deseja excluir permanentemente "{{name}}"? Esta ação não pode ser desfeita.',
    amlUserActivateTitle: 'Ativar Usuário',
    amlUserActivateMessage: 'Tem certeza que deseja reativar "{{name}}"? Eles recuperarão o acesso ao sistema.',
    amlUserActivateConfirm: 'Ativar',
    amlUserToastDeleted: 'Usuário "{{name}}" excluído.',
    amlUserToastActivated: 'Usuário "{{name}}" ativado.',
    amlUserToastFailedToDelete: 'Falha ao excluir o usuário.',
    amlUserToastFailedToActivate: 'Falha ao ativar o usuário.',
    amlUserDetailTitle: 'Detalhe do Usuário',
    amlUserDetailFieldStatus: 'Status',
    amlUserDetailFieldRole: 'Função',
    amlUserDetailFieldClient: 'Cliente',
    amlUserDetailFieldCreated: 'Criado',
    amlUserFormTitleEdit: 'Editar Usuário',
    amlUserFormTitleCreate: 'Adicionar Usuário',
    amlUserFormFieldEmail: 'E-mail',
    amlUserFormFieldFirstName: 'Nome',
    amlUserFormFieldLastName: 'Sobrenome',
    amlUserFormFieldPassword: 'Senha',
    amlUserFormFieldConfirmPassword: 'Confirmar Senha',
    amlUserFormFieldClient: 'Cliente',
    amlUserFormFieldType: 'Tipo',
    amlUserFormFieldActive: 'Ativo',
    amlUserFormPlaceholderEmail: 'usuario@exemplo.com',
    amlUserFormPlaceholderFirstName: 'Insira o nome',
    amlUserFormPlaceholderLastName: 'Insira o sobrenome',
    amlUserFormPlaceholderPassword: 'Insira a senha',
    amlUserFormPlaceholderConfirmPassword: 'Confirme a senha',
    amlUserFormPlaceholderSelectClient: 'Selecionar cliente',
    amlUserFormValidationEmailRequired: 'O e-mail é obrigatório',
    amlUserFormValidationValidEmail: 'Insira um endereço de e-mail válido',
    amlUserFormValidationFirstNameRequired: 'O nome é obrigatório',
    amlUserFormValidationLastNameRequired: 'O sobrenome é obrigatório',
    amlUserFormValidationClientRequired: 'O cliente é obrigatório',
    amlUserFormValidationPasswordRequired: 'A senha é obrigatória',
    amlUserFormValidationPasswordMinLength: 'A senha deve ter pelo menos {{count}} caracteres',
    amlUserFormValidationPasswordUppercase: 'A senha deve incluir uma letra maiúscula',
    amlUserFormValidationPasswordLowercase: 'A senha deve incluir uma letra minúscula',
    amlUserFormValidationPasswordNumber: 'A senha deve incluir um número',
    amlUserFormValidationPasswordSpecial: 'A senha deve incluir um caractere especial',
    amlUserFormValidationPasswordConfirmRequired: 'A confirmação de senha é obrigatória',
    amlUserFormValidationPasswordsDoNotMatch: 'As senhas não coincidem',
    amlUserFormValidationEnterNewPasswordFirst: 'Insira a nova senha antes de confirmar',
    amlUserFormErrorEmailAlreadyExists: 'Já existe um usuário com este e-mail',
    amlUserFormErrorUserNotFound: 'Usuário não encontrado — pode ter sido excluído',
    amlUserFormErrorNoPermission: 'Você não tem permissão para realizar esta ação',
    amlUserFormErrorInvalidData: 'Dados inválidos — verifique suas entradas',
    amlUserFormErrorNetworkError: 'Erro de rede — verifique sua conexão e tente novamente',
    amlUserFormBtnSaveChanges: 'Salvar Alterações',
    amlUserFormBtnCreateUser: 'Criar Usuário',
    amlUserToastUserUpdated: 'Usuário atualizado com sucesso',
    amlUserToastUserCreated: 'Usuário criado com sucesso',
    amlUserFormShowingOf: 'Mostrando {{from}}–{{to}} de {{total}}',

    // AML Reset Password
    amlResetPasswordTitle: 'Redefinir Senha',
    amlResetPasswordFieldNewPassword: 'Nova Senha',
    amlResetPasswordFieldConfirmPassword: 'Confirmar Senha',
    amlResetPasswordPlaceholderNew: 'Insira a nova senha',
    amlResetPasswordPlaceholderConfirm: 'Confirme a nova senha',
    amlResetPasswordRuleAtLeast8Chars: 'Pelo menos 8 caracteres',
    amlResetPasswordRuleUppercase: 'Letra maiúscula',
    amlResetPasswordRuleLowercase: 'Letra minúscula',
    amlResetPasswordRuleNumber: 'Número',
    amlResetPasswordRuleSpecialChar: 'Caractere especial',
    amlResetPasswordValidationRequired: 'A senha é obrigatória',
    amlResetPasswordValidationMinLength: 'A senha deve ter pelo menos {{count}} caracteres',
    amlResetPasswordValidationDoesNotMeetRequirements: 'A senha não atende todos os requisitos',
    amlResetPasswordValidationConfirmRequired: 'Por favor confirme a senha',
    amlResetPasswordValidationDoNotMatch: 'As senhas não coincidem',
    amlResetPasswordBtnSetPassword: 'Definir Senha',
    amlResetPasswordToastSuccess: 'Senha atualizada com sucesso',

    // AML Shared
    amlPageSizeShow: 'Mostrar',
    amlPageSizePerPage: 'por página',
    amlEmptyStateApplyFiltersToLoadData: 'Aplique filtros para carregar dados',
    amlEmptyStateApplyFiltersDesc: 'Use os filtros acima para carregar o relatório.',
    amlEmptyStateNoResultsFound: 'Nenhum resultado encontrado',
    amlEmptyStateNoResultsDesc: 'Tente ajustar sua pesquisa ou filtros.',
    amlInlineCellPlaceholderEnter: 'Inserir…',
    amlUserLoginFailedError: 'Falha ao entrar como usuário.',

    // Performance Sidebar
    perfSidebarTitle: 'Desempenho',
    perfNavMyKpis: 'Meus KPIs',
    perfNavMyKpisDesc: 'Painel pessoal',
    perfNavTeam: 'Equipe',
    perfNavTeamDesc: 'KPIs de subordinados diretos',
    perfNavOrganization: 'Organização',
    perfNavOrganizationDesc: 'Visão geral da organização',
    perfNavKpiCatalog: 'Catálogo de KPIs',
    perfNavKpiCatalogDesc: 'Modelos e definições',
    perfNavCycles: 'Ciclos',
    perfNavCyclesDesc: 'Ciclos de desempenho',

    // Performance Header
    perfHeaderTitle: 'Desempenho',
    perfHeaderSubtitleDefault: 'Rastreamento de KPIs e insights',
    perfBtnAssignKpi: 'Atribuir KPI',

    // Performance My KPIs
    perfMyKpisGetStartedTitle: 'Comece com o Rastreamento de Desempenho',
    perfMyKpisGetStartedDesc: 'Para rastrear KPIs, você primeiro precisa criar um ciclo de desempenho — um período de tempo como um trimestre ou semestre durante o qual os KPIs são rastreados. Isso permite comparar o desempenho entre períodos e manter os objetivos focados.',
    perfMyKpisCreateFirstCycle: 'Crie Seu Primeiro Ciclo',
    perfMyKpisAskAdminToCreateCycle: 'Peça ao seu administrador para criar um ciclo de desempenho na aba Ciclos.',
    perfMyKpisNoActiveCycleTitle: 'Nenhum Ciclo Ativo Selecionado',
    perfMyKpisNoActiveCycleDesc: 'Selecione um ciclo no menu suspenso do cabeçalho, ou ative um dos seus ciclos rascunho na aba Ciclos.',
    perfMyKpisOverallScore: 'Pontuação Geral',
    perfMyKpisSummaryOnTrack: 'No caminho',
    perfMyKpisSummaryAtRisk: 'Em risco',
    perfMyKpisSummaryBehind: 'Atrasado',
    perfMyKpisSummaryNotStarted: 'Não iniciado',
    perfMyKpisSummaryCompleted: 'Concluído',
    perfMyKpisNoKpisAssigned: 'Nenhum KPI atribuído para este ciclo',
    perfMyKpisNoKpisDesc: 'Navegue pelo Catálogo de KPIs para atribuir KPIs, ou seu gerente os definirá para você.',

    // Performance Team
    perfTeamNoDirectReportsTitle: 'Sem Subordinados Diretos',
    perfTeamNoDirectReportsDesc: 'Esta aba mostra o progresso de KPIs das pessoas que se reportam a você. Atualmente, ninguém na organização tem o campo "Reporta a" configurado para você.',
    perfTeamSetupInstructions: 'Para configurar sua equipe:',
    perfTeamSetupStep1: 'Vá para o módulo de Organização (ícone de engrenagem, barra lateral)',
    perfTeamSetupStep2: 'Abra o perfil de um membro da equipe na aba Pessoas',
    perfTeamSetupStep3: 'Defina o campo "Reporta a" deles como você mesmo',
    perfTeamMemberCount: '{{count}} membro(s) da equipe',
    perfTeamOnTrack: '{{count}} no caminho',
    perfTeamAtRisk: '{{count}} em risco',
    perfTeamBehind: '{{count}} atrasado(s)',
    perfTeamBtnAiSummary: 'Resumo IA',
    perfTeamAiSummaryGenerating: 'Gerando...',
    perfTeamAiSummaryTitle: 'Resumo IA da Equipe',
    perfTeamNoKpisAssigned: 'Nenhum KPI atribuído',

    // Performance Organization
    perfOrgNoActiveCycleTitle: 'Sem Ciclo Ativo',
    perfOrgNoActiveCycleDesc: 'Não há ciclo de desempenho ativo. Crie um para ver os dados de KPI de toda a organização.',
    perfOrgSummaryTotalKpis: 'Total de KPIs',
    perfOrgSummaryOnTrack: 'No caminho',
    perfOrgSummaryAtRisk: 'Em risco',
    perfOrgSummaryBehind: 'Atrasado',
    perfOrgSummaryNotStarted: 'Não iniciado',
    perfOrgSummaryOverallScore: 'Pontuação Geral',
    perfOrgByDepartment: 'Por Departamento',
    perfOrgAllMembers: 'Todos os Membros ({{count}})',
    perfOrgMemberKpiCount: '{{count}} KPIs',
    perfOrgNoMembersWithKpis: 'Nenhum membro com KPIs neste departamento.',

    // Performance KPI Detail
    perfKpiDetailTabProgress: 'Progresso',
    perfKpiDetailTabComments: 'Comentários',
    perfKpiDetailTabReviews: 'Revisões',
    perfKpiDetailCheckIn: 'Registrar',
    perfKpiDetailAssignedTo: 'Atribuído a {{name}}',
    perfKpiDetailAiInsight: 'Insight IA',
    perfKpiDetailCheckInHistory: 'Histórico de Registros',
    perfKpiDetailNoCheckIns: 'Ainda sem registros',
    perfKpiDetailNoComments: 'Ainda sem comentários. Inicie a conversa.',
    perfKpiDetailNoReviews: 'Ainda sem revisões. As revisões são enviadas durante a fase de revisão.',
    perfKpiDetailAddComment: 'Adicionar um comentário...',
    perfKpiDetailOverdue: '({{count}} atrasado(s))',
    perfKpiDetailFinancialImpact: 'Impacto financeiro: ${{value}}',
    perfKpiDetailStrengths: 'Pontos fortes: {{text}}',
    perfKpiDetailImprovements: 'Áreas a melhorar: {{text}}',
    perfKpiDetailDeleteKpiQuestion: 'Excluir este KPI e todos os seus dados?',
    perfKpiDetailArchiveKpiQuestion: 'Arquivar este KPI?',
    perfKpiDetailBtnDelete: 'Sim, excluir',
    perfKpiDetailBtnArchive: 'Sim, arquivar',
    perfKpiDetailBtnCancel: 'Cancelar',
    perfKpiDetailAriaLabelClose: 'Fechar painel',
    perfKpiDetailAriaLabelArchive: 'Arquivar este KPI',
    perfKpiDetailAriaLabelDelete: 'Excluir este KPI',
    perfKpiDetailAriaLabelSendComment: 'Enviar comentário',
    perfKpiDetailRevisionFeedbackPlaceholder: 'Feedback de revisão...',
    perfKpiDetailBtnVerify: 'Verificar',
    perfKpiDetailBtnRequestRevision: 'Solicitar Revisão',
    perfKpiDetailBtnSend: 'Enviar',
    perfKpiDetailStatusSubmitted: 'Enviado',
    perfKpiDetailStatusVerified: 'Verificado',
    perfKpiDetailStatusNeedsRevision: 'Precisa de Revisão',
    perfKpiDetailReviewNote: 'Nota de revisão: {{text}}',

    // Performance KPI Card
    perfCardNoCheckInsYet: 'Ainda sem registros',
    perfCardCheckIn: 'Registrar',
    perfCardUnnamedKpi: 'KPI sem nome',

    // Performance KPI Status
    perfKpiStatusOnTrack: 'No caminho',
    perfKpiStatusAtRisk: 'Em risco',
    perfKpiStatusBehind: 'Atrasado',
    perfKpiStatusNotStarted: 'Não iniciado',
    perfKpiStatusCompleted: 'Concluído',
    perfKpiStatusPending: 'Pendente',
    perfKpiStatusMissed: 'Perdido',
    perfKpiStatusCancelled: 'Cancelado',

    // Performance Check-in Form
    perfCheckInFormTitle: 'Registrar',
    perfCheckInBtnCancel: 'Cancelar',
    perfCheckInFieldCurrentValue: 'Valor Atual',
    perfCheckInFieldFinancialImpact: 'Impacto Financeiro (este período)',
    perfCheckInFieldNotes: 'Notas (opcional)',
    perfCheckInFieldEvidence: 'Evidência (opcional)',
    perfCheckInNotesPlaceholder: 'O que impulsionou este número? Contexto para seu gerente...',
    perfCheckInAttachFiles: 'Anexar arquivos (PDF, imagens, planilhas)',
    perfCheckInUploading: 'Carregando...',
    perfCheckInFileTooLarge: 'O arquivo "{{name}}" excede o limite de 50MB',
    perfCheckInErrorFailedToSubmit: 'Falha ao enviar o registro',
    perfCheckInBtnSubmit: 'Enviar Registro',

    // Performance Assign KPI
    perfAssignKpiTitleTemplate: 'Atribuir: {{name}}',
    perfAssignKpiTitleCustom: 'Criar e Atribuir KPI',
    perfAssignKpiStep1: '1. Definir',
    perfAssignKpiStep2: '2. Atribuir',
    perfAssignKpiFieldKpiName: 'Nome do KPI',
    perfAssignKpiFieldDescription: 'Descrição',
    perfAssignKpiSectionMeasurement: 'Medição',
    perfAssignKpiFieldTarget: 'Meta',
    perfAssignKpiFieldBaseline: 'Linha de Base',
    perfAssignKpiFieldWeight: 'Peso',
    perfAssignKpiFieldUnitLabel: 'Rótulo da Unidade',
    perfAssignKpiSectionCheckInFrequency: 'Frequência de Registro',
    perfAssignKpiCheckInFrequencyDesc: 'Com que frequência o responsável deve reportar o progresso. Este não é o ciclo — o ciclo define o período geral (ex. T1 2026), enquanto a cadência define quantos registros ocorrem dentro dele.',
    perfAssignKpiFieldFormula: 'Fórmula / Método de Cálculo',
    perfAssignKpiSectionVerifier: 'Verificador de Registros',
    perfAssignKpiVerifierOptional: 'Opcional',
    perfAssignKpiVerifierPlaceholder: 'Escolha quem valida os registros (ex. Finanças, um gerente)...',
    perfAssignKpiSearchVerifier: 'Pesquisar verificador...',
    perfAssignKpiTrackFinancialImpact: 'Rastrear Impacto Financeiro',
    perfAssignKpiFinancialImpactDescOn: 'Defina o valor monetário esperado que este KPI irá gerar ou economizar',
    perfAssignKpiFinancialImpactDescOff: 'Opcional — vincule este KPI a uma meta de receita ou economia',
    perfAssignKpiFieldExpectedFinancialImpact: 'Impacto Financeiro Esperado ($)',
    perfAssignKpiFinancialImpactNote: 'Este valor será rastreado contra o impacto real reportado em cada registro, fornecendo uma visão contínua do ROI.',
    perfAssignKpiFromTemplate: 'Do modelo:',
    perfAssignKpiSearchTeamMembers: 'Pesquisar membros da equipe...',
    perfAssignKpiSelectedCount: '{{count}} selecionado(s)',
    perfAssignKpiAvailableCount: '{{count}} disponível(is)',
    perfAssignKpiBtnCancel: 'Cancelar',
    perfAssignKpiBtnNextPickPeople: 'Próximo: Escolher Pessoas',
    perfAssignKpiBtnAssign: 'Atribuir KPI a {{count}} pessoa(s)',
    perfAssignKpiErrorFailed: 'Falha ao atribuir KPI',
    perfAssignKpiKpiNamePlaceholder: 'ex., Taxa de Crescimento de Receita, Criação de Valor Líquido...',
    perfAssignKpiDescriptionPlaceholder: 'O que este KPI mede e por que é importante?',
    perfAssignKpiFormulaPlaceholder: 'Como este KPI é calculado? O que inclui/exclui?',
    perfAssignKpiUnitLabelPlaceholder: 'ex., entregas, departamentos, marcos...',
    perfAssignKpiFinancialImpactPlaceholder: 'ex., 50 000',

    // Performance Units
    perfUnitCurrency: 'Moeda ($)',
    perfUnitPercentage: 'Porcentagem (%)',
    perfUnitCount: 'Quantidade',
    perfUnitScore: 'Pontuação',
    perfUnitHours: 'Horas',
    perfUnitRatio: 'Proporção',
    perfUnitCustom: 'Personalizado',

    // Performance Categories
    perfCategoryFinancial: 'Financeiro',
    perfCategorySales: 'Vendas',
    perfCategoryMarketing: 'Marketing',
    perfCategoryOperations: 'Operações',
    perfCategoryProduct: 'Produto',
    perfCategoryHrPeople: 'RH e Pessoas',
    perfCategoryCustomerSuccess: 'Sucesso do Cliente',
    perfCategoryCustom: 'Personalizado',

    // Performance Cadences
    perfCadenceWeekly: 'Semanal',
    perfCadenceBiweekly: 'Quinzenal',
    perfCadenceMonthly: 'Mensal',
    perfCadenceQuarterly: 'Trimestral',

    // Performance Directionality
    perfDirectionalityHigherBetter: 'Mais alto é melhor',
    perfDirectionalityLowerBetter: 'Mais baixo é melhor',
    perfDirectionalityHitExact: 'Atingir meta exata',

    // Performance Cycle Modal
    perfCycleModalTitle: 'Novo Ciclo de Desempenho',
    perfCycleFieldCycleType: 'Tipo de Ciclo',
    perfCycleFieldCycleName: 'Nome do Ciclo',
    perfCycleFieldStartDate: 'Data de Início',
    perfCycleFieldEndDate: 'Data de Fim',
    perfCyclePlaceholderName: 'ex., Marketing, Engenharia, Empresa...',
    perfCyclePlaceholderStartDate: 'Escolha a data de início',
    perfCyclePlaceholderEndDate: 'Escolha a data de fim',
    perfCycleWillDisplayAs: 'Será exibido como:',
    perfCycleAutoAdvance: 'Avanço automático para o próximo ciclo',
    perfCycleAutoAdvanceDesc: 'Quando este ciclo fechar, criar automaticamente o próximo período e transferir os KPIs ativos',
    perfCycleValidationFillAllFields: 'Por favor preencha todos os campos',
    perfCycleValidationEndDateAfterStart: 'A data de fim deve ser após a data de início',
    perfCycleErrorFailedToCreate: 'Falha ao criar o ciclo',
    perfCycleBtnCancel: 'Cancelar',
    perfCycleBtnCreate: 'Criar Ciclo',
    perfCycleTypeWeekly: 'Semanal',
    perfCycleTypeWeeklyDesc: 'Semanas do ano',
    perfCycleTypeMonthly: 'Mensal',
    perfCycleTypeMonthlyDesc: 'Meses do calendário',
    perfCycleTypeQuarterly: 'Trimestral',
    perfCycleTypeQuarterlyDesc: 'T1, T2, T3, T4',
    perfCycleTypeSemiAnnual: 'Semestral',
    perfCycleTypeSemiAnnualDesc: 'S1 e S2',
    perfCycleTypeAnnual: 'Anual',
    perfCycleTypeAnnualDesc: 'Ano completo',
    perfCycleTypeCustom: 'Personalizado',
    perfCycleTypeCustomDesc: 'Defina suas próprias datas',

    // Performance Cycle Manager
    perfCycleManagerTitle: 'Ciclos de Desempenho',
    perfCycleManagerBtnNewCycle: 'Novo Ciclo',
    perfCycleManagerWhatsACycleTitle: 'O que é um ciclo de desempenho?',
    perfCycleManagerWhatsACycleDesc: 'É um período de tempo recorrente — uma semana, mês, trimestre ou ano — durante o qual os KPIs são rastreados. Quando um ciclo fecha, avança automaticamente para o próximo período e transfere os KPIs ativos. Clique em "Novo Ciclo" para começar.',
    perfCycleManagerStatusDraft: 'Rascunho',
    perfCycleManagerStatusActive: 'Ativo',
    perfCycleManagerStatusInReview: 'Em Revisão',
    perfCycleManagerStatusClosed: 'Fechado',
    perfCycleManagerBtnActivate: 'Ativar',
    perfCycleManagerBtnStartReview: 'Iniciar Revisão',
    perfCycleManagerBtnClose: 'Fechar',
    perfCycleManagerBtnDelete: 'Excluir',
    perfCycleManagerBtnNo: 'Não',
    perfCycleManagerRecurringCycleTooltip: 'Ciclo recorrente',
    perfCycleManagerAriaLabelDeleteCycle: 'Excluir ciclo',
    perfCycleManagerNoCyclesTitle: 'Ainda sem ciclos de desempenho',
    perfCycleManagerNoCyclesDesc: 'Clique em "Novo Ciclo" acima para criar seu primeiro ciclo.',
    perfCycleManagerErrorCannotDelete: 'Não é possível excluir',

    // Performance KPI Catalog
    perfCatalogTitle: 'Catálogo de KPIs',
    perfCatalogTemplatesCount: '{{count}} modelos',
    perfCatalogBtnCustomKpi: 'KPI Personalizado',
    perfCatalogSearchPlaceholder: 'Pesquisar modelos por nome, descrição ou questão de negócio...',
    perfCatalogFilterAll: 'Todos',
    perfCatalogNoTemplatesMatch: 'Nenhum modelo corresponde à sua pesquisa',
    perfCatalogTemplateUnit: 'Unidade:',
    perfCatalogTemplateTarget: 'Meta:',
    perfCatalogTemplateCadence: 'Cadência:',

    // Settings Sidebar
    settingsSidebarTitle: 'Configurações',
    settingsNavOrganization: 'Organização',
    settingsNavOrganizationDesc: 'Pessoas, departamentos e configurações',
    settingsNavPlatform: 'Plataforma',
    settingsNavPlatformDesc: 'Configuração global da plataforma',

    // Settings Platform
    settingsPlatformTitle: 'Configurações da Plataforma',
    settingsPlatformSubtitle: 'Gestão de tenants, organizações e módulos',
    settingsTabTenants: 'Tenants',
    settingsTabOrganizations: 'Organizações',
    settingsTabAi: 'IA',
    settingsTabDemo: 'Demo',

    settingsLlmPlatformTitle: 'Chave API da Anthropic',
    settingsLlmPlatformHelp:
      'Chave padrão da plataforma usada por organizações que não têm chave própria. Necessária para recursos de IA.',
    settingsLlmOrgTitle: 'Chave API da Anthropic',
    settingsLlmOrgHelp:
      'Chave opcional da organização. Se não for definida, os recursos de IA usam a chave da plataforma.',
    settingsLlmFieldLabel: 'Chave API',
    settingsLlmFieldPlaceholder: 'sk-ant-...',
    settingsLlmConfigured: 'Configurada',
    settingsLlmNotConfigured: 'Não configurada',
    settingsLlmUsingPlatform: 'Usando padrão da plataforma',
    settingsLlmBtnSave: 'Salvar chave',
    settingsLlmBtnUpdate: 'Atualizar chave',
    settingsLlmBtnClear: 'Limpar chave',
    settingsLlmBtnClearOrg: 'Usar padrão da plataforma',
    settingsLlmClearConfirmTitle: 'Limpar chave API?',
    settingsLlmClearConfirmMessage:
      'Os recursos de IA deixarão de funcionar até que uma nova chave da plataforma seja definida (ou as orgs configurem a própria).',
    settingsLlmClearOrgConfirmMessage:
      'Esta organização usará a chave API da Anthropic da plataforma.',
    settingsLlmSaveSuccess: 'Chave API salva',
    settingsLlmSaveError: 'Falha ao salvar a chave API',
    settingsLlmClearSuccess: 'Chave API limpa',
    settingsLlmClearError: 'Falha ao limpar a chave API',
    settingsLlmError: 'Falha ao carregar o status da chave API',
    settingsLlmModelTitle: 'Modelo Claude',
    settingsLlmModelHelp:
      'ID do modelo Anthropic da plataforma usado por todos os recursos de IA.',
    settingsLlmModelFieldLabel: 'ID do modelo',
    settingsLlmModelFieldPlaceholder: 'claude-sonnet-5',
    settingsLlmModelBtnSave: 'Salvar modelo',
    settingsLlmModelSaveSuccess: 'Modelo salvo',
    settingsLlmModelSaveError: 'Falha ao salvar o modelo',

    // Settings Tenants
    settingsTenantsBtnCreateTenant: 'Criar Tenant',
    settingsTenantsFieldName: 'Nome',
    settingsTenantsFieldSlug: 'Slug',
    settingsTenantsPlaceholderName: 'ex. Acme Corp',
    settingsTenantsPlaceholderSlug: 'ex. acme-corp',
    settingsTenantsNoTenants: 'Ainda sem tenants',
    settingsTenantsBtnCreate: 'Criar',
    settingsTenantsBtnCancel: 'Cancelar',
    settingsTenantsBtnSuspend: 'Suspender',
    settingsTenantsBtnActivate: 'Ativar',
    settingsTenantsCount: '{{count}} tenant',
    settingsTenantsCountPlural: '{{count}} tenants',
    settingsTenantsBadgeHq: 'HQ',
    settingsTenantsNoOrgsInTenant: 'Nenhuma organização neste tenant',
    settingsTenantsModuleAccess: 'Acesso a Módulos',
    settingsTenantsAriaLabelInviteUser: 'Convidar usuário para esta organização',
    settingsTenantsToastTenantCreated: 'Tenant criado',
    settingsTenantsToastTenantSuspended: 'Tenant suspenso',
    settingsTenantsToastTenantActivated: 'Tenant ativo',
    settingsTenantsToastModuleDisabled: 'Módulo desabilitado',
    settingsTenantsToastModuleEnabled: 'Módulo habilitado',
    settingsTenantsErrorFailedToCreateTenant: 'Falha ao criar tenant',
    settingsTenantsErrorFailedToUpdateTenant: 'Falha ao atualizar tenant',
    settingsTenantsErrorFailedToToggleModule: 'Falha ao alternar módulo',
    settingsTenantsErrorFailedToEnableModule: 'Falha ao habilitar módulo',
    settingsProvisionModalTitle: 'Habilitar Módulo para Usuários',
    settingsProvisionModalBody: 'Habilitar este módulo para todos os usuários existentes na organização, ou deixá-lo desabilitado e permitir que o administrador atribua o acesso manualmente?',
    settingsProvisionBtnCancel: 'Cancelar',
    settingsProvisionBtnNoneManual: 'Nenhum (manual)',
    settingsProvisionBtnAllUsers: 'Todos os Usuários',

    // Settings Organizations
    settingsOrgsBtnCreateOrg: 'Criar Org',
    settingsOrgsFilterByTenant: 'Filtrar por tenant',
    settingsOrgsPlaceholderAllTenants: 'Todos os tenants',
    settingsOrgsPlaceholderSelectTenant: 'Selecionar tenant',
    settingsOrgsFieldOrganizationName: 'Nome da Organização',
    settingsOrgsPlaceholderOrgName: 'ex. Operações Ltda',
    settingsOrgsNoOrganizations: 'Sem organizações',
    settingsOrgsCount: '{{count}} organização',
    settingsOrgsCountPlural: '{{count}} organizações',
    settingsOrgsRoleCount: '{{count}} papel',
    settingsOrgsRoleCountPlural: '{{count}} papéis',
    settingsOrgsUnknownTenant: 'Desconhecido',
    settingsOrgsToastOrgCreated: 'Organização criada',
    settingsOrgsErrorFailedToCreateOrg: 'Falha ao criar organização',
    settingsOrgsModuleAccess: 'Acesso a Módulos',
    settingsOrgsAriaLabelInviteUser: 'Convidar usuário para esta organização',
    settingsOrgsLeadApiKeyTitle: 'Chave API do Formulário de Leads',
    settingsOrgsLeadApiKeyHelp: 'Use esta chave como cabeçalho X-API-Key ao enviar leads do seu site.',
    settingsOrgsLeadApiKeyCopy: 'Copiar',
    settingsOrgsLeadApiKeyCopied: 'Copiado!',
    settingsOrgsLeadApiKeyError: 'Falha ao carregar a chave API',
    settingsOrgsLeadApiKeyShow: 'Mostrar chave',
    settingsOrgsLeadApiKeyHide: 'Ocultar chave',
    settingsOrgsLeadApiKeyRegenerate: 'Regenerar',
    settingsOrgsLeadApiKeyRegenerateConfirmTitle: 'Regenerar chave API?',
    settingsOrgsLeadApiKeyRegenerateConfirmMessage:
      'A chave atual deixará de funcionar imediatamente. Atualize qualquer site ou integração que use a chave antiga.',
    settingsOrgsLeadApiKeyRegenerateSuccess: 'Chave API regenerada',
    settingsOrgsLeadApiKeyRegenerateError: 'Falha ao regenerar a chave API',

    // Settings Demo
    settingsDemoTitle: 'Organizações Demo',
    settingsDemoSubtitle: 'Crie organizações demo completamente populadas com departamentos, usuários, KPIs e dados de desempenho.',
    settingsDemoBtnCreateDemoOrg: 'Criar Org Demo',
    settingsDemoPlaceholderOrgName: 'Nome da organização (ex. Acme Corp)',
    settingsDemoCreatedTitle: 'Demo Criado',
    settingsDemoDismiss: 'Dispensar',
    settingsDemoFieldOrganization: 'Organização',
    settingsDemoFieldStats: 'Estatísticas',
    settingsDemoFieldCredentials: 'Credenciais',
    settingsDemoFieldEmail: 'E-mail',
    settingsDemoFieldPassword: 'Senha',
    settingsDemoStatUsers: '{{count}} usuários',
    settingsDemoStatKpis: '{{count}} KPIs',
    settingsDemoNoDemoOrgs: 'Ainda sem organizações demo',
    settingsDemoOrgCount: '{{count}} org demo',
    settingsDemoOrgCountPlural: '{{count}} orgs demo',
    settingsDemoToastDemoOrgCreated: 'Org demo criada',
    settingsDemoToastDemoOrgDeleted: 'Org demo excluída',
    settingsDemoErrorFailedToCreate: 'Falha ao criar org demo',
    settingsDemoErrorFailedToDelete: 'Falha ao excluir',
    settingsDemoDeleteTitle: 'Excluir Organização Demo',
    settingsDemoDeleteBody: 'Isso excluirá permanentemente {{name}} e todos os seus dados.',
    settingsDemoBtnDeleteEverything: 'Excluir Tudo',
    settingsDemoBtnCancel: 'Cancelar',

    // Discovery Portal
    discoveryPortalTitle: 'Agente de Descoberta',
    discoveryTabBoard: 'Quadro',
    discoveryTabChat: 'Chat',
    discoveryTabSpec: 'Especificação',
    discoveryBoardFilterAll: 'Todos',
    discoveryBoardFilterCollecting: 'Coletando',
    discoveryBoardFilterSpecReady: 'Spec Pronta',
    discoveryBoardFilterSubmitted: 'Enviado',
    discoveryBoardFilterInProgress: 'Em Andamento',
    discoveryBoardBtnNewRequest: 'Nova Solicitação',
    discoveryBoardFailedToLoad: 'Falha ao carregar as solicitações.',
    discoveryBoardBtnRetry: 'Tentar Novamente',
    discoveryBoardNoneYet: 'Ainda sem solicitações de descoberta.',
    discoveryBoardBtnCreateFirst: 'Crie sua primeira solicitação',
    discoveryBoardCardUntitledRequest: 'Solicitação sem título',
    discoveryBoardCardUnknownInitiator: 'Desconhecido',
    discoveryBoardNewRequestDialogTitle: 'Nova Solicitação de Descoberta',
    discoveryBoardNewRequestLabel: 'Descreva o que você precisa construir ou alterar',
    discoveryBoardNewRequestPlaceholder: 'ex. Precisamos de um painel que mostre dados de vendas em tempo real para todas as regiões...',
    discoveryBoardBtnCancelDialog: 'Cancelar',
    discoveryBoardBtnCreateRequest: 'Criar Solicitação',
    discoveryStatusDraft: 'Rascunho',
    discoveryStatusCollecting: 'Coletando',
    discoveryStatusSpecReady: 'Spec Pronta',
    discoveryStatusSubmitted: 'Enviado',
    discoveryStatusInProgress: 'Em Andamento',
    discoveryStatusImplemented: 'Implementado',
    discoveryStatusDeclined: 'Recusado',
    discoveryChatSelectRequest: 'Selecione uma solicitação do quadro para começar a conversar.',
    discoveryChatNotificationsOn: 'Notificações ativadas',
    discoveryChatNotificationsOff: 'Notificações desativadas',
    discoveryChatEmailNotificationsOn: 'Notificações por e-mail ativadas',
    discoveryChatEmailNotificationsOff: 'Notificações por e-mail desativadas',
    discoveryChatStakeholders: 'Partes interessadas',
    discoveryChatUntitledRequest: 'Solicitação sem título',
    discoveryChatDiscoveryAgentLabel: 'Agente de Descoberta',
    discoveryChatInputPlaceholder: 'Descreva suas necessidades ou responda às perguntas do agente...',
    discoveryChatDropFilesHere: 'Solte arquivos aqui',
    discoveryChatBtnAddMoreInfo: 'Adicionar Mais Informações',
    discoveryChatAriaLabelAttachFile: 'Anexar arquivo',
    discoveryActionSuggestedStakeholder: 'Parte Interessada Sugerida',
    discoveryActionSameOrDifferentPerson: '{{name}} já é uma parte interessada. É a mesma pessoa ou alguém diferente?',
    discoveryActionBtnSamePerson: 'A mesma pessoa',
    discoveryActionBtnDifferentPerson: 'Uma pessoa diferente',
    discoveryActionStakeholderLabelName: 'Nome',
    discoveryActionStakeholderLabelContext: 'Contexto',
    discoveryActionStakeholderNamePlaceholder: 'Nome',
    discoveryActionStakeholderContextPlaceholder: 'Por que esta pessoa é relevante...',
    discoveryActionSearchByNameOrEmail: 'Pesquisar por nome ou inserir e-mail...',
    discoveryActionBtnAddStakeholder: 'Adicionar Parte Interessada',
    discoveryActionBtnAdding: 'Adicionando...',
    discoveryActionFollowUpRequestTitle: 'Solicitação de Acompanhamento',
    discoveryActionBtnSend: 'Enviar',
    discoveryActionBtnSkip: 'Pular',
    discoveryActionReadyToGenerateSpecTitle: 'Pronto para Gerar Especificação',
    discoveryActionReadyToGenerateSpecDesc: 'O agente coletou informações suficientes para gerar um documento de especificação.',
    discoveryActionBtnGenerateSpec: 'Gerar Especificação',
    discoveryStakeholderPanelTitle: 'Partes Interessadas',
    discoveryStakeholderPanelNoStakeholders: 'Ainda sem partes interessadas.',
    discoveryStakeholderPanelNoStakeholdersDesc: 'Adicione pessoas que possam fornecer informações sobre esta solicitação.',
    discoveryStakeholderAriaLabelAdd: 'Adicionar parte interessada',
    discoveryStakeholderSearchPlaceholder: 'Pesquisar por e-mail ou nome...',
    discoveryStakeholderContextPlaceholder: 'Relevante para... (ex. conhece o sistema de faturamento)',
    discoveryStakeholderBtnAdd: 'Adicionar Parte Interessada',
    discoveryStakeholderBadgeExternal: 'Externo',
    discoveryStakeholderBadgeInternal: 'Interno',
    discoveryStakeholderBadgeAppUser: 'Usuário do App',
    discoveryStakeholderStatusInvited: 'Convidado',
    discoveryStakeholderStatusActive: 'Ativo',
    discoveryStakeholderStatusCompleted: 'Concluído',
    discoveryStakeholderErrorAlreadyExists: 'Esta pessoa já é uma parte interessada nesta solicitação.',
    discoveryStakeholderErrorFailedToAdd: 'Falha ao adicionar parte interessada',
    discoverySpecTitle: 'Especificação',
    discoverySpecBtnBackToChat: 'Voltar ao Chat',
    discoverySpecVersionLabel: 'Versão {{count}}',
    discoverySpecBtnDownloadMd: 'Baixar MD',
    discoverySpecBtnExportPdf: 'Exportar PDF',
    discoverySpecBtnRequestRevision: 'Solicitar Revisão',
    discoverySpecNoSpecTitle: 'Ainda sem especificação gerada.',
    discoverySpecNoSpecDesc: 'Continue conversando com o Agente de Descoberta para coletar requisitos, depois gere uma especificação quando estiver pronto.',
    discoverySpecBtnGenerateSpec: 'Gerar Especificação',
    discoverySpecErrorFailedToLoad: 'Falha ao carregar versões da especificação',
    discoverySpecErrorFailedToGenerate: 'Falha ao gerar especificação',
    discoveryExternalPortalBrandDiscovery: 'Descoberta',
    discoveryExternalPortalClosedBanner: 'Esta solicitação foi fechada. Você pode revisar a conversa, mas não pode enviar novas mensagens.',
    discoveryExternalPortalDefaultWelcome: 'Você foi convidado a compartilhar sua perspectiva sobre esta solicitação de funcionalidade. O agente fará perguntas direcionadas.',
    discoveryExternalPortalEmptyPrompt: 'Compartilhe seus pensamentos, requisitos ou perguntas sobre esta solicitação de funcionalidade. O agente de descoberta guiará a conversa.',
    discoveryExternalPortalInputPlaceholder: 'Digite sua mensagem…',
    discoveryExternalPortalBtnAddMoreInfo: 'Adicionar Mais Informações',
    discoveryExternalPortalLoadingPortal: 'Carregando portal…',
    discoveryExternalPortalErrorTitle: 'Não foi possível carregar',
    discoveryExternalPortalErrorDefault: 'Este link é inválido ou expirou. Por favor verifique com a pessoa que o compartilhou.',
    discoveryExternalPortalErrorInvalidLink: 'Link inválido — nenhum token de acesso fornecido.',
    discoveryExternalPortalErrorExpiredLink: 'Este link é inválido ou expirou.',
    discoveryExternalPortalErrorClosed: 'Esta solicitação de descoberta foi fechada.',
    discoveryExternalPortalErrorLoadFailed: 'Falha ao carregar. Por favor tente novamente mais tarde.',
    discoveryExternalPortalErrorUploadFailed: 'Falha no upload do arquivo. Por favor tente novamente.',
    discoveryExternalPortalErrorSomethingWentWrong: 'Desculpe, algo deu errado. Por favor tente novamente.',
    discoveryExternalPortalInterjectionAddressed: 'Obrigado — sua contribuição sobre isso já foi registrada. Você pode continuar abaixo se tiver mais a adicionar.',
    discoveryExternalPortalAriaLabelAttachFile: 'Anexar arquivo',
    discoveryExternalPortalAriaLabelStopGenerating: 'Parar de gerar',
    discoveryExternalPortalAriaLabelSendMessage: 'Enviar mensagem',

    // Survey Portal
    surveyPortalTitle: 'Pesquisas',
    surveyTabBoard: 'Quadro',
    surveyTabEditor: 'Editor',
    surveyTabResults: 'Resultados',

    // Survey Board
    surveyBoardFilterAll: 'Todas',
    surveyBoardFilterDrafts: 'Rascunhos',
    surveyBoardFilterInProgress: 'Em Andamento',
    surveyBoardFilterCompleted: 'Concluídas',
    surveyBoardStatusDraft: 'Rascunho',
    surveyBoardStatusInProgress: 'Em Andamento',
    surveyBoardStatusCompleted: 'Concluída',
    surveyBoardBtnNewSurvey: 'Nova Pesquisa',
    surveyBoardFailedToLoad: 'Falha ao carregar as pesquisas.',
    surveyBoardBtnRetry: 'Tentar novamente',
    surveyBoardNoneYet: 'Ainda sem pesquisas.',
    surveyBoardBtnCreateFirst: 'Crie sua primeira pesquisa',
    surveyBoardCardUntitled: 'Pesquisa sem título',
    surveyBoardCardUnknownCreator: 'Desconhecido',
    surveyBoardBtnStop: 'Parar',
    surveyBoardDeleteTitle: 'Excluir pesquisa',
    surveyBoardDeleteMessage: 'Tem certeza de que deseja excluir "{{title}}"? Esta ação não pode ser desfeita.',
    surveyBoardDeleteConfirm: 'Excluir',
    surveyBoardDeleteDeleting: 'Excluindo…',
    surveyBoardDeleteTooltip: 'Excluir pesquisa',
    surveyBoardBtnCancel: 'Cancelar',
    surveyBoardUploadTooltip: 'Enviar resposta',

    // Survey Upload Modal
    surveyUploadTitle: 'Enviar resposta de pesquisa',
    surveyUploadLabelName: 'Nome do respondente',
    surveyUploadPlaceholderName: 'Nome completo…',
    surveyUploadLabelEmail: 'E-mail do respondente',
    surveyUploadPlaceholderEmail: 'email@exemplo.com',
    surveyUploadLabelContent: 'Conteúdo da resposta',
    surveyUploadPlaceholderContent: 'Cole o e-mail ou texto com as respostas aqui…',
    surveyUploadModePaste: 'Colar texto',
    surveyUploadModeFile: 'Enviar arquivo',
    surveyUploadDropHint: 'Arraste um arquivo aqui (.txt, .csv, .xlsx, .doc, .docx, .pdf, …)',
    surveyUploadProcessing: 'Analisando conteúdo com IA…',
    surveyUploadConfidenceLabel: 'Confiança IA: {{value}}%',
    surveyUploadMappedCount: '{{count}} de {{total}} perguntas mapeadas',
    surveyUploadLowConfidence: 'O conteúdo não parece ser uma resposta válida a esta pesquisa. Tente outro texto ou selecione a pesquisa correta.',
    surveyUploadProcessingFailed: 'Falha ao processar o conteúdo. Tente novamente.',
    surveyUploadStoreFailed: 'Falha ao salvar a resposta.',
    surveyUploadSuccessToast: 'Resposta enviada com sucesso!',
    surveyUploadTryDifferent: 'Tente colar outro texto ou enviar outro arquivo.',
    surveyUploadBtnCancel: 'Cancelar',
    surveyUploadBtnProcess: 'Processar com IA',
    surveyUploadBtnBack: 'Voltar',
    surveyUploadBtnConfirm: 'Confirmar e salvar',
    surveyUploadBtnTryAgain: 'Tentar novamente',
    surveyUploadBtnProceedAnyway: 'Continuar mesmo assim',
    surveyUploadLowConfirmTitle: 'Confiança baixa — Tem certeza?',
    surveyUploadLowConfirmMessage: 'A confiança da IA é de apenas {{value}}%. As respostas mapeadas podem ser imprecisas. Deseja salvar esta resposta mesmo assim?',
    surveyUploadLowConfirmYes: 'Sim, salvar mesmo assim',
    surveyUploadEmailRequired: 'O e-mail é obrigatório',
    surveyUploadEmailInvalid: 'Digite um e-mail válido',
    surveyUploadNameRequired: 'O nome é obrigatório',

    // Survey Editor
    surveyEditorLabelTitle: 'Título da pesquisa',
    surveyEditorPlaceholderTitle: 'Digite o título da pesquisa...',
    surveyEditorLabelRecipients: 'Destinatários',
    surveyEditorRecipientCount: '{{count}} destinatário',
    surveyEditorRecipientCountPlural: '{{count}} destinatários',
    surveyEditorPlaceholderEmail: 'Digite o e-mail e pressione Enter...',
    surveyEditorBtnAdd: 'Adicionar',
    surveyEditorUploadExtracting: 'Extraindo e-mails...',
    surveyEditorUploadLabel: 'Envie um arquivo para extrair e-mails (CSV, Excel, PDF, TXT)',
    surveyEditorLabelPublicLink: 'Link público',
    surveyEditorPublicLinkCaption: 'Permite que qualquer pessoa com o link responda (eles informarão nome e e-mail).',
    surveyEditorPublicLinkPendingSave: ' O link será gerado ao salvar.',
    surveyEditorAriaLabelCopyLink: 'Copiar link',
    surveyEditorLabelDeadline: 'Prazo (opcional)',
    surveyEditorPlaceholderDeadline: 'Selecionar prazo...',
    surveyEditorLabelQuestions: 'Perguntas',
    surveyEditorPlaceholderQuestion: 'Digite sua pergunta...',
    surveyEditorBtnAddQuestion: 'Adicionar pergunta',
    surveyEditorBtnSave: 'Salvar',
    surveyEditorBtnSaveAndSend: 'Salvar e enviar',
    surveyEditorToastExtractedEmails: '{{count}} novo(s) e-mail(s) extraído(s) de {{filename}}',
    surveyEditorToastNoNewEmails: 'Nenhum e-mail novo encontrado no arquivo',
    surveyEditorErrorExtractEmails: 'Falha ao extrair e-mails do arquivo',
    surveyEditorErrorToggleLink: 'Falha ao ativar o link público',
    surveyEditorToastLinkCopied: 'Link copiado para a área de transferência',
    surveyEditorErrorTitleRequired: 'Título e pelo menos uma pergunta são obrigatórios',
    surveyEditorErrorRecipientRequired: 'Adicione pelo menos um destinatário ou ative o link público para enviar',
    surveyEditorToastSaved: 'Pesquisa salva',
    surveyEditorErrorSaveFailed: 'Falha ao salvar a pesquisa',
    surveyEditorToastSent: 'Pesquisa enviada',
    surveyEditorErrorSendFailed: 'Falha ao enviar a pesquisa',

    // Survey Results
    surveyResultsTabAiSummary: 'Resumo IA',
    surveyResultsTabResponses: 'Respostas',
    surveyResultsBtnDownloadPdf: 'Baixar PDF',
    surveyResultsBtnRegenerate: 'Regenerar resumo',
    surveyResultsAiLoading: 'Gerando resumo de IA...',
    surveyResultsAiNotAvailable: 'Resumo de IA ainda não disponível.',
    surveyResultsOverview: 'Visão geral',
    surveyResultsKeyTakeaways: 'Principais conclusões',
    surveyResultsFallbackTitle: 'Resumo IA',
    surveyResultsSentimentPositive: 'Positivo',
    surveyResultsSentimentNegative: 'Negativo',
    surveyResultsSentimentMixed: 'Misto',
    surveyResultsSentimentNeutral: 'Neutro',
    surveyResultsResponseCount: '{{count}} resposta',
    surveyResultsResponseCountPlural: '{{count}} respostas',
    surveyResultsSearchPlaceholder: 'Buscar por nome ou e-mail...',
    surveyResultsAllRecipients: 'Todos os destinatários ({{responded}} de {{total}} responderam)',
    surveyResultsTagViaLink: 'Via link',
    surveyResultsBadgeCompleted: 'Concluído',
    surveyResultsBadgePending: 'Pendente',
    surveyResultsNotRespondedYet: 'Este destinatário ainda não respondeu.',
    surveyResultsNoAnswers: 'Nenhuma resposta registrada.',
    surveyResultsFallbackQuestion: 'Pergunta',
    surveyResultsSelectRecipient: 'Selecione um destinatário para ver suas respostas.',

    // Survey Responder
    surveyResponderErrorUnavailable: 'Esta pesquisa não está mais disponível ou o link é inválido.',
    surveyResponderErrorDuplicateEmail: 'Este e-mail já foi usado para responder a esta pesquisa.',
    surveyResponderErrorSubmitFailed: 'Falha ao enviar sua resposta. Por favor tente novamente.',
    surveyResponderThankYou: 'Obrigado!',
    surveyResponderResponseRecorded: 'Sua resposta foi registrada. Você pode fechar esta página.',
    surveyResponderLabelEmail: 'Seu e-mail',
    surveyResponderPlaceholderEmail: 'seu@email.com',
    surveyResponderLabelName: 'Seu nome',
    surveyResponderPlaceholderName: 'Digite seu nome...',
    surveyResponderPlaceholderAnswer: 'Digite sua resposta...',
    surveyResponderFallbackTitle: 'Pesquisa',
    surveyResponderBtnSubmit: 'Enviar resposta',

    // Sales — Sidebar
    salesSidebarTitle: 'Vendas',
    salesSidebarPipelineLabel: 'Pipeline',
    salesSidebarPipelineDesc: 'Quadro Kanban',
    salesSidebarArchivedLabel: 'Arquivados',
    salesSidebarArchivedDesc: 'Leads arquivados',
    salesSidebarGeneratorLabel: 'Gerador IA',
    salesSidebarGeneratorDesc: 'Descobrir novos leads',
    salesSidebarSettingsLabel: 'Configurações',
    salesSidebarSettingsDesc: 'Perfil e exemplos',

    // Sales — Status labels
    salesStatusNew: 'Novo',
    salesStatusContacted: 'Contatado',
    salesStatusNegotiation: 'Negociação',
    salesStatusWon: 'Ganho',
    salesStatusLost: 'Perdido',
    salesStatusDisqualified: 'Desqualificado',

    // Sales — Kanban board
    salesKanbanLeadsInPipeline: '{{count}} lead no pipeline',
    salesKanbanLeadsInPipelinePlural: '{{count}} leads no pipeline',
    salesKanbanErrorUpdateStatus: 'Falha ao atualizar status',
    salesKanbanMovedTo: 'Movido para {{status}}',
    salesKanbanNoLeads: 'Sem leads',

    // Sales — Lead card
    salesLeadCardAiBadge: 'IA',
    salesLeadCardSalesLabel: 'Vendas',
    salesLeadCardAiRatingTitle: 'Avaliação IA',
    salesLeadCardDragHandle: 'Arrastar para reordenar',

    // Sales — Lead modal
    salesLeadModalTabOverview: 'Visão geral',
    salesLeadModalTabComments: 'Comentários',
    salesLeadModalTabHistory: 'Histórico',
    salesLeadModalBadgeAiDiscovered: 'Descoberto por IA',
    salesLeadModalBtnUnarchive: 'Desarquivar',
    salesLeadModalBtnArchive: 'Arquivar',
    salesLeadModalLabelAiRating: 'Avaliação IA',
    salesLeadModalBtnRefreshAi: 'Atualizar análise IA',
    salesLeadModalLabelYourRating: 'Sua avaliação',
    salesLeadModalLabelBusinessInfo: 'Info do negócio',
    salesLeadModalLabelPhone: 'Telefone',
    salesLeadModalLabelSuggestedPitch: 'Pitch sugerido',
    salesLeadModalLabelAiAnalysis: 'Análise IA',
    salesLeadModalCreated: 'Criado: ',
    salesLeadModalUpdated: 'Atualizado: ',
    salesLeadModalNoComments: 'Sem comentários ainda',
    salesLeadModalNoHistory: 'Sem histórico ainda',
    salesLeadModalCommentPlaceholder: 'Escreva um comentário... (@agent para perguntar à IA)',
    salesLeadModalCommentHint: 'seguido de uma pergunta para perguntar à IA',
    salesLeadModalCommentHintPrefix: 'Digite ',
    salesLeadModalAgentLabel: 'Agente IA',
    salesLeadModalFallbackUser: 'Usuário',
    salesLeadModalFallbackSystem: 'Sistema',
    salesLeadModalToastUnarchived: 'Lead desarquivado',
    salesLeadModalToastArchived: 'Lead arquivado',
    salesLeadModalToastAiRefreshed: 'Avaliação IA atualizada',
    salesLeadModalErrorRefreshAi: 'Falha ao atualizar avaliação IA',
    salesLeadModalErrorAgentFailed: 'Requisição ao agente falhou',
    salesLeadModalErrorCommentFailed: 'Falha ao enviar comentário',
    salesLeadModalHistoryCreatedAs: 'Criado como {{status}}',

    // Sales — Archived
    salesArchivedEmpty: 'Sem leads arquivados',
    salesArchivedEmptyDesc: 'Leads arquivados aparecerão aqui',
    salesArchivedCount: '{{count}} lead arquivado',
    salesArchivedCountPlural: '{{count}} leads arquivados',
    salesArchivedColCompany: 'Empresa',
    salesArchivedColStatus: 'Status',
    salesArchivedColAiRating: 'Avaliação IA',
    salesArchivedColSalesRating: 'Avaliação Vendas',
    salesArchivedColCreated: 'Criado',
    salesArchivedBtnUnarchive: 'Desarquivar',

    // Sales — Company Profile
    salesProfileTitle: 'Perfil da empresa',
    salesProfileDesc: 'Descreva sua empresa, o perfil de cliente ideal e o que faz um bom lead. Este contexto é usado pelo agente de IA ao qualificar leads.',
    salesProfilePlaceholder: 'ex. Somos uma empresa SaaS B2B focada em conformidade empresarial. Nosso cliente ideal é...',
    salesProfileBtnSave: 'Salvar perfil',
    salesProfileToastSaved: 'Perfil da empresa atualizado',
    salesProfileErrorSave: 'Falha ao atualizar perfil',

    // Sales — Lead Examples
    salesExamplesTitle: 'Exemplos de leads',
    salesExamplesDesc: 'Bons e maus exemplos ajudam a IA a aprender o que faz um lead de qualidade para o seu negócio.',
    salesExamplesBtnAdd: 'Adicionar exemplo',
    salesExamplesTypeGood: 'Bom',
    salesExamplesTypeBad: 'Ruim',
    salesExamplesLabelUrl: 'URL da empresa',
    salesExamplesPlaceholderUrl: 'https://exemplo.com',
    salesExamplesLabelExplanation: 'Explicação',
    salesExamplesPlaceholderExplanation: 'Por que este é um bom/mau exemplo de lead?',
    salesExamplesBtnCancel: 'Cancelar',
    salesExamplesBtnSave: 'Salvar exemplo',
    salesExamplesEmpty: 'Sem exemplos ainda',
    salesExamplesEmptyDesc: 'Adicione bons e maus exemplos de leads para melhorar a precisão de qualificação da IA',
    salesExamplesAriaDelete: 'Excluir exemplo',
    salesExamplesToastAdded: 'Exemplo adicionado',
    salesExamplesErrorAdd: 'Falha ao adicionar exemplo',
    salesExamplesToastDeleted: 'Exemplo excluído',
    salesExamplesErrorDelete: 'Falha ao excluir exemplo',

    // Sales — Lead Generator
    salesGeneratorTitle: 'Gerador IA de leads',
    salesGeneratorSubtitle: 'Descubra e qualifique leads com análise de IA',
    salesGeneratorCardLocation: 'Localização alvo',
    salesGeneratorLabelCountry: 'País',
    salesGeneratorPlaceholderCountry: 'Selecionar país...',
    salesGeneratorLabelState: 'Estado',
    salesGeneratorPlaceholderState: 'Todos os estados',
    salesGeneratorLabelCity: 'Cidade',
    salesGeneratorPlaceholderCity: 'ex. São Paulo',
    salesGeneratorLabelDistance: 'Distância: {{value}} km',
    salesGeneratorCardRefinement: 'Refinamento de busca',
    salesGeneratorOptionalLabel: '(opcional)',
    salesGeneratorLabelKeywords: 'Indústria / Palavras-chave',
    salesGeneratorPlaceholderKeywords: 'ex. SaaS, manufatura, saúde',
    salesGeneratorHintKeywords: 'Deixe vazio para a IA decidir com base no seu perfil de empresa',
    salesGeneratorLabelMaxResults: 'Máx. resultados',
    salesGeneratorBtnGenerate: 'Gerar leads',
    salesGeneratorInfoText: 'A IA usará seu perfil de empresa e exemplos de leads das Configurações para encontrar e avaliar clientes potenciais.',
    salesGeneratorInitMessage: 'Inicializando gerador IA de leads...',
    salesGeneratorRunningTitle: 'Descobrindo leads...',
    salesGeneratorFoundCount: '{{count}} encontrados',
    salesGeneratorBtnNewSearch: 'Nova busca',
    salesGeneratorEmptyTitle: 'Nenhum lead descoberto',
    salesGeneratorEmptyDesc: 'Tente ampliar seus critérios de busca.',
    salesGeneratorBtnTryAgain: 'Tentar novamente',
    salesGeneratorBtnDeselectAll: 'Desmarcar tudo',
    salesGeneratorBtnSelectAll: 'Selecionar tudo',
    salesGeneratorSelectedCount: '{{selected}} de {{total}} selecionados',
    salesGeneratorBtnDiscard: 'Descartar',
    salesGeneratorBtnAddToPipeline: 'Adicionar {{count}} ao pipeline',
    salesGeneratorErrorNoLeads: 'Nenhum lead selecionado',
    salesGeneratorToastLeadsAdded: '{{count}} leads adicionados ao pipeline!',
    salesGeneratorErrorAddFailed: 'Falha ao adicionar leads',
    salesGeneratorErrorGenerateFailed: 'Falha ao gerar leads',
    salesGeneratorStreamEndedEarly: '{{count}} leads encontrados (fluxo encerrado antes).',
    salesGeneratorNoResults: 'A busca terminou sem resultados.',
    salesGeneratorFoundSummary: '{{count}} leads encontrados.'
  }
};

export const getTranslation = (lang: string, key: string, variables: Record<string, string | number> = {}): string => {
  let text = translations[lang]?.[key] || translations['en'][key] || key;
  
  // Replace template variables like {{count}}
  Object.keys(variables).forEach(varKey => {
    text = text.replace(new RegExp(`{{${varKey}}}`, 'g'), String(variables[varKey]));
  });
  
  return text;
};

/**
 * Map module IDs to human-readable, localized labels
 */
export const getModuleLabel = (moduleId: string, lang: SupportedLanguage): string => {
  const moduleKeyMap: Record<string, string> = {
    'financial-forensics': 'moduleFinancialForensics',
    // Add more modules here as needed
  };
  
  const translationKey = moduleKeyMap[moduleId];
  if (!translationKey) {
    // Fallback: capitalize and replace hyphens with spaces
    return moduleId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  return getTranslation(lang, translationKey);
};

